import {Composition} from "../../engine/composition";
import {CompositionStateStore} from "../store/CompositionStateStore";
import {CompositionPersistenceService} from "./CompositionPersistenceService";
import {CANVAS_ID, COMPOSITION_CONTAINER_ID} from "../config/config";
import {hex} from "../../engine/types";
import {Timestamp} from "../../engine/models";
import {CompositionEventsEnum} from "../CompositionEvents";
import {EngineInternalEventsEnum} from "../../engine/EngineInternalEvents";
import {VideoClip} from "../../engine/clips/video/video";
import {AudioClip, MediaClip} from "../../engine/clips";
import {ImageClip} from "../../engine/clips/image/image";
import {ScriptLayerClip} from "../../engine/clips/script/script";
import {ApplicationStore, VisualElementRef} from "../store/ApplicationStore";
import type { SceneProperties, ScriptLayerSourceSegment, TransitionConfig } from "../../engine/clips/script/script.interfaces";
import type { MaskShape } from "../../engine/utils/shapeMask";
import { ProjectAssetsStore } from "../../state/ProjectAssetsStore";
import { FocusStore, FocusRect } from "../../state/FocusStore";
import type { ScriptChunkScenePropsV1, ScriptChunkV1 } from "../types/ScriptChunk";

const DEBUG_ENGINE_LOGS =
    typeof window !== "undefined" && (window as any).__LIBRETTO_DEBUG_ENGINE_LOGS === true;

type ScriptLayerUpdateFlags = {
    cutsChanged?: boolean;
    gapsChanged?: boolean;
    insertionsChanged?: boolean;
    splitsChanged?: boolean;
    speakerOverridesChanged?: boolean;
    speakerMutesChanged?: boolean;
    transcriptHiddenRangesChanged?: boolean;
    wordCorrectionsChanged?: boolean;
    transcriptParagraphBreaksChanged?: boolean;
    transcriptMarkersChanged?: boolean;
};

export class CompositionService {
    private static _composition: Composition | null = null;

    private static _compositionMounted = false;

    private static _sessionVersion = 0;

    // Guard: prevent creating multiple default script spines concurrently.
    private static _ensureEmptyScriptSpinePromise: Promise<void> | null = null;
    private static _ensureEmptyScriptSpineTargetEndSec: number = 0;

    // Script-layer batching: coalesce many edits (e.g. "Remove all filler words") into one schedule + UI update.
    private static _scriptLayerBatchDepth = 0;
    private static _scriptLayerBatchPending:
        | { clipId: string; timingChanged: boolean; flags: ScriptLayerUpdateFlags }
        | null = null;

    private static readonly AUTO_SCENE_TRANSITION_DEFAULT_DURATION_MS = 800;
    private static readonly AUTO_SCENE_TRANSITION_DEFAULT_EASING = "natural";

    private static shouldAutoApplySmartTransitionsToNewScenes(): boolean {
        try {
            return Boolean(ProjectAssetsStore.projectApplySmartTransitionsToNewScenes);
        } catch {
            return false;
        }
    }

    private static inferAutoSmartTransitionConfigForSceneBoundary(args: {
        scriptClip: any;
        fromSceneIndex: number;
        toSceneIndex: number;
    }): TransitionConfig {
        const base: TransitionConfig = {
            audio: { type: "none", durationMs: 0 },
            video: {
                type: "smart",
                durationMs: this.AUTO_SCENE_TRANSITION_DEFAULT_DURATION_MS,
                easing: this.AUTO_SCENE_TRANSITION_DEFAULT_EASING,
                addFades: false,
            },
        };

        try {
            const clip: any = args.scriptClip;
            const from = Number(args.fromSceneIndex ?? NaN);
            const to = Number(args.toSceneIndex ?? NaN);
            if (!clip || !Number.isFinite(from) || !Number.isFinite(to) || from < 0 || to < 0) return base;

            const sources: any[] = typeof clip.getVideoSources === "function" ? clip.getVideoSources() : [];
            const n = Array.isArray(sources) ? sources.length : 0;
            if (n <= 0 || typeof clip.getSceneVisualProperties !== "function") return base;

            const visibleSetForScene = (sceneIndex: number): Set<number> => {
                const set = new Set<number>();
                for (let i = 0; i < n; i++) {
                    const v = clip.getSceneVisualProperties(sceneIndex, i);
                    const a = Number(v?.alpha ?? 1);
                    if (Number.isFinite(a) && a > 0.01) set.add(i);
                }
                return set;
            };

            const a = visibleSetForScene(from);
            const b = visibleSetForScene(to);
            let differs = false;
            if (a.size !== b.size) differs = true;
            else {
                for (const v of a) {
                    if (!b.has(v)) {
                        differs = true;
                        break;
                    }
                }
            }

            if (differs) {
                base.video = { ...(base.video as any), addFades: true } as any;
            }
        } catch {}

        return base;
    }

    /**
     * Ensure a scene boundary has a Smart transition configured on both sides (prev.out and next.in).
     * Does not override existing user-configured transitions.
     */
    private static ensureAutoSmartTransitionAtBoundary(scriptClip: any, boundarySceneIndex: number): void {
        if (!this.shouldAutoApplySmartTransitionsToNewScenes()) return;
        const idx = Number(boundarySceneIndex ?? NaN);
        if (!Number.isFinite(idx) || idx <= 0) return;

        const prevSceneIndex = idx - 1;
        const getIn = (i: number) => {
            try { return scriptClip.getSceneInTransition?.(i); } catch { return undefined; }
        };
        const getOut = (i: number) => {
            try { return scriptClip.getSceneOutTransition?.(i); } catch { return undefined; }
        };
        const setIn = (i: number, v: TransitionConfig | undefined) => {
            try { scriptClip.setSceneInTransition?.(i, v); } catch {}
        };
        const setOut = (i: number, v: TransitionConfig | undefined) => {
            try { scriptClip.setSceneOutTransition?.(i, v); } catch {}
        };

        const existingOut = getOut(prevSceneIndex) as TransitionConfig | undefined;
        const existingIn = getIn(idx) as TransitionConfig | undefined;

        const resolved =
            existingOut ??
            existingIn ??
            this.inferAutoSmartTransitionConfigForSceneBoundary({
                scriptClip,
                fromSceneIndex: prevSceneIndex,
                toSceneIndex: idx,
            });

        // Keep both sides equivalent (design invariant).
        if (!existingOut) setOut(prevSceneIndex, resolved);
        if (!existingIn) setIn(idx, resolved);
        if (existingOut && existingIn) {
            try {
                const a = JSON.stringify(existingOut);
                const b = JSON.stringify(existingIn);
                if (a !== b) {
                    // Prefer out-transition as the source of truth (rendering also prefers outTransition).
                    setIn(idx, existingOut);
                }
            } catch {}
        }
    }

    public static get composition(): Composition {
        if (!this._composition) {
            throw new Error("Composition not initialized. Call CompositionService.initialize() first.");
        }
        return this._composition;
    }

    /**
     * Check if composition is initialized
     */
    static isInitialized(): boolean {
        return this._composition !== null;
    }

    /**
     * A monotonically-increasing counter that changes whenever we switch compositions.
     * Use to guard async work (thumbnails/transcripts) from writing into a new session.
     */
    static getSessionVersion(): number {
        return this._sessionVersion;
    }

    static bumpSessionVersion(): number {
        this._sessionVersion++;
        // Reset default empty-spine coalescing state between compositions.
        // Otherwise, a large previous target can leak into a new composition.
        this._ensureEmptyScriptSpineTargetEndSec = 0;
        this._ensureEmptyScriptSpinePromise = null;
        return this._sessionVersion;
    }

    /**
     * Coalesce multiple script-layer edits into a single expensive update.
     * Useful when applying many cuts/gaps in a loop to avoid repeated schedule recompilation,
     * thumbnail refresh work, persistence writes, and console spam.
     */
    static beginScriptLayerBatch(): void {
        this._scriptLayerBatchDepth += 1;
    }

    /**
     * Emit SCRIPT_LAYER_UPDATED (internal + UI) for the current ScriptLayerClip.
     * Use this when the script clip's placement/state changes in a way that should
     * refresh transcript/scenes/thumbnails/persistence (e.g. per-source segments updated).
     */
    static notifyScriptLayerUpdated(flags: ScriptLayerUpdateFlags = {}, opts?: { timingChanged?: boolean }): void {
        if (!this.isInitialized()) return;
        const clip: any = this.getScriptLayerClip?.();
        if (!clip || String(clip.type) !== "script") return;
        if (opts?.timingChanged) {
            this.queueOrApplyScriptLayerTimingUpdate(clip, flags);
            return;
        }

        // Non-timing update: still emit both events so stores can react.
        try {
            this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                clipId: clip.id,
                cuts: clip.scriptCuts,
                gaps: clip.gaps,
                gapInsertions: clip.gapInsertions,
                segments: clip.scriptSegments,
                splits: clip.splits,
                duration: clip.duration?.seconds ?? 0,
                hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
            });
        } catch {}

        try {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    ...(flags ?? {}),
                },
            });
        } catch {}
    }

    /**
     * Flush a script-layer batch. Safe to call even if batching isn't active.
     */
    static async endScriptLayerBatch(): Promise<void> {
        if (this._scriptLayerBatchDepth <= 0) return;
        this._scriptLayerBatchDepth -= 1;
        if (this._scriptLayerBatchDepth > 0) return;
        await this.flushScriptLayerBatch();
    }

    private static async flushScriptLayerBatch(): Promise<void> {
        const pending = this._scriptLayerBatchPending;
        this._scriptLayerBatchPending = null;
        if (!pending) return;
        if (!this.isInitialized()) return;

        const clip: any = this.getClipById(pending.clipId);
        if (!clip || String(clip.type) !== "script") return;

        if (pending.timingChanged) {
            try {
                await this.composition.onClipTimingChanged();
            } catch (e) {
                console.error("[CompositionService] Error in onClipTimingChanged:", e);
            }
        }

        try {
            this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                clipId: clip.id,
                cuts: clip.scriptCuts,
                gaps: clip.gaps,
                gapInsertions: clip.gapInsertions,
                segments: clip.scriptSegments,
                splits: clip.splits,
                duration: clip.duration?.seconds ?? 0,
                hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
            });
        } catch {}

        try {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    ...(pending.flags ?? {}),
                },
            });
        } catch {}
    }

    private static queueOrApplyScriptLayerTimingUpdate(clip: any, flags: ScriptLayerUpdateFlags): void {
        if (!this.isInitialized()) return;
        if (!clip?.id) return;

        const clipId = String(clip.id);
        if (this._scriptLayerBatchDepth > 0) {
            const prev = this._scriptLayerBatchPending;
            this._scriptLayerBatchPending = {
                clipId,
                timingChanged: true,
                flags: { ...(prev?.flags ?? {}), ...(flags ?? {}) },
            };
            return;
        }

        // Immediate mode: preserve existing behavior.
        try {
            this.composition.onClipTimingChanged();
        } catch (e) {
            console.error("[CompositionService] Error in onClipTimingChanged:", e);
        }

        try {
            this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                clipId: clip.id,
                cuts: clip.scriptCuts,
                gaps: clip.gaps,
                gapInsertions: clip.gapInsertions,
                segments: clip.scriptSegments,
                splits: clip.splits,
                duration: clip.duration?.seconds ?? 0,
                hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
            });
        } catch {}

        try {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    ...(flags ?? {}),
                },
            });
        } catch {}
    }

    /**
     * Initialize the composition singleton and mount it to the canvas
     */
    static async initialize(opts?: { force?: boolean; compositionId?: string }): Promise<void> {
        this._compositionMounted = false;
        if (this._composition) {
            if (opts?.force) {
                await this.destroy();
            } else {
                console.warn("Composition already initialized");
                return;
            }
        }

        // Create new composition instance
        this._composition = new Composition({
            width: 1920,
            height: 1080,
            background: '#000000',
            backend: 'webgl',
            id: String(opts?.compositionId ?? ""),
        });

        this.mountComposition();
    }

    /**
     * Fully dispose the current composition (audio ctx, renderer, tickers) and clear singleton.
     */
    static async destroy(): Promise<void> {
        const comp = this._composition;
        this._compositionMounted = false;
        this._composition = null;
        if (!comp) return;
        let disposed = false;
        try {
            // Prefer engine-level disposal which stops tickers + closes AudioContext.
            // @ts-ignore
            await comp.dispose?.();
            disposed = true;
        } catch {}
        // Fallback best-effort.
        if (!disposed) {
            try { await (comp as any).pause?.(); } catch {}
            try { (comp as any).renderer?.destroy?.(true); } catch {}
            try { await (comp as any).audioCtx?.close?.(); } catch {}
        }

        // Always clear the visible canvas to prevent stale frames when switching compositions.
        try {
            const canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement | null;
            if (canvas) {
                canvas.width = canvas.width;
            }
        } catch {}
    }

    /**
     * Mount the composition to the canvas element
     */
    static mountComposition(): void {
        if (!this._composition) {
            return;
        }
        const container = document.getElementById(COMPOSITION_CONTAINER_ID) as HTMLDivElement | null;
        const canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement | null;

        if (!container || !canvas) {
            // Canvas/container may not be mounted yet (e.g. switching layouts / recording mode).
            // Keep mounted=false so the next call can retry.
            this._compositionMounted = false;
            return;
        }

        // Re-attach when the canvas element changes (React remount) or when we never attached.
        if (this.composition.canvas !== canvas) {
            this.composition.attachCanvasToComposition(canvas);
        }

        // Calculate and apply scaling (avoid scale(0) which makes the canvas "disappear").
        const baseW = this.composition.settings.width;
        const baseH = this.composition.settings.height;
        const cw = container.clientWidth;
        const ch = container.clientHeight;
        if (cw > 0 && ch > 0 && baseW > 0 && baseH > 0) {
            const scale = Math.min(cw / baseW, ch / baseH);
            if (Number.isFinite(scale) && scale > 0) {
                canvas.style.width = `${baseW}px`;
                canvas.style.height = `${baseH}px`;
                canvas.style.transform = `scale(${scale})`;
                canvas.style.transformOrigin = "center";
            }
        }

        this._compositionMounted = true;
    }

    /**
     * Update the composition scaling when container size changes
     */
    static updateScaling(): void {
        if (!this._composition) return;

        const canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement;

        if (!canvas) return;

        if (canvas.width !== this.composition.settings.width ||
            canvas.height !== this.composition.settings.height) {
            canvas.width = this.composition.settings.width;
            canvas.height = this.composition.settings.height;
            this.composition.computeFrame();
        }
    }

    /**
     * Cleanup the composition instance
     */
    static cleanup(): void {
        void this.destroy();
    }

    static getCurrentTime(): number {
        if (!this._composition) return 0;
        return this.composition.frame / this.composition.fps;
    }

    static getFitDuration(): number {
        if (!this._composition) return 0;
        return this.composition.duration.seconds;
    }

    /**
     * ScriptClip-defined composition end (seconds), if a script clip exists.
     * This is the authoritative composition bound for all non-script clips.
     */
    static getScriptEndSec(): number | null {
        try {
            const script: any = this.getScriptLayerClip?.();
            if (!script) return null;
            const start = Number(script.start?.seconds ?? 0);
            const dur = Number(script.timelineDuration?.seconds ?? 0);
            if (!Number.isFinite(start) || !Number.isFinite(dur)) return null;
            return start + dur;
        } catch {
            return null;
        }
    }

    /**
     * Clamp (trim/delete) all non-script clips so they do not extend past the ScriptClip end.
     * This enforces the invariant: for any non-script clip, clip.end <= scriptEnd.
     */
    static clampAllNonScriptClipsToScriptEnd(): void {
        const scriptEnd = this.getScriptEndSec();
        if (scriptEnd == null || !Number.isFinite(scriptEnd)) return;
        const EPS = 1e-3;

        for (const tr of (this.composition as any)?.tracks ?? []) {
            for (const clip of tr?.clips ?? []) {
                if (!clip) continue;
                if (String((clip as any).type) === "script") continue;

                const start = Number((clip as any).start?.seconds ?? 0);
                let dur = Number((clip as any).timelineDuration?.seconds ?? Number.NaN);
                if (!Number.isFinite(dur)) {
                    const stop = Number((clip as any).stop?.seconds ?? start);
                    dur = Math.max(0, stop - start);
                }
                if (!Number.isFinite(start) || !Number.isFinite(dur)) continue;

                const end = start + dur;
                if (start >= scriptEnd - EPS) {
                    this.deleteClipById(String((clip as any).id));
                    continue;
                }

                // Captions added "for all scenes" are composition-wide and should always
                // end exactly at the script end (including after undo / inserting time).
                try {
                    if (String((clip as any).type) === "captions" && String((clip as any).timingScope ?? "all") === "all") {
                        const delta = scriptEnd - end;
                        if (Math.abs(delta) > EPS) {
                            this.trimClipRight(String((clip as any).id), delta);
                        }
                        continue;
                    }
                } catch {}

                if (end > scriptEnd + EPS) {
                    const delta = scriptEnd - end; // negative (shrink)
                    this.trimClipRight(String((clip as any).id), delta);
                }
            }
        }
    }

    /**
     * Ensure the composition has a ScriptLayerClip spine. When the first inserted clip is non-script,
     * we create an empty script clip of the requested duration so the composition has authoritative bounds.
     */
    static async ensureEmptyScriptSpine(targetEndSec: number): Promise<void> {
        if (!this._composition) return;
        const session = this.getSessionVersion?.() ?? 0;
        const desired = Number(targetEndSec ?? 0);
        if (!Number.isFinite(desired) || desired <= 0) return;

        // Already exists: nothing to do (for now).
        const existing = this.getScriptLayerClip();
        if (existing) return;

        // Coalesce concurrent requests.
        this._ensureEmptyScriptSpineTargetEndSec = Math.max(this._ensureEmptyScriptSpineTargetEndSec, desired);
        if (this._ensureEmptyScriptSpinePromise) return this._ensureEmptyScriptSpinePromise;

        this._ensureEmptyScriptSpinePromise = (async () => {
            if ((this.getSessionVersion?.() ?? 0) !== session) return;
            const endSec = this._ensureEmptyScriptSpineTargetEndSec;

            const comp = this.composition;
            const width = Number((comp as any)?.width ?? 1920) || 1920;
            const height = Number((comp as any)?.height ?? 1080) || 1080;

            // Create an empty ScriptClip (no sources) and size it to the current canvas.
            const script = ScriptLayerClip.fromInputConfigs([], {
                position: { x: width / 2, y: height / 2 },
                width,
                height,
            } as any);
            script.name = "Script";
            script.duration.seconds = endSec;
            try {
                script.range = [Timestamp.fromSeconds(0), Timestamp.fromSeconds(endSec)];
            } catch {}

            // Init must treat empty sources as READY; see ScriptLayerClip.init().
            try {
                await script.init();
            } catch {}

            if ((this.getSessionVersion?.() ?? 0) !== session) return;
            await this.addToScriptLayer(script);

            // Notify transcript + scene bar to render "/" scenes immediately.
            try {
                this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                    clipId: script.id,
                    cuts: script.scriptCuts,
                    gaps: script.gaps,
                    gapInsertions: script.gapInsertions,
                    segments: script.scriptSegments,
                    splits: script.splits,
                    duration: script.duration?.seconds ?? 0,
                    hasVideo: false,
                });
            } catch {}

            try {
                this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                    script: {
                        layerId: (this.composition as any)?.scriptLayer?.layerId ?? null,
                        clipId: script.id,
                        splitsChanged: true,
                        insertionsChanged: true,
                        cutsChanged: true,
                        gapsChanged: true,
                    },
                });
            } catch {}

            // Now that the ScriptClip end exists, enforce the bound invariant.
            try { this.clampAllNonScriptClipsToScriptEnd(); } catch {}
        })()
            .finally(() => {
                this._ensureEmptyScriptSpinePromise = null;
                this._ensureEmptyScriptSpineTargetEndSec = 0;
            });

        return this._ensureEmptyScriptSpinePromise;
    }

    static getFps(): number {
        if (!this._composition) return 30;
        return this.composition.fps;
    }

    static async play(): Promise<void> {
        if (!this._composition) return;
        await this.composition.play();
    }

    static async pause(): Promise<void> {
        if (!this._composition) return;
        await this.composition.pause();
    }

    static async seek(time: number): Promise<void> {
        if (!this._composition) return;
        const fps = this.composition.fps;

        // Clamp seek to the authoritative composition bounds.
        // - If a ScriptClip exists, it defines composition end.
        // - Otherwise fall back to the composition fit duration.
        const scriptEnd = this.getScriptEndSec();
        const maxTime = scriptEnd != null ? scriptEnd : this.getFitDuration();
        const maxSeekTime = Math.max(0, maxTime - 1 / fps);
        const safeTime = Number.isFinite(time) ? time : 0;
        const clampedTime = Math.max(0, Math.min(safeTime, maxSeekTime));

        // Convert seconds to frames
        const frame = Math.round(clampedTime * fps);
        await this.composition.seek(frame);
    }

    static recomputeFrame(): void {
        this.composition.recomputeFrameAtCurrentTime();
    }

    static async nextFrame(): Promise<void> {
        if (!this._composition) return;
        // await this.composition.nextFrame();
    }

    static async previousFrame(): Promise<void> {
        if (!this._composition) return;
        // await this.composition.prevFrame();
    }

    static setBackgroundColor(color: hex): void {
        if (!this._composition) return;
        const next = String(color ?? "").trim();
        if (!next) return;

        const scriptClip: any = this.getScriptLayerClip();
        const scope = ApplicationStore.sceneEditScope;

        // Prefer per-scene background when a script spine exists (scene-aware).
        try {
            if (scriptClip && typeof scriptClip.getScenes === "function") {
                const compTime = Number(CompositionStateStore.currentTime ?? 0);
                const clipLocal = scriptClip.mapCompTimeToClipLocal?.(compTime);
                const sceneIndex =
                    clipLocal != null && typeof scriptClip.findSceneIndexForTime === "function"
                        ? Number(scriptClip.findSceneIndexForTime(clipLocal))
                        : -1;

                if (Number.isFinite(sceneIndex) && sceneIndex >= 0) {
                    if (scope === "all") {
                        const scenes: any[] = Array.isArray(scriptClip.getScenes()) ? scriptClip.getScenes() : [];
                        for (const s of scenes) {
                            const idx = Number((s as any)?.index);
                            if (!Number.isFinite(idx) || idx < 0) continue;
                            scriptClip.setSceneProperties?.(idx, { backgroundColor: next });
                        }
                        // Keep the composition-level default aligned so newly-created scenes match.
                        try { this.composition.changeBackground?.(next as any); } catch {}
                    } else {
                        scriptClip.setSceneProperties?.(sceneIndex, { backgroundColor: next });
                    }

                    try { this.composition.computeFrame(); } catch {}
                    try { this.notifyClipUpdated(scriptClip); } catch {}
                    return;
                }
            }
        } catch {}

        // Fallback: global background.
        try {
            // Prefer engine method to update renderer + canvas immediately
            // @ts-ignore
            this.composition.changeBackground?.(next as any);
        } catch {
            // Fallback: just update settings (may not repaint immediately)
            // @ts-ignore
            this.composition.settings.background = next as any;
        }
    }

    /**
     * Set visual zoom as a percent (100 = no zoom).
     */
    static setClipZoomPercent(visualElement: VisualElementRef, percent: number): void {
        const clip: any = this.getClipById(visualElement.clipId);
        if (!clip) return;
        const pct = Math.max(100, Number.isFinite(percent) ? percent : 100);
        try {
            const s = pct / 100;
            clip.scale = { x: s, y: s };
            // force a frame so overlay/UI updates immediately
            clip.trigger?.('frame', undefined);
            this.composition.computeFrame();
            CompositionService.triggerInteractiveElementRefresh(visualElement);
            this.notifyClipUpdated(clip);
        } catch {}
    }

    /**
     * Apply position preset: 'fill' or 'fit' relative to the composition canvas.
     * Keeps aspect ratio by setting only one dimension and clearing the other.
     */
    static setClipPositionPreset(visualElement: VisualElementRef, preset: 'fill' | 'fit'): void {
        const clip: any = this.getClipById(visualElement.clipId);
        if (!clip) return;
        const comp = this._composition;
        if (!comp) return;
        const cw = comp.settings.width ?? comp.width ?? 1920;
        const ch = comp.settings.height ?? comp.height ?? 1080;
        const scope = ApplicationStore.sceneEditScope;

        // ScriptLayerClip: apply preset to the selected video source, not the whole clip.
        if (String(clip.type) === 'script' && typeof clip.updateVideoSourceTransform === 'function') {
            try { (clip as any).__librettoAutoDefaultPlacementUserEdited = true; } catch {}
            const sourceIndex = Number.isFinite(Number(visualElement.sourceIndex))
                ? Number(visualElement.sourceIndex)
                : 0;

            const originalDims = typeof clip.getOriginalSourceDimensions === 'function'
                ? clip.getOriginalSourceDimensions(sourceIndex)
                : undefined;
            const currentVisual = typeof clip.getVideoSourceVisual === 'function'
                ? clip.getVideoSourceVisual(sourceIndex)
                : undefined;

            const sourceW = Number(originalDims?.width ?? currentVisual?.originalWidth ?? currentVisual?.width ?? 0);
            const sourceH = Number(originalDims?.height ?? currentVisual?.originalHeight ?? currentVisual?.height ?? 0);
            if (!(sourceW > 0 && sourceH > 0)) return;

            const widthRatio = cw / sourceW;
            const heightRatio = ch / sourceH;
            const scaler = preset === 'fit'
                ? Math.min(widthRatio, heightRatio)
                : Math.max(widthRatio, heightRatio);

            try {
                const spansMultipleScenes = this.clipSpansMultipleScenes(clip);
                const useSceneOverrides = scope === "scene" && spansMultipleScenes;
                const useAllScope = scope === "all" && spansMultipleScenes;

                const next = {
                    width: sourceW * scaler,
                    height: sourceH * scaler,
                    x: cw / 2,
                    y: ch / 2,
                };

                if (useSceneOverrides && typeof clip.setSceneVisualProperties === "function") {
                    const scenes: any[] = Array.isArray(clip.getScenes?.()) ? clip.getScenes() : [];
                    const t = Number(CompositionStateStore.currentTime ?? 0);
                    const selectedIdx = ApplicationStore.selectedSceneIndex;
                    const scene =
                        selectedIdx != null
                            ? scenes.find((s: any) => Number(s?.index) === Number(selectedIdx))
                            : (scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
                              scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
                              scenes[scenes.length - 1]);
                    const sceneIndex = Number(scene?.index ?? 0);

                    clip.setSceneVisualProperties(sceneIndex, sourceIndex, next);
                } else {
                    if (useAllScope && typeof clip.clearSceneVisualOverrideKeysForSource === "function") {
                        try { clip.clearSceneVisualOverrideKeysForSource(sourceIndex, ["x", "y", "width", "height"]); } catch {}
                    }
                    clip.updateVideoSourceTransform(sourceIndex, next);
                }
                this.composition.computeFrame();
                this.triggerInteractiveElementRefresh({ clipId: visualElement.clipId, sourceIndex });
                try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
                this.notifyClipUpdated(clip);
            } catch {}
            return;
        }

        const clipMediaResoultion = CompositionStateStore.clipIdToMediaResolution[visualElement.clipId] ?? {width: 0, height: 0};

        const sourceW = clipMediaResoultion.width;
        const sourceH = clipMediaResoultion.height;

        // const sAR = sourceW / sourceH;
        // const cAR = cw / ch;

        if (!(sourceW > 0 && sourceH > 0)) return;

        const widthRatio = cw / sourceW;
        const heightRatio = ch / sourceH;

        try {
            const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(clip);
            const useSceneOverrides = scope === "scene" && spansMultipleScenes;
            const useAllScope = scope === "all" && spansMultipleScenes;

            if (preset === 'fit') {
                const scaler = Math.min(widthRatio, heightRatio);
                const w = sourceW * scaler;
                const h = sourceH * scaler;
                if (useSceneOverrides) {
                    const sceneKey = CompositionService.getCurrentSceneKey();
                    if (sceneKey) {
                        CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, { width: w, height: h, x: cw / 2, y: ch / 2 });
                    } else {
                        clip.width = w;
                        clip.height = h;
                        clip.position = { x: cw / 2, y: ch / 2 };
                    }
                } else {
                    if (useAllScope) {
                        CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["x", "y", "width", "height"]);
                    }
                    clip.width = w;
                    clip.height = h;
                }
            } else {
                const scaler = Math.max(widthRatio, heightRatio);
                const w = sourceW * scaler;
                const h = sourceH * scaler;
                if (useSceneOverrides) {
                    const sceneKey = CompositionService.getCurrentSceneKey();
                    if (sceneKey) {
                        CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, { width: w, height: h, x: cw / 2, y: ch / 2 });
                    } else {
                        clip.width = w;
                        clip.height = h;
                        clip.position = { x: cw / 2, y: ch / 2 };
                    }
                } else {
                    if (useAllScope) {
                        CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["x", "y", "width", "height"]);
                    }
                    clip.width = w;
                    clip.height = h;
                }
            }
            // Center in canvas (only when not using per-scene overrides, which already include x/y).
            if (!useSceneOverrides) {
                clip.position = { x: cw / 2, y: ch / 2 };
            }
            clip.trigger?.('frame', undefined);
            this.composition.computeFrame();
            this.triggerInteractiveElementRefresh(visualElement);
            try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
            this.notifyClipUpdated(clip);
        } catch {}
    }

    /**
     * Apply a simple shape mask (e.g. circle) to a visual element (video/image or script-layer source).
     * Respects scene edit scope when the clip spans multiple scenes.
     */
    static setClipMaskShape(visualElement: VisualElementRef, shape: MaskShape): void {
        const clip: any = this.getClipById(visualElement.clipId);
        if (!clip) return;
        const scope = ApplicationStore.sceneEditScope;

        // ScriptLayerClip: apply to the selected video source (supports per-scene overrides).
        if (String(clip.type) === "script" && typeof (clip as any).updateVideoSourceTransform === "function") {
            try { (clip as any).__librettoAutoDefaultPlacementUserEdited = true; } catch {}
            const sourceIndex = Number.isFinite(Number(visualElement.sourceIndex))
                ? Number(visualElement.sourceIndex)
                : 0;

            const spansMultipleScenes = this.clipSpansMultipleScenes(clip);
            const useAllScope = scope === "all" && spansMultipleScenes;

            try {
                if (useAllScope) {
                    try { (clip as any).clearSceneVisualOverrideKeysForSource?.(sourceIndex, ["maskShape"]); } catch {}
                    try { (clip as any).updateVideoSourceTransform(sourceIndex, { maskShape: shape }); } catch {}
                } else {
                    // Use selected scene if set; otherwise scene at playhead.
                    const scenes: any[] = Array.isArray((clip as any).getScenes?.()) ? (clip as any).getScenes() : [];
                    const explicit = ApplicationStore.selectedSceneIndex;
                    const t = Number(CompositionStateStore.currentTime ?? 0);
                    const scene =
                        explicit != null
                            ? scenes.find((s: any) => Number(s?.index) === Number(explicit)) ?? scenes[explicit]
                            : scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
                              scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
                              scenes[scenes.length - 1];
                    const sceneIndex = Number(scene?.index);
                    if (Number.isFinite(sceneIndex)) {
                        try { (clip as any).setSceneVisualProperties?.(sceneIndex, sourceIndex, { maskShape: shape }); } catch {}
                    } else {
                        // Fallback: no scene context, treat as base.
                        try { (clip as any).updateVideoSourceTransform(sourceIndex, { maskShape: shape }); } catch {}
                    }
                }

                this.composition.computeFrame();
                this.triggerInteractiveElementRefresh({ clipId: visualElement.clipId, sourceIndex });
                this.notifyClipUpdated(clip);
            } catch {}
            return;
        }

        // Regular visual clips (video/image).
        const isVisual = String(clip.type) === "video" || String(clip.type) === "image";
        if (!isVisual) return;

        try {
            const spansMultipleScenes = this.clipSpansMultipleScenes(clip);
            const useSceneOverrides = scope === "scene" && spansMultipleScenes;
            const useAllScope = scope === "all" && spansMultipleScenes;

            if (useSceneOverrides) {
                const sceneKey = this.getCurrentSceneKey();
                if (sceneKey) {
                    this.setSceneVisualOverridesForClip(clip, sceneKey, { maskShape: shape });
                } else {
                    (clip as any).maskShape = shape;
                }
            } else {
                if (useAllScope) {
                    this.clearSceneVisualOverrideKeysForClip(clip, ["maskShape"]);
                }
                (clip as any).maskShape = shape;
            }

            clip.trigger?.("frame", undefined);
            this.composition.computeFrame();
            this.triggerInteractiveElementRefresh(visualElement);
            this.notifyClipUpdated(clip);
        } catch {}
    }

    /**
     * Apply a position preset ('fit' | 'fill') to ALL video sources in a ScriptLayerClip.
     * Intended for sensible defaults when inserting a brand-new script clip.
     *
     * This avoids UI selection side-effects (unlike setClipPositionPreset which refreshes selection).
     */
    static setScriptClipPositionPresetForAllVideoSources(clipId: string, preset: 'fill' | 'fit' = 'fit'): void {
        const clip: any = this.getClipById(clipId);
        if (!clip || String(clip.type) !== 'script') return;
        const comp = this._composition;
        if (!comp) return;

        const cw = comp.settings.width ?? comp.width ?? 1920;
        const ch = comp.settings.height ?? comp.height ?? 1080;

        const sources: any[] = typeof clip.getVideoSources === 'function' ? clip.getVideoSources() : [];
        if (!Array.isArray(sources) || sources.length === 0) return;

        const getTrackIdForSource = (sourceIndex: number): string | null => {
            try {
                const cfgs: any[] = typeof clip.getInputConfigs === "function" ? clip.getInputConfigs() : [];
                const entries: any[] = typeof clip.getVideoEntries === "function" ? clip.getVideoEntries() : [];
                const entry = entries?.[sourceIndex];
                if (!entry?.input) return null;
                const cfg = cfgs.find((c: any) => c?.input === entry.input);
                const trackId = String(cfg?.assetId ?? "");
                return trackId || null;
            } catch {}
            return null;
        };

        const placeWithFocus = (args: {
            regionLeft: number;
            regionTop: number;
            regionW: number;
            regionH: number;
            scaledW: number;
            scaledH: number;
            focusRect: FocusRect | null;
        }): { x: number; y: number } => {
            const regionLeft = Number(args.regionLeft ?? 0);
            const regionTop = Number(args.regionTop ?? 0);
            const regionW = Number(args.regionW ?? 0);
            const regionH = Number(args.regionH ?? 0);
            const w = Number(args.scaledW ?? 0);
            const h = Number(args.scaledH ?? 0);
            const centerX = regionLeft + regionW / 2;
            const centerY = regionTop + regionH / 2;

            if (!(w > 0 && h > 0 && regionW > 0 && regionH > 0)) return { x: centerX, y: centerY };
            const focus = args.focusRect;
            if (!focus) return { x: centerX, y: centerY };

            const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

            const minX = regionLeft + Math.min(w / 2, regionW - w / 2);
            const maxX = regionLeft + Math.max(w / 2, regionW - w / 2);
            const minY = regionTop + Math.min(h / 2, regionH - h / 2);
            const maxY = regionTop + Math.max(h / 2, regionH - h / 2);

            const focusCx = (Number(focus.x ?? 0) + Number(focus.w ?? 0) / 2) * w;
            const focusCy = (Number(focus.y ?? 0) + Number(focus.h ?? 0) / 2) * h;

            const desiredX = centerX - focusCx + w / 2;
            const desiredY = centerY - focusCy + h / 2;

            return {
                x: clamp(desiredX, minX, maxX),
                y: clamp(desiredY, minY, maxY),
            };
        };

        for (let i = 0; i < sources.length; i++) {
            const originalDims = typeof clip.getOriginalSourceDimensions === 'function'
                ? clip.getOriginalSourceDimensions(i)
                : undefined;
            const currentVisual = typeof clip.getVideoSourceVisual === 'function'
                ? clip.getVideoSourceVisual(i)
                : undefined;
            const sourceSprite = typeof clip.getVideoSprite === "function"
                ? clip.getVideoSprite(i)
                : undefined;

            const sourceW = Number(originalDims?.width ?? currentVisual?.originalWidth ?? currentVisual?.width ?? 0);
            const sourceH = Number(originalDims?.height ?? currentVisual?.originalHeight ?? currentVisual?.height ?? 0);
            if (!(sourceW > 0 && sourceH > 0)) continue;

            const rotationDeg = Number((sourceSprite as any)?.angle ?? currentVisual?.rotation ?? 0);
            const rotationRad = (rotationDeg * Math.PI) / 180;
            const absCos = Math.abs(Math.cos(rotationRad));
            const absSin = Math.abs(Math.sin(rotationRad));
            const effectiveW = Math.max(1, sourceW * absCos + sourceH * absSin);
            const effectiveH = Math.max(1, sourceW * absSin + sourceH * absCos);

            const widthRatio = cw / effectiveW;
            const heightRatio = ch / effectiveH;
            const scaler = preset === 'fit'
                ? Math.min(widthRatio, heightRatio)
                : Math.max(widthRatio, heightRatio);

            try {
                const trackId = getTrackIdForSource(i);
                const focusRect = trackId ? FocusStore.getQuickSummary(trackId)?.rect ?? null : null;
                const placed = placeWithFocus({
                    regionLeft: 0,
                    regionTop: 0,
                    regionW: cw,
                    regionH: ch,
                    scaledW: sourceW * scaler,
                    scaledH: sourceH * scaler,
                    focusRect,
                });
                clip.updateVideoSourceTransform(i, {
                    width: sourceW * scaler,
                    height: sourceH * scaler,
                    x: placed.x,
                    y: placed.y,
                });
            } catch {}
        }

        try {
            this.composition.computeFrame();
        } catch {}
        this.notifyClipUpdated(clip);
    }

    private static getCanvasAspectPresetFromSize(
        w: number,
        h: number
    ): "horizontal" | "vertical" | "square" {
        const ww = Number(w);
        const hh = Number(h);
        if (!Number.isFinite(ww) || !Number.isFinite(hh) || ww <= 0 || hh <= 0) return "horizontal";
        const ratio = ww / hh;
        if (Math.abs(ratio - 1) < 0.06) return "square";
        return ratio < 1 ? "vertical" : "horizontal";
    }

    private static getTrackRecordByTrackId(trackId?: string): any | undefined {
        const id = String(trackId ?? "");
        if (!id) return undefined;
        return (ProjectAssetsStore.projectTracks ?? []).find((t: any) => {
            const tid = String(t?.id ?? "");
            const tTrackId = String(t?.trackId ?? "");
            return tid === id || tTrackId === id;
        });
    }

    /**
     * Apply the "no layout" default placement for script-layer video sources based on:
     * - 1 source: fit + centered
     * - 2 sources: Screen layout if one is a screen recording, else MultiCam
     *
     * This is used on script clip insertion and on canvas resize/aspect changes.
     */
    static applyDefaultScriptClipPlacement(clipId: string): void {
        const clip: any = this.getClipById(clipId);
        if (!clip || String(clip.type) !== "script") return;
        const comp = this._composition;
        if (!comp) return;

        const cw = Number(comp.settings.width ?? comp.width ?? 1920);
        const ch = Number(comp.settings.height ?? comp.height ?? 1080);
        if (!(cw > 0 && ch > 0)) return;

        const sources: any[] = typeof clip.getVideoSources === "function" ? clip.getVideoSources() : [];
        if (!Array.isArray(sources) || sources.length === 0) return;

        const aspectPreset = this.getCanvasAspectPresetFromSize(cw, ch);

        let needsFocus = false;
        const getFocusRectForTrack = (trackId: string | null): FocusRect | null => {
            if (!trackId) return null;
            const rect =
                FocusStore.getRectAtTimeSec(trackId, 0) ??
                FocusStore.getQuickSummary(trackId)?.rect ??
                null;
            if (!rect) needsFocus = true;
            return rect;
        };

        const recordNeedsFocusFlag = () => {
            try {
                (clip as any).__librettoAutoDefaultPlacementNeedsFocus = Boolean(needsFocus);
            } catch {}
        };

        const getTrackIdForSource = (srcIdx: number): string | null => {
            try {
                const cfgs: any[] = typeof clip.getInputConfigs === "function" ? clip.getInputConfigs() : [];
                const entries: any[] = typeof clip.getVideoEntries === "function" ? clip.getVideoEntries() : [];
                const entry = entries?.[srcIdx];
                if (!entry?.input) return null;
                const cfg = cfgs.find((c: any) => c?.input === entry.input);
                const trackId = String(cfg?.assetId ?? "");
                return trackId || null;
            } catch {}
            return null;
        };

        const placeWithFocus = (args: {
            regionLeft: number;
            regionTop: number;
            regionW: number;
            regionH: number;
            scaledW: number;
            scaledH: number;
            focusRect: FocusRect | null;
        }): { x: number; y: number } => {
            const regionLeft = Number(args.regionLeft ?? 0);
            const regionTop = Number(args.regionTop ?? 0);
            const regionW = Number(args.regionW ?? 0);
            const regionH = Number(args.regionH ?? 0);
            const w = Number(args.scaledW ?? 0);
            const h = Number(args.scaledH ?? 0);
            const centerX = regionLeft + regionW / 2;
            const centerY = regionTop + regionH / 2;

            if (!(w > 0 && h > 0 && regionW > 0 && regionH > 0)) return { x: centerX, y: centerY };
            const focus = args.focusRect;
            if (!focus) return { x: centerX, y: centerY };

            const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

            const minX = regionLeft + Math.min(w / 2, regionW - w / 2);
            const maxX = regionLeft + Math.max(w / 2, regionW - w / 2);
            const minY = regionTop + Math.min(h / 2, regionH - h / 2);
            const maxY = regionTop + Math.max(h / 2, regionH - h / 2);

            const focusCx = (Number(focus.x ?? 0) + Number(focus.w ?? 0) / 2) * w;
            const focusCy = (Number(focus.y ?? 0) + Number(focus.h ?? 0) / 2) * h;

            const desiredX = centerX - focusCx + w / 2;
            const desiredY = centerY - focusCy + h / 2;

            return {
                x: clamp(desiredX, minX, maxX),
                y: clamp(desiredY, minY, maxY),
            };
        };

        const isScreenSource = (srcIdx: number): boolean => {
            try {
                const cfgs: any[] = typeof clip.getInputConfigs === "function" ? clip.getInputConfigs() : [];
                const entries: any[] = typeof clip.getVideoEntries === "function" ? clip.getVideoEntries() : [];
                const entry = entries[srcIdx];
                if (!entry?.input) return false;
                const cfg = cfgs.find((c: any) => c?.input === entry.input);
                const trackId = getTrackIdForSource(srcIdx) ?? String(cfg?.assetId ?? "");
                const tr = this.getTrackRecordByTrackId(trackId);
                if (typeof tr?.isScreenRecording === "boolean") return Boolean(tr.isScreenRecording);
                const title = String(tr?.title ?? cfg?.name ?? "");
                return /screen/i.test(title);
            } catch {}
            return false;
        };

        // 1 source: fit to canvas.
        if (sources.length === 1) {
            try {
                const trackId = getTrackIdForSource(0);
                getFocusRectForTrack(trackId);
            } catch {}
            this.setScriptClipPositionPresetForAllVideoSources(clipId, "fit");
            recordNeedsFocusFlag();
            return;
        }

        // 2 sources: Screen default (one screen recording) else MultiCam.
        if (sources.length === 2) {
            const screenIdx = isScreenSource(0) ? 0 : isScreenSource(1) ? 1 : -1;
            const camIdx = screenIdx === 0 ? 1 : screenIdx === 1 ? 0 : -1;

            if (screenIdx >= 0 && camIdx >= 0) {
                // Screen: fit (contain) + center.
                try {
                    const dims =
                        typeof clip.getOriginalSourceDimensions === "function"
                            ? clip.getOriginalSourceDimensions(screenIdx)
                            : undefined;
                    const vis =
                        typeof clip.getVideoSourceVisual === "function"
                            ? clip.getVideoSourceVisual(screenIdx)
                            : undefined;
                    const sw = Number(dims?.width ?? vis?.originalWidth ?? vis?.width ?? 0);
                    const sh = Number(dims?.height ?? vis?.originalHeight ?? vis?.height ?? 0);
                    if (sw > 0 && sh > 0) {
                        const s = Math.min(cw / sw, ch / sh);
                        const trackId = getTrackIdForSource(screenIdx);
                        const focusRect = getFocusRectForTrack(trackId);
                        const placed = placeWithFocus({
                            regionLeft: 0,
                            regionTop: 0,
                            regionW: cw,
                            regionH: ch,
                            scaledW: sw * s,
                            scaledH: sh * s,
                            focusRect,
                        });
                        clip.updateVideoSourceTransform(screenIdx, {
                            width: sw * s,
                            height: sh * s,
                            x: placed.x,
                            y: placed.y,
                            alpha: 1,
                        });
                    }
                } catch {}

                // Camera: circle, 1/4 canvas height, anchored bottom-left with margin.
                try {
                    const dims =
                        typeof clip.getOriginalSourceDimensions === "function"
                            ? clip.getOriginalSourceDimensions(camIdx)
                            : undefined;
                    const vis =
                        typeof clip.getVideoSourceVisual === "function"
                            ? clip.getVideoSourceVisual(camIdx)
                            : undefined;
                    const sw = Number(dims?.width ?? vis?.originalWidth ?? vis?.width ?? 0);
                    const sh = Number(dims?.height ?? vis?.originalHeight ?? vis?.height ?? 0);
                    if (sw > 0 && sh > 0) {
                        const targetH = ch * 0.25;
                        const s = targetH / sh;
                        const w = sw * s;
                        const h = sh * s;
                        const margin = Math.max(12, Math.min(cw, ch) * 0.02);
                        clip.updateVideoSourceTransform(camIdx, {
                            width: w,
                            height: h,
                            x: margin + w / 2,
                            y: ch - margin - h / 2,
                            alpha: 1,
                            maskShape: "circle",
                        });
                    }
                } catch {}

                // Ensure other sources (none in this case) are visible.
            } else {
                // MultiCam: horizontal = side-by-side, vertical/square = stacked.
                const isStacked = aspectPreset === "vertical" || aspectPreset === "square";
                const regionW = isStacked ? cw : cw / 2;
                const regionH = isStacked ? ch / 2 : ch;

                for (let i = 0; i < 2; i++) {
                    try {
                        const dims =
                            typeof clip.getOriginalSourceDimensions === "function"
                                ? clip.getOriginalSourceDimensions(i)
                                : undefined;
                        const vis =
                            typeof clip.getVideoSourceVisual === "function"
                                ? clip.getVideoSourceVisual(i)
                                : undefined;
                        const sw = Number(dims?.width ?? vis?.originalWidth ?? vis?.width ?? 0);
                        const sh = Number(dims?.height ?? vis?.originalHeight ?? vis?.height ?? 0);
                        if (!(sw > 0 && sh > 0)) continue;

                        const s = Math.min(regionW / sw, regionH / sh);
                        const w = sw * s;
                        const h = sh * s;
                        const regionLeft = isStacked ? 0 : regionW * i;
                        const regionTop = isStacked ? regionH * i : 0;
                        const trackId = getTrackIdForSource(i);
                        const focusRect = getFocusRectForTrack(trackId);
                        const placed = placeWithFocus({
                            regionLeft,
                            regionTop,
                            regionW,
                            regionH,
                            scaledW: w,
                            scaledH: h,
                            focusRect,
                        });

                        clip.updateVideoSourceTransform(i, {
                            width: w,
                            height: h,
                            x: placed.x,
                            y: placed.y,
                            alpha: 1,
                            maskShape: "none",
                        });
                    } catch {}
                }
            }

            try {
                this.composition.computeFrame();
            } catch {}
            this.notifyClipUpdated(clip);
            recordNeedsFocusFlag();
            return;
        }

        // 3+ sources: MultiCam defaults (no crop). Horizontal supports up to 6, vertical/square up to 4 by default.
        if (sources.length >= 3) {
            const screenIdxs = sources
                .map((_, idx) => (isScreenSource(idx) ? idx : -1))
                .filter((idx) => idx >= 0);
            const screenIdx = screenIdxs.length ? screenIdxs[0] : -1;
            const camIdxs = sources.map((_, idx) => idx).filter((idx) => idx !== screenIdx);

            const applyFitToRegion = (srcIdx: number, region: { left: number; top: number; w: number; h: number }) => {
                try {
                    const dims =
                        typeof clip.getOriginalSourceDimensions === "function"
                            ? clip.getOriginalSourceDimensions(srcIdx)
                            : undefined;
                    const vis =
                        typeof clip.getVideoSourceVisual === "function"
                            ? clip.getVideoSourceVisual(srcIdx)
                            : undefined;
                    const sw = Number(dims?.width ?? vis?.originalWidth ?? vis?.width ?? 0);
                    const sh = Number(dims?.height ?? vis?.originalHeight ?? vis?.height ?? 0);
                    if (!(sw > 0 && sh > 0)) return;

                    const s = Math.min(region.w / sw, region.h / sh);
                    const w = sw * s;
                    const h = sh * s;
                    const trackId = getTrackIdForSource(srcIdx);
                    const focusRect = getFocusRectForTrack(trackId);
                    const placed = placeWithFocus({
                        regionLeft: region.left,
                        regionTop: region.top,
                        regionW: region.w,
                        regionH: region.h,
                        scaledW: w,
                        scaledH: h,
                        focusRect,
                    });

                    clip.updateVideoSourceTransform(srcIdx, {
                        width: w,
                        height: h,
                        x: placed.x,
                        y: placed.y,
                        alpha: 1,
                        maskShape: "none",
                    });
                } catch {}
            };

            // Horizontal: if we have a screen recording, default to "two/three/four + screen" arrangement.
            if (aspectPreset === "horizontal" && screenIdx >= 0 && camIdxs.length >= 2) {
                const leftW = cw * 0.67;
                const rightW = cw - leftW;
                const screenRegion = { left: 0, top: 0, w: leftW, h: ch };

                const camCountWanted = Math.min(camIdxs.length, 4);
                const camRegions: Array<{ left: number; top: number; w: number; h: number }> = [];

                if (camCountWanted <= 3) {
                    const rowH = ch / camCountWanted;
                    for (let i = 0; i < camCountWanted; i++) {
                        camRegions.push({ left: leftW, top: i * rowH, w: rightW, h: rowH });
                    }
                } else {
                    const cellW = rightW / 2;
                    const cellH = ch / 2;
                    camRegions.push({ left: leftW, top: 0, w: cellW, h: cellH });
                    camRegions.push({ left: leftW + cellW, top: 0, w: cellW, h: cellH });
                    camRegions.push({ left: leftW, top: cellH, w: cellW, h: cellH });
                    camRegions.push({ left: leftW + cellW, top: cellH, w: cellW, h: cellH });
                }

                // Screen source
                applyFitToRegion(screenIdx, screenRegion);

                // Camera sources on the right
                const camsToPlace = camIdxs.slice(0, camRegions.length);
                for (let i = 0; i < camsToPlace.length; i++) {
                    applyFitToRegion(camsToPlace[i], camRegions[i]);
                }

                // Hide any extras beyond what we placed to avoid overlap.
                for (let i = 0; i < sources.length; i++) {
                    if (i === screenIdx) continue;
                    if (camsToPlace.includes(i)) continue;
                    try { clip.updateVideoSourceTransform(i, { alpha: 0 }); } catch {}
                }

                try { this.composition.computeFrame(); } catch {}
                this.notifyClipUpdated(clip);
                recordNeedsFocusFlag();
                return;
            }

            // Otherwise, grid/stack MultiCam arrangement based on aspect.
            const regions: Array<{ left: number; top: number; w: number; h: number }> = [];
            const pushGrid = (cols: number, rows: number, maxCells: number) => {
                const cellW = cw / cols;
                const cellH = ch / rows;
                for (let r = 0; r < rows; r++) {
                    for (let c = 0; c < cols; c++) {
                        if (regions.length >= maxCells) return;
                        regions.push({ left: c * cellW, top: r * cellH, w: cellW, h: cellH });
                    }
                }
            };

            const count = sources.length;
            const countWanted =
                aspectPreset === "horizontal"
                    ? Math.min(count, 6)
                    : aspectPreset === "vertical" || aspectPreset === "square"
                    ? Math.min(count, 4)
                    : Math.min(count, 6);

            if (aspectPreset === "vertical") {
                if (countWanted === 3) pushGrid(1, 3, 3);
                else pushGrid(2, 2, countWanted);
            } else if (aspectPreset === "square") {
                if (countWanted === 3) pushGrid(2, 2, 3);
                else if (countWanted === 4) pushGrid(2, 2, 4);
                else pushGrid(2, 2, countWanted);
            } else {
                if (countWanted === 3) pushGrid(3, 1, 3);
                else if (countWanted === 4) pushGrid(2, 2, 4);
                else if (countWanted === 5) pushGrid(3, 2, 5);
                else pushGrid(3, 2, countWanted);
            }

            const indicesToPlace = sources.map((_, idx) => idx).slice(0, regions.length);
            for (let i = 0; i < indicesToPlace.length; i++) {
                applyFitToRegion(indicesToPlace[i], regions[i]);
            }

            for (let i = 0; i < sources.length; i++) {
                if (indicesToPlace.includes(i)) continue;
                try { clip.updateVideoSourceTransform(i, { alpha: 0 }); } catch {}
            }

            try { this.composition.computeFrame(); } catch {}
            this.notifyClipUpdated(clip);
            recordNeedsFocusFlag();
            return;
        }

        // Fallback: fit all sources to canvas.
        this.setScriptClipPositionPresetForAllVideoSources(clipId, "fit");
        recordNeedsFocusFlag();
    }

    /**
     * If a script clip was auto-placed before focus analysis was available, re-apply the default
     * placement once focus arrives. Guarded so we never override user edits.
     */
    static maybeReapplyDefaultScriptClipPlacementFromFocus(): void {
        try {
            const scriptClip: any = this.getScriptLayerClip?.();
            if (!scriptClip || String(scriptClip.type) !== "script") return;

            const auto = Boolean((scriptClip as any).__librettoAutoDefaultPlacement);
            if (!auto) return;

            const userEdited = Boolean((scriptClip as any).__librettoAutoDefaultPlacementUserEdited);
            if (userEdited) return;

            const needsFocus = Boolean((scriptClip as any).__librettoAutoDefaultPlacementNeedsFocus);
            if (!needsFocus) return;

            const sources: any[] = typeof scriptClip.getVideoSources === "function" ? scriptClip.getVideoSources() : [];
            if (!Array.isArray(sources) || sources.length === 0) return;

            const cfgs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
            const entries: any[] = typeof scriptClip.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];

            const getTrackIdForSource = (srcIdx: number): string | null => {
                try {
                    const entry = entries?.[srcIdx];
                    if (!entry?.input) return null;
                    const cfg = cfgs.find((c: any) => c?.input === entry.input);
                    const trackId = String(cfg?.assetId ?? "");
                    return trackId || null;
                } catch {}
                return null;
            };

            // Only re-apply once focus is available for ALL sources.
            for (let i = 0; i < sources.length; i++) {
                const trackId = getTrackIdForSource(i);
                if (!trackId) continue;
                const rect =
                    FocusStore.getRectAtTimeSec(trackId, 0) ??
                    FocusStore.getQuickSummary(trackId)?.rect ??
                    null;
                if (!rect) return;
            }

            (scriptClip as any).__librettoAutoDefaultPlacementNeedsFocus = false;
            this.applyDefaultScriptClipPlacement(String(scriptClip.id));
        } catch {}
    }

    static triggerInteractiveElementRefresh(ref: VisualElementRef | null): void {
        ApplicationStore.clearSelectedVisualElement();
        ApplicationStore.setSelectedVisualElement(ref);
    }

    static setResolution(width: number, height: number): void {
        if (!this._composition) return;
        this.composition.settings.width = width;
        this.composition.settings.height = height;
    }

    static getFonts() {
        // TODO: Implement when needed
    }

    static getMediaById(mediaId: string) {
        // TODO: Implement when needed
    }

    static addMediaToGallery(file: File | string, mimeType?: string) {
        // TODO: Implement when needed
    }

    static getClipById(clipId: string) {
        const comp = this._composition;
        if (!comp) return undefined;
        for (const tr of comp.tracks) {
            // @ts-ignore
            const found = tr.clips.find((c: any) => c.id === clipId);
            if (found) return found;
        }
        return undefined;
    }

    /**
     * Notify subscribers that a clip's properties changed.
     * Use this for visual/crop updates which don't naturally emit CLIP_UPDATED.
     */
    static notifyClipUpdated(clip: any, opts?: { timingChanged?: boolean; transient?: boolean }): void {
        if (!clip) return;
        try {
            (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip, transient: Boolean(opts?.transient) });
        } catch {}
        try {
            (this.composition as any)?.trigger?.(EngineInternalEventsEnum.CLIP_UPDATED, { clip, transient: Boolean(opts?.transient) });
        } catch {}
        if (opts?.timingChanged) {
            try { (this.composition as any)?.onClipTimingChanged?.(); } catch {}
        }
    }

    /**
     * Update a media clip's in/out window (range) and offset together.
     * This is used for Slip-style edits where the timeline position stays fixed
     * but the source segment shifts.
     */
    static setMediaClipWindow(
        clipId: string,
        window: { inSec: number; outSec: number; offsetSec: number },
        opts?: { transient?: boolean },
    ): void {
        const clip: any = this.getClipById(clipId);
        if (!clip) return;
        if (!("range" in clip) || !("_offset" in clip)) return;
        if (String(clip.type ?? "") === "script") return;

        const inSecRaw = Number(window?.inSec ?? 0);
        const outSecRaw = Number(window?.outSec ?? 0);
        const offsetSecRaw = Number(window?.offsetSec ?? 0);
        if (!Number.isFinite(inSecRaw) || !Number.isFinite(outSecRaw) || outSecRaw <= inSecRaw) return;

        const mediaDur = Number(clip.duration?.seconds ?? 0);
        const windowLen = Math.max(0, outSecRaw - inSecRaw);
        const maxIn = mediaDur > 0 ? Math.max(0, mediaDur - windowLen) : Math.max(0, inSecRaw);
        const inSec = Math.max(0, Math.min(maxIn, inSecRaw));
        const outSec = inSec + windowLen;
        const offsetSec = Number.isFinite(offsetSecRaw) ? offsetSecRaw : 0;

        try {
            if (Array.isArray(clip.range)) {
                if (clip.range[0]) clip.range[0].seconds = inSec;
                if (clip.range[1]) clip.range[1].seconds = outSec;
                if (!clip.range[0] || !clip.range[1]) {
                    clip.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(outSec)];
                }
            } else {
                clip.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(outSec)];
            }
        } catch {
            clip.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(outSec)];
        }

        // Avoid MediaClip.offset setter during transient updates (it rebuilds schedules continuously).
        try {
            if (clip._offset && typeof clip._offset === "object") {
                clip._offset.seconds = offsetSec;
            } else {
                clip._offset = Timestamp.fromSeconds(offsetSec);
            }
        } catch {
            clip._offset = Timestamp.fromSeconds(offsetSec);
        }

        try { clip.normalizeCuts?.(); } catch {}
        try { clip.deriveSegments?.(); } catch {}
        try { clip.trigger?.("frame", undefined); } catch {}

        this.notifyClipUpdated(clip, { timingChanged: !opts?.transient, transient: Boolean(opts?.transient) });
    }

    static getLayerById(layerId: string) {
        const comp = this._composition;
        if (!comp) return undefined;
        for (const tr of comp.tracks) {
            // @ts-ignore
            if (tr.id === layerId) return tr;
        }
        return undefined;
    }

    static async createLayer(index?: number, clipId: string = '') {
        const clip = this.getClipById(clipId);
        if (!clip) return;

        return await CompositionService.composition.addAt(clip, index);
    }

    static deleteLayer(layerId: string) {
        const track: any = this.getLayerById(layerId);
        if (!track) {
            return;
        }
        try {
            // Prefer removing via the composition to ensure state/view is cleaned up
            this.composition.removeTrack(track);
        } catch (e) {
            console.error('deleteLayer failed:', e);
        }
    }

    static async moveClipToLayer(clipId: string, layerId: string): Promise<boolean> {
        const clip: any = this.getClipById(clipId);
        if (!clip) return false;
        if (!this.canMoveClipToLayer(clipId, layerId)) return false;

        const destTrack: any = this.getLayerById(layerId);
        if (!destTrack) return false;

        // No-op: already on that layer.
        if (clip?.track?.id === layerId) return true;

        clip.detach();
        await destTrack.add(clip);
        return true;
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // Layer Order Operations (for visual clips)
    // Track index 0 is "front"/top layer in rendering order.
    // These operations swap clips between tracks to change layer order without creating new tracks.
    // ─────────────────────────────────────────────────────────────────────────────

    /** Get the time interval (start, end) for a clip in seconds */
    private static _getClipInterval(clipId: string): { start: number; end: number } | null {
        const uiClip = CompositionStateStore.clips?.[clipId];
        if (uiClip && typeof uiClip.startTime === 'number' && typeof uiClip.trimmedDuration === 'number') {
            return { start: uiClip.startTime, end: uiClip.startTime + uiClip.trimmedDuration };
        }
        const clip: any = this.getClipById(clipId);
        if (!clip) return null;
        const start = clip.start?.seconds ?? 0;
        const stop = clip.stop?.seconds ?? (start + (clip.timelineDuration?.seconds ?? 0));
        return { start, end: stop };
    }

    /** Check if two time intervals overlap */
    private static _intervalsOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
        return a.start < b.end && b.start < a.end;
    }

    /** Check if a clip is visual (video/image/captions, or script with video sources) */
    static isVisualClip(clip: any): boolean {
        if (!clip) return false;
        if (clip.type === 'video' || clip.type === 'image' || clip.type === 'captions' || clip.type === 'text' || clip.type === 'subtitles' || clip.type === 'placeholder') return true;
        if (clip.type === 'script' && typeof clip.getVideoSources === 'function') {
            return (clip.getVideoSources?.()?.length ?? 0) > 0;
        }
        return false;
    }

    private static _getClipMediaKind(clip: any): 'audio' | 'visual' | 'script' | 'unknown' {
        if (!clip) return 'unknown';
        const type = String(clip.type);
        if (type === 'audio') return 'audio';
        if (type === 'script') return 'script';
        if (this.isVisualClip(clip)) return 'visual';
        return 'unknown';
    }

    private static _getTrackMediaKind(track: any): 'script' | 'audio' | 'visual' | 'mixed' | 'empty' {
        if (!track) return 'empty';
        if (this._isScriptLayerTrack(track)) return 'script';
        const clips: any[] = Array.isArray(track?.clips) ? track.clips : [];
        let hasAudio = false;
        let hasVisual = false;
        for (const c of clips) {
            const kind = this._getClipMediaKind(c);
            if (kind === 'audio') hasAudio = true;
            if (kind === 'visual') hasVisual = true;
        }
        if (hasAudio && hasVisual) return 'mixed';
        if (hasAudio) return 'audio';
        if (hasVisual) return 'visual';
        return 'empty';
    }

    static canMoveClipToLayer(clipId: string, layerId: string): boolean {
        const clip: any = this.getClipById(clipId);
        if (!clip) return false;
        // ScriptClip defines the composition spine and must remain on the script layer.
        if (String(clip.type) === 'script' || this.isScriptLayerClip(clipId)) {
            return false;
        }

        const destTrack: any = this.getLayerById(layerId);
        if (!destTrack) return false;
        // Never allow non-script clips onto the script layer track.
        if (this._isScriptLayerTrack(destTrack)) return false;

        // Placeholder clips are treated as lightweight overlay tracks. They should not share tracks with
        // video/image clips (prevents confusing track heights + ensures "replace placeholder" works cleanly).
        try {
            const destClips: any[] = Array.isArray(destTrack?.clips) ? destTrack.clips : [];
            const destHasVideoOrImage = destClips.some((c) => c && (String(c.type) === "video" || String(c.type) === "image"));
            const destHasPlaceholder = destClips.some((c) => c && String(c.type) === "placeholder");
            const t = String(clip?.type ?? "");
            if (t === "placeholder" && destHasVideoOrImage) return false;
            if ((t === "video" || t === "image") && destHasPlaceholder) return false;
        } catch {}

        const clipKind = this._getClipMediaKind(clip);
        const trackKind = this._getTrackMediaKind(destTrack);

        if (clipKind === 'audio') return trackKind !== 'visual' && trackKind !== 'script';
        if (clipKind === 'visual') return trackKind !== 'audio' && trackKind !== 'script';
        return trackKind !== 'script';
    }
    
    /** Check if a clip is the script layer clip (should not be moved in layer order operations) */
    static isScriptLayerClip(clipId: string): boolean {
        const scriptLayerInfo = (this._composition as any)?.scriptLayer;
        if (!scriptLayerInfo) return false;
        return scriptLayerInfo.clipId === clipId;
    }
    
    /** Check if a track is the script layer track (should be skipped in layer order swaps) */
    private static _isScriptLayerTrack(track: any): boolean {
        const scriptLayerInfo = (this._composition as any)?.scriptLayer;
        if (!scriptLayerInfo) return false;
        return scriptLayerInfo.layerId === track?.id;
    }

    /** Find an overlapping visual clip on a track (if any) */
    private static _findOverlappingVisualOnTrack(
        track: any, 
        interval: { start: number; end: number }, 
        excludeClipId?: string
    ): any | null {
        const clips = track?.clips ?? [];
        for (const c of clips) {
            if (!c || c.id === excludeClipId) continue;
            if (!this.isVisualClip(c)) continue;
            const other = this._getClipInterval(c.id);
            if (other && this._intervalsOverlap(interval, other)) {
                return c;
            }
        }
        return null;
    }

    /** Check if a clip can be placed on a track without overlapping existing clips (ignoring specified clips) */
    private static _canPlaceOnTrack(
        track: any, 
        interval: { start: number; end: number }, 
        ignoreClipIds: Set<string>
    ): boolean {
        const clips = track?.clips ?? [];
        for (const c of clips) {
            if (!c || ignoreClipIds.has(c.id)) continue;
            const other = this._getClipInterval(c.id);
            if (other && this._intervalsOverlap(interval, other)) return false;
        }
        return true;
    }

    /**
     * Bring a visual clip's track one layer forward (toward index 0 = front).
     * Uses track.layer() which properly handles Pixi stage and triggers events.
     * Also updates CompositionStateStore.orderedLayerIds for timeline UI sync.
     * Returns true if the track was moved, false if already at front.
     */
    static bringClipForward(clipId: string): boolean {
        const comp = this._composition;
        if (!comp) return false;
        
        const clip: any = this.getClipById(clipId);
        if (!clip || !clip.track) return false;
        if (!this.isVisualClip(clip)) return false;
        if (this._isScriptLayerTrack(clip.track)) return false;

        const tracks: any[] = (comp as any).tracks ?? [];
        const currentTrack = clip.track;
        const currentIndex = tracks.findIndex((t) => t?.id === currentTrack.id);
        
        // Already at front (index 0)?
        if (currentIndex <= 0) return false;

        const scriptIndex = tracks.findIndex((t) => this._isScriptLayerTrack(t));
        const minAllowedIndex = scriptIndex >= 0 && scriptIndex < currentIndex ? scriptIndex + 1 : 0;
        if (currentIndex <= minAllowedIndex) return false;

        // Find the closest non-audio track above. If only audio tracks exist above, move to minAllowedIndex.
        let targetIndex = -1;
        for (let i = currentIndex - 1; i >= minAllowedIndex; i--) {
            const kind = this._getTrackMediaKind(tracks[i]);
            if (kind === 'audio') continue;
            if (kind === 'script') break;
            targetIndex = i;
            break;
        }
        if (targetIndex === -1) targetIndex = minAllowedIndex;
        if (targetIndex >= currentIndex) return false;

        currentTrack.layer(targetIndex);
        
        // Rebuild stage visual order (needed for ScriptLayerClip containers)
        this._rebuildStageOrder();
        
        // Sync CompositionStateStore.orderedLayerIds with new track order
        this._syncOrderedLayerIds();
        
        // Re-render current frame
        (this.composition as any)?.computeFrame?.();
        
        return true;
    }

    /**
     * Send a visual clip's track one layer backward (toward higher index = back).
     * Uses track.layer() which properly handles Pixi stage and triggers events.
     * Also updates CompositionStateStore.orderedLayerIds for timeline UI sync.
     * Returns true if the track was moved, false if already at back.
     */
    static sendClipBackward(clipId: string): boolean {
        const comp = this._composition;
        if (!comp) return false;
        
        const clip: any = this.getClipById(clipId);
        if (!clip || !clip.track) return false;
        if (!this.isVisualClip(clip)) return false;
        if (this._isScriptLayerTrack(clip.track)) return false;

        const tracks: any[] = (comp as any).tracks ?? [];
        const currentTrack = clip.track;
        const currentIndex = tracks.findIndex((t) => t?.id === currentTrack.id);
        
        // Already at back (last index)?
        if (currentIndex < 0 || currentIndex >= tracks.length - 1) return false;

        const scriptIndex = tracks.findIndex((t) => this._isScriptLayerTrack(t));
        const maxAllowedIndex = scriptIndex >= 0 && scriptIndex > currentIndex ? scriptIndex - 1 : tracks.length - 1;
        if (maxAllowedIndex <= currentIndex) return false;

        // Find the closest non-audio track below. If only audio tracks exist below, move to maxAllowedIndex.
        let targetIndex = -1;
        for (let i = currentIndex + 1; i <= maxAllowedIndex; i++) {
            const kind = this._getTrackMediaKind(tracks[i]);
            if (kind === 'audio') continue;
            if (kind === 'script') break;
            targetIndex = i;
            break;
        }
        if (targetIndex === -1) targetIndex = maxAllowedIndex;
        if (targetIndex <= currentIndex) return false;

        currentTrack.layer(targetIndex);
        
        // Rebuild stage visual order (needed for ScriptLayerClip containers)
        this._rebuildStageOrder();
        
        // Sync CompositionStateStore.orderedLayerIds with new track order
        this._syncOrderedLayerIds();
        
        // Re-render current frame
        (this.composition as any)?.computeFrame?.();
        
        return true;
    }
    
    /**
     * Sync CompositionStateStore.orderedLayerIds with the current composition.tracks order.
     * This ensures the timeline UI reflects the actual Pixi rendering order.
     */
    private static _syncOrderedLayerIds(opts?: { persist?: boolean }): void {
        const comp = this._composition;
        if (!comp) return;
        
        const tracks: any[] = (comp as any).tracks ?? [];
        // orderedLayerIds is stored in reverse order (last track first in array)
        const newOrderedLayerIds = tracks.map((t: any) => t.id).reverse();

        // Avoid churn if nothing actually changed.
        const current = CompositionStateStore.orderedLayerIds ?? [];
        const same =
            current.length === newOrderedLayerIds.length &&
            current.every((id, i) => id === newOrderedLayerIds[i]);
        if (!same) {
            // Update the MobX store (triggers timeline UI re-render)
            CompositionStateStore.setOrderedLayerIds(newOrderedLayerIds);
        }

        // Also persist to Firestore unless explicitly suppressed.
        if (opts?.persist !== false && !same) {
            CompositionPersistenceService.setOrderedLayerIds(newOrderedLayerIds);
        }
    }

    /**
     * Ensure the script layer track renders behind all other tracks by default.
     * This matches the mental model: ScriptClip is the spine/base; other layers sit above it.
     *
     * Important: ScriptLayerClip video containers are direct stage children, so we also rebuild
     * stage order when we move the track.
     */
    static ensureScriptLayerTrackAtBack(opts?: { persist?: boolean }): boolean {
        const comp = this._composition;
        if (!comp) return false;

        const scriptLayerId = (comp as any)?.scriptLayer?.layerId;
        if (!scriptLayerId) return false;

        const tracks: any[] = (comp as any).tracks ?? [];
        const idx = tracks.findIndex((t) => t?.id === scriptLayerId);
        if (idx < 0) return false;
        const desired = Math.max(0, tracks.length - 1);
        if (idx === desired) return false;

        const track: any = tracks[idx];
        try {
            track?.layer?.("bottom");
        } catch {
            try {
                track?.layer?.(desired);
            } catch {}
        }

        // Rebuild stage visual order (needed for ScriptLayerClip containers)
        try { this._rebuildStageOrder(); } catch {}

        // Sync UI ordering for the timeline.
        try { this._syncOrderedLayerIds(opts); } catch {}

        // Re-render current frame so users immediately see the correct stacking.
        try { (this.composition as any)?.computeFrame?.(); } catch {}

        return true;
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // Global Layer Order Operations (tracks + script sources)
    //
    // Goal: layer-order actions on non-script clips should be able to move above/below
    // ScriptLayerClip visual elements (sources), not just reorder among other tracks.
    //
    // Implementation: when a track hits the "ceiling" (can't surpass script sources via
    // track index), we adjust the neighboring script source z-values instead (by changing
    // ScriptLayerClip source layerIndex in the same z-domain used by _rebuildStageOrder()).
    // ─────────────────────────────────────────────────────────────────────────────

    private static _applyScriptSourceZUpdates(
        updates: Array<{ sourceIndex: number; desiredZ: number }>,
        scriptBaseZ: number
    ): void {
        if (!updates.length) return;
        const scriptClip: any = this.getScriptLayerClip?.();
        if (!scriptClip || !this._isScriptLayerClipObject(scriptClip)) return;

        for (const u of updates) {
            if (!u || !Number.isFinite(u.desiredZ) || !Number.isFinite(u.sourceIndex)) continue;
            const newLayerIndex = u.desiredZ - scriptBaseZ;
            try {
                scriptClip.setSourceLayerIndex(u.sourceIndex, newLayerIndex);
            } catch {}
        }

        this._rebuildStageOrder();
        (this.composition as any)?.computeFrame?.();
        this.notifyClipUpdated(scriptClip);
    }

    private static _bringTrackForwardInVisualStack(clipId: string): boolean {
        const clip: any = this.getClipById(clipId);
        if (!clip || !clip.track) return false;
        if (!this.isVisualClip(clip)) return false;
        if (this._isScriptLayerTrack(clip.track)) return false;

        const { stack, scriptBaseZ } = this._getVisualZStack();
        const trackId = String(clip.track.id ?? "");
        const idx = stack.findIndex((it) => it.kind === "track" && it.trackId === trackId);
        if (idx < 0 || idx >= stack.length - 1) return false;

        const next = stack[idx + 1];
        if (next.kind === "track") {
            return this.bringClipForward(clipId);
        }

        // Swap with the neighboring script source by pushing it just behind the track.
        const upper = stack[idx].z;
        const lower = idx > 0 ? stack[idx - 1].z : upper - 1;
        const MIN_Z_GAP = 1;
        const desiredZ =
            Number.isFinite(lower) && Number.isFinite(upper) && upper > lower
                ? upper - Math.min(MIN_Z_GAP, (upper - lower) / 2)
                : upper - MIN_Z_GAP;

        this._applyScriptSourceZUpdates([{ sourceIndex: Number(next.sourceIndex), desiredZ }], scriptBaseZ);
        return true;
    }

    private static _sendTrackBackwardInVisualStack(clipId: string): boolean {
        const clip: any = this.getClipById(clipId);
        if (!clip || !clip.track) return false;
        if (!this.isVisualClip(clip)) return false;
        if (this._isScriptLayerTrack(clip.track)) return false;

        const { stack, scriptBaseZ } = this._getVisualZStack();
        const trackId = String(clip.track.id ?? "");
        const idx = stack.findIndex((it) => it.kind === "track" && it.trackId === trackId);
        if (idx <= 0) return false;

        const prev = stack[idx - 1];
        if (prev.kind === "track") {
            return this.sendClipBackward(clipId);
        }

        // Swap with neighboring script source by pulling it just in front of the track.
        const lower = stack[idx].z;
        const upper = idx < stack.length - 1 ? stack[idx + 1].z : lower + 1;
        const MIN_Z_GAP = 1;
        const desiredZ =
            Number.isFinite(lower) && Number.isFinite(upper) && upper > lower
                ? lower + Math.min(MIN_Z_GAP, (upper - lower) / 2)
                : lower + MIN_Z_GAP;

        this._applyScriptSourceZUpdates([{ sourceIndex: Number(prev.sourceIndex), desiredZ }], scriptBaseZ);
        return true;
    }

    private static _bringTrackToFrontInVisualStack(clipId: string): void {
        const clip: any = this.getClipById(clipId);
        if (!clip || !clip.track) return;
        if (!this.isVisualClip(clip)) return;
        if (this._isScriptLayerTrack(clip.track)) return;

        // First, bring the track to the front among tracks.
        this.bringClipToFront(clipId);

        // Then, ensure any script sources that were above it are placed just behind it.
        const { stack, scriptBaseZ } = this._getVisualZStack();
        const trackId = String(clip.track.id ?? "");
        const idx = stack.findIndex((it) => it.kind === "track" && it.trackId === trackId);
        if (idx < 0) return;

        const zTrack = stack[idx].z;
        const aboveSources = stack
            // Include tie case (`z === zTrack`): in stage ordering, script sources win ties over tracks.
            .filter((it) => it.kind === "scriptSource" && Number.isFinite(it.z) && it.z >= zTrack)
            .sort((a, b) => a.z - b.z);
        if (!aboveSources.length) return;

        const lower = idx > 0 ? stack[idx - 1].z : zTrack - 1;
        const upper = zTrack;
        const span = Number.isFinite(lower) && Number.isFinite(upper) ? Math.max(0, upper - lower) : 0;
        const step = span > 0 ? span / (aboveSources.length + 1) : 0;

        const updates: Array<{ sourceIndex: number; desiredZ: number }> = [];
        for (let i = 0; i < aboveSources.length; i++) {
            const it = aboveSources[i];
            let desiredZ: number;
            if (step > 0) {
                desiredZ = lower + step * (i + 1);
            } else {
                // Degenerate: no room between lower/upper; still force behind the track.
                desiredZ = upper - 1 * (aboveSources.length - i);
            }
            // Ensure strictly behind the track to avoid tie-breaking putting the source on top.
            if (!(desiredZ < upper)) desiredZ = upper - 1 * (aboveSources.length - i);
            updates.push({ sourceIndex: Number(it.sourceIndex), desiredZ });
        }
        this._applyScriptSourceZUpdates(updates, scriptBaseZ);
    }

    private static _sendTrackToBackInVisualStack(clipId: string): void {
        const clip: any = this.getClipById(clipId);
        if (!clip || !clip.track) return;
        if (!this.isVisualClip(clip)) return;
        if (this._isScriptLayerTrack(clip.track)) return;

        // First, send the track to the back among tracks (but never below the script layer track).
        this.sendClipToBack(clipId);

        // Then, ensure any script sources that were behind it are placed just in front of it.
        const { stack, scriptBaseZ } = this._getVisualZStack();
        const trackId = String(clip.track.id ?? "");
        const idx = stack.findIndex((it) => it.kind === "track" && it.trackId === trackId);
        if (idx < 0) return;

        const zTrack = stack[idx].z;
        const belowSources = stack
            .filter((it) => it.kind === "scriptSource" && Number.isFinite(it.z) && it.z < zTrack)
            .sort((a, b) => a.z - b.z);
        if (!belowSources.length) return;

        const upper = idx < stack.length - 1 ? stack[idx + 1].z : zTrack + 1;
        const lower = zTrack;
        const span = Number.isFinite(lower) && Number.isFinite(upper) ? Math.max(0, upper - lower) : 0;
        const step = span > 0 ? span / (belowSources.length + 1) : 0;

        const updates: Array<{ sourceIndex: number; desiredZ: number }> = [];
        for (let i = 0; i < belowSources.length; i++) {
            const it = belowSources[i];
            let desiredZ: number;
            if (step > 0) {
                desiredZ = lower + step * (i + 1);
            } else {
                // Degenerate: no room; still force in front of the track.
                desiredZ = lower + 1 * (i + 1);
            }
            // Ensure strictly in front of the track.
            if (!(desiredZ > lower)) desiredZ = lower + 1 * (i + 1);
            updates.push({ sourceIndex: Number(it.sourceIndex), desiredZ });
        }
        this._applyScriptSourceZUpdates(updates, scriptBaseZ);
    }
    
    /**
     * Rebuild the Pixi stage children order to match the editor's visual z-stack.
     *
     * - Track order still drives non-script clip ordering (index 0 = front).
     * - ScriptLayerClip video source containers are direct stage children and can be
     *   re-ordered globally relative to tracks via their `layerIndex`.
     *
     * Stage rendering order: last child renders on top.
     */
    private static _rebuildStageOrder(): void {
        const comp = this._composition;
        if (!comp) return;

        const stage = (comp as any).stage;
        if (!stage) return;

        const tracks: any[] = (comp as any).tracks ?? [];

        // Use a stable, track-index-based Z domain that doesn't depend on track count.
        // Front track (index 0) should have the highest Z.
        const TRACK_Z_STEP = 1000;
        const trackZ = (trackIndex: number): number => -trackIndex * TRACK_Z_STEP;

        const items: Array<{ child: any; z: number; kind: "track" | "scriptSource" }> = [];

        // Track views (these contain non-script visual clips).
        for (let i = 0; i < tracks.length; i++) {
            const track = tracks[i];
            if (track?.view && track.view.parent === stage) {
                items.push({ child: track.view, z: trackZ(i), kind: "track" });
            }
        }

        // ScriptLayerClip source containers (direct stage children).
        try {
            const scriptClip: any = this.getScriptLayerClip?.();
            if (scriptClip && scriptClip._videoContainers && typeof scriptClip.getVideoEntriesSortedByLayer === "function") {
                const scriptTrackId = (comp as any)?.scriptLayer?.layerId ?? null;
                const scriptTrackIndex =
                    typeof scriptTrackId === "string" && scriptTrackId
                        ? tracks.findIndex((t) => t?.id === scriptTrackId)
                        : -1;
                const scriptBaseZ = scriptTrackIndex >= 0 ? trackZ(scriptTrackIndex) : trackZ(tracks.length);

                const sortedEntries = scriptClip.getVideoEntriesSortedByLayer(); // back-to-front by layerIndex
                for (const { entry } of sortedEntries) {
                    const container = scriptClip._videoContainers.get(entry.input);
                    if (!container || container.parent !== stage) continue;
                    const li = Number(entry.layerIndex ?? 0);
                    const layerIndex = Number.isFinite(li) ? li : 0;
                    items.push({ child: container, z: scriptBaseZ + layerIndex, kind: "scriptSource" });
                }
            }
        } catch {}

        // Sort back-to-front (ascending z) then re-add so higher z renders on top.
        items.sort((a, b) => {
            if (a.z !== b.z) return a.z - b.z;
            // On ties, keep tracks below script sources so a script source can sit "on top" of a track.
            if (a.kind !== b.kind) return a.kind === "track" ? -1 : 1;
            return 0;
        });

        for (const { child } of items) {
            if (child?.parent === stage) stage.removeChild(child);
            stage.addChild(child);
        }
    }

    /**
     * Compute a global visual z-stack for tracks + ScriptLayerClip sources.
     * Higher `z` renders on top. Uses the same z domain as `_rebuildStageOrder()`.
     */
    private static _getVisualZStack(): { stack: Array<{ kind: "track" | "scriptSource"; z: number; trackId?: string; clipId?: string; sourceIndex?: number }>; scriptBaseZ: number } {
        const comp = this._composition;
        if (!comp) return { stack: [], scriptBaseZ: 0 };

        const tracks: any[] = (comp as any).tracks ?? [];
        const TRACK_Z_STEP = 1000;
        const trackZ = (trackIndex: number): number => -trackIndex * TRACK_Z_STEP;

        const stack: Array<{ kind: "track" | "scriptSource"; z: number; trackId?: string; clipId?: string; sourceIndex?: number }> = [];

        // Track layers (skip audio-only tracks for visual ordering ops).
        for (let i = 0; i < tracks.length; i++) {
            const track = tracks[i];
            if (!track) continue;
            const kind = this._getTrackMediaKind?.(track);
            if (kind === "audio") continue;
            stack.push({ kind: "track", z: trackZ(i), trackId: String(track.id ?? "") });
        }

        const scriptTrackId = (comp as any)?.scriptLayer?.layerId ?? null;
        const scriptTrackIndex =
            typeof scriptTrackId === "string" && scriptTrackId
                ? tracks.findIndex((t) => t?.id === scriptTrackId)
                : -1;
        const scriptBaseZ = scriptTrackIndex >= 0 ? trackZ(scriptTrackIndex) : trackZ(tracks.length);

        // Script sources
        try {
            const scriptClip: any = this.getScriptLayerClip?.();
            if (scriptClip && typeof scriptClip.getVideoEntries === "function") {
                const entries = scriptClip.getVideoEntries() ?? [];
                for (let sourceIndex = 0; sourceIndex < entries.length; sourceIndex++) {
                    const entry = entries[sourceIndex];
                    const li = Number(entry?.layerIndex ?? 0);
                    const layerIndex = Number.isFinite(li) ? li : 0;
                    stack.push({
                        kind: "scriptSource",
                        z: scriptBaseZ + layerIndex,
                        clipId: String(scriptClip.id ?? ""),
                        sourceIndex,
                    });
                }
            }
        } catch {}

        // Back-to-front (match _rebuildStageOrder() tie-breaking so neighbor operations behave consistently)
        stack.sort((a, b) => {
            if (a.z !== b.z) return a.z - b.z;
            if (a.kind !== b.kind) return a.kind === "track" ? -1 : 1;
            return 0;
        });
        return { stack, scriptBaseZ };
    }

    /**
     * Bring a visual clip's track all the way to front (index 0).
     */
    static bringClipToFront(clipId: string): void {
        const comp = this._composition;
        if (!comp) return;
        
        const clip: any = this.getClipById(clipId);
        if (!clip || !clip.track) return;
        if (!this.isVisualClip(clip)) return;
        if (this._isScriptLayerTrack(clip.track)) return;
        
        const tracks: any[] = (comp as any).tracks ?? [];
        const currentIndex = tracks.findIndex((t) => t?.id === clip.track.id);
        if (currentIndex < 0) return;

        const scriptIndex = tracks.findIndex((t) => this._isScriptLayerTrack(t));
        const minAllowedIndex = scriptIndex >= 0 && scriptIndex < currentIndex ? scriptIndex + 1 : 0;
        if (currentIndex <= minAllowedIndex) return;

        clip.track.layer(minAllowedIndex);
        this._rebuildStageOrder();
        this._syncOrderedLayerIds();
        (this.composition as any)?.computeFrame?.();
    }

    /**
     * Send a visual clip's track all the way to back (last index).
     */
    static sendClipToBack(clipId: string): void {
        const comp = this._composition;
        if (!comp) return;
        
        const clip: any = this.getClipById(clipId);
        if (!clip || !clip.track) return;
        if (!this.isVisualClip(clip)) return;
        if (this._isScriptLayerTrack(clip.track)) return;
        
        const tracks: any[] = (comp as any).tracks ?? [];
        const currentIndex = tracks.findIndex((t) => t?.id === clip.track.id);
        if (currentIndex < 0) return;

        const scriptIndex = tracks.findIndex((t) => this._isScriptLayerTrack(t));
        const maxAllowedIndex = scriptIndex >= 0 && scriptIndex > currentIndex ? scriptIndex - 1 : tracks.length - 1;
        if (maxAllowedIndex <= currentIndex) return;

        clip.track.layer(maxAllowedIndex);
        this._rebuildStageOrder();
        this._syncOrderedLayerIds();
        (this.composition as any)?.computeFrame?.();
    }
    
    // ─────────────────────────────────────────────────────────────────────────────
    // Source-level layer operations for ScriptLayerClip visual elements
    // ─────────────────────────────────────────────────────────────────────────────
    
    /**
     * Check if a clip object is a ScriptLayerClip (by examining its properties)
     */
    private static _isScriptLayerClipObject(clip: any): boolean {
        return clip?.type === 'script' && typeof clip.getVideoEntries === 'function';
    }
    
    /**
     * Bring a specific source within a ScriptLayerClip one layer forward.
     * This only changes the source's z-order within the clip, not the clip's track.
     */
    static bringSourceForward(clipId: string, sourceIndex: number): boolean {
        const clip: any = this.getClipById(clipId);
        if (!clip || !this._isScriptLayerClipObject(clip)) return false;

        const { stack, scriptBaseZ } = this._getVisualZStack();
        const idx = stack.findIndex(
            (it) => it.kind === "scriptSource" && it.clipId === String(clipId) && it.sourceIndex === sourceIndex
        );
        if (idx < 0) return false;
        if (idx >= stack.length - 1) return false;

        const next = stack[idx + 1];
        const nextNext = stack[idx + 2];
        const lower = next.z;
        const upper = nextNext?.z;

        let desiredZ: number;
        if (upper === undefined || !Number.isFinite(upper)) {
            desiredZ = lower + 1;
        } else if (!(upper > lower)) {
            desiredZ = lower + 0.001;
        } else {
            desiredZ = lower + (upper - lower) / 2;
        }

        const newLayerIndex = desiredZ - scriptBaseZ;
        clip.setSourceLayerIndex(sourceIndex, newLayerIndex);
        this._rebuildStageOrder();
        (this.composition as any)?.computeFrame?.();
        this.notifyClipUpdated(clip);
        return true;
    }
    
    /**
     * Send a specific source within a ScriptLayerClip one layer backward.
     * This only changes the source's z-order within the clip, not the clip's track.
     */
    static sendSourceBackward(clipId: string, sourceIndex: number): boolean {
        const clip: any = this.getClipById(clipId);
        if (!clip || !this._isScriptLayerClipObject(clip)) return false;

        const { stack, scriptBaseZ } = this._getVisualZStack();
        const idx = stack.findIndex(
            (it) => it.kind === "scriptSource" && it.clipId === String(clipId) && it.sourceIndex === sourceIndex
        );
        if (idx < 0) return false;
        if (idx <= 0) return false;

        const prev = stack[idx - 1];
        const prevPrev = stack[idx - 2];
        const upper = prev.z;
        const lower = prevPrev?.z;

        let desiredZ: number;
        if (lower === undefined || !Number.isFinite(lower)) {
            desiredZ = upper - 1;
        } else if (!(upper > lower)) {
            desiredZ = upper - 0.001;
        } else {
            desiredZ = lower + (upper - lower) / 2;
        }

        const newLayerIndex = desiredZ - scriptBaseZ;
        clip.setSourceLayerIndex(sourceIndex, newLayerIndex);
        this._rebuildStageOrder();
        (this.composition as any)?.computeFrame?.();
        this.notifyClipUpdated(clip);
        return true;
    }
    
    /**
     * Bring a specific source within a ScriptLayerClip to the front.
     * This only changes the source's z-order within the clip, not the clip's track.
     */
    static bringSourceToFront(clipId: string, sourceIndex: number): void {
        const clip: any = this.getClipById(clipId);
        if (!clip || !this._isScriptLayerClipObject(clip)) return;

        const { stack, scriptBaseZ } = this._getVisualZStack();
        const maxZ = stack.reduce((acc, it) => (it.z > acc ? it.z : acc), Number.NEGATIVE_INFINITY);
        const desiredZ = (Number.isFinite(maxZ) ? maxZ : 0) + 1;
        const newLayerIndex = desiredZ - scriptBaseZ;

        clip.setSourceLayerIndex(sourceIndex, newLayerIndex);
        this._rebuildStageOrder();
        (this.composition as any)?.computeFrame?.();
        this.notifyClipUpdated(clip);
    }
    
    /**
     * Send a specific source within a ScriptLayerClip to the back.
     * This only changes the source's z-order within the clip, not the clip's track.
     */
    static sendSourceToBack(clipId: string, sourceIndex: number): void {
        const clip: any = this.getClipById(clipId);
        if (!clip || !this._isScriptLayerClipObject(clip)) return;

        const { stack, scriptBaseZ } = this._getVisualZStack();
        const minZ = stack.reduce((acc, it) => (it.z < acc ? it.z : acc), Number.POSITIVE_INFINITY);
        const desiredZ = (Number.isFinite(minZ) ? minZ : 0) - 1;
        const newLayerIndex = desiredZ - scriptBaseZ;

        clip.setSourceLayerIndex(sourceIndex, newLayerIndex);
        this._rebuildStageOrder();
        (this.composition as any)?.computeFrame?.();
        this.notifyClipUpdated(clip);
    }
    
    /**
     * Smart layer operations that handle both regular clips and ScriptLayerClip sources.
     * For ScriptLayerClip with a selected source, operate on the source.
     * For regular clips, operate on the track.
     */
    static bringVisualForward(clipId: string, sourceIndex?: number): boolean {
        const clip: any = this.getClipById(clipId);
        if (!clip) return false;
        
        // If it's a ScriptLayerClip with a sourceIndex, move just that source
        if (this._isScriptLayerClipObject(clip) && sourceIndex !== undefined) {
            return this.bringSourceForward(clipId, sourceIndex);
        }
        
        // Otherwise, move the whole track in the global visual stack (tracks + script sources)
        return this._bringTrackForwardInVisualStack(clipId);
    }
    
    static sendVisualBackward(clipId: string, sourceIndex?: number): boolean {
        const clip: any = this.getClipById(clipId);
        if (!clip) return false;
        
        if (this._isScriptLayerClipObject(clip) && sourceIndex !== undefined) {
            return this.sendSourceBackward(clipId, sourceIndex);
        }
        
        return this._sendTrackBackwardInVisualStack(clipId);
    }
    
    static bringVisualToFront(clipId: string, sourceIndex?: number): void {
        const clip: any = this.getClipById(clipId);
        if (!clip) return;
        
        if (this._isScriptLayerClipObject(clip) && sourceIndex !== undefined) {
            this.bringSourceToFront(clipId, sourceIndex);
            return;
        }
        
        this._bringTrackToFrontInVisualStack(clipId);
    }
    
    static sendVisualToBack(clipId: string, sourceIndex?: number): void {
        const clip: any = this.getClipById(clipId);
        if (!clip) return;
        
        if (this._isScriptLayerClipObject(clip) && sourceIndex !== undefined) {
            this.sendSourceToBack(clipId, sourceIndex);
            return;
        }
        
        this._sendTrackToBackInVisualStack(clipId);
    }

    // ─────────────────────────────────────────────────────────────────────────────

    static async addMediaToLayer(layerId: string, mediaId: string, dropTime = CompositionService.getCurrentTime()) {
        // TODO: Implement when needed
    }

    static addTextClipToLayer(layerId: string, text: string = "Hello World") {
        // TODO: Implement when needed
    }

    static deleteMediaById(mediaId: string) {
        // TODO: Implement when needed
    }

    static deleteClipById(clipId: string) {
        const clip: any = this.getClipById(clipId);
        if (!clip) {
            return;
        }
        const isScript = String(clip.type) === "script" || this.isScriptLayerClip(String(clipId));

        // Invariant: non-script clips cannot exist without the ScriptClip spine.
        // If the user deletes the ScriptClip (e.g. deletes the last remaining scene), remove all other clips too.
        if (isScript) {
            const otherClipIds: string[] = [];
            try {
                for (const tr of (this.composition as any)?.tracks ?? []) {
                    for (const c of tr?.clips ?? []) {
                        if (!c) continue;
                        const id = String((c as any).id ?? "");
                        if (!id || id === String(clipId)) continue;
                        if (String((c as any).type) === "script") continue;
                        otherClipIds.push(id);
                    }
                }
            } catch {}

            // Delete from top-level to allow tracks to self-remove when empty.
            for (const id of otherClipIds) {
                try {
                    this.deleteClipById(id);
                } catch {}
            }
        }
        try {
            // Prefer removing via the owning track to ensure state/view is cleaned up
            clip.track?.remove(clip);
            // CLIP_REMOVED also fires for transient detach/re-layer operations, so
            // metadata cleanup belongs on this explicit deletion path rather than
            // in CompositionStateStore.removeClip().
            CompositionStateStore.removeMetadataForClip(clipId);
            // Clear the engine's scriptLayer clipId so future lookups don't point at a deleted clip.
            if (isScript) {
                try {
                    const sl = (this.composition as any)?.scriptLayer;
                    if (sl && sl.clipId === clipId) {
                        (this.composition as any).scriptLayer = { ...sl, clipId: null };
                    }
                } catch {}
            }
            // Re-render once so the visible canvas clears when the last visual clip is removed.
            this.composition?.recomputeFrameAtCurrentTime?.();
        } catch (e) {
            console.error('deleteClipById failed:', e);
        }
    }
    
    /**
     * Delete a visual element.
     * For regular clips: deletes the entire clip.
     * For ScriptLayerClip sources: removes just the video source (audio continues).
     */
    static deleteVisualElement(clipId: string, sourceIndex?: number): void {
        const clip: any = this.getClipById(clipId);
        if (!clip) return;
        
        if (this._isScriptLayerClipObject(clip) && sourceIndex !== undefined) {
            // ScriptLayerClip - remove just the video source
            if (typeof clip.removeVideoSource === 'function') {
                clip.removeVideoSource(sourceIndex);
                this._rebuildStageOrder();
                (this.composition as any)?.computeFrame?.();
                
                // Clear selection since the source is gone
                ApplicationStore.clearSelectedVisualElement();
            }
        } else {
            // Regular clip - delete the whole clip
            this.deleteClipById(clipId);
            ApplicationStore.clearSelectedClipId();
        }
    }

		    static moveClipTo(clipId: string, newStartSec: number) {
		        const clip: any = this.getClipById(clipId);
		        if (!clip) {
		            return newStartSec;
		        }
		        // ScriptClip is the composition spine: it is immovable and always anchored at t=0.
		        if (String(clip.type) === 'script' || this.isScriptLayerClip(clipId)) {
		            try {
		                const currentStart = clip.start?.seconds ?? 0;
		                // If something already moved it, snap back to 0 to preserve invariants.
		                if (Math.abs(currentStart) > 1e-3) {
		                    const inSec = clip.range?.[0]?.seconds ?? 0;
		                    if ('range' in clip && 'offset' in clip) {
		                        clip.offset = Timestamp.fromSeconds(-inSec);
		                    } else if (typeof clip.offsetBy === 'function') {
		                        clip.offsetBy(Timestamp.fromSeconds(-currentStart));
		                    } else if ('start' in clip) {
		                        clip.start = Timestamp.fromSeconds(0);
		                    }
		                }
		            } catch {}
		            return 0;
		        }
	        const getVisibleEndSec = (c: any): number => {
	            const s = c?.start?.seconds ?? 0;
	            const d = c?.timelineDuration?.seconds;
	            if (typeof d === 'number' && Number.isFinite(d)) return s + d;
            const stop = c?.stop?.seconds;
            if (typeof stop === 'number' && Number.isFinite(stop)) return stop;
            return s;
        };
        const index = clip.track.clips.findIndex((c: any) => c.id === clip.id);
        let previousClipEndTime = 0;
        let nextClipStartTime = Infinity;
        if (index > 0) {
            const previousClip = clip.track.clips[index - 1];
            previousClipEndTime = getVisibleEndSec(previousClip);
        }
        if (index < clip.track.clips.length - 1) {
            const nextClip = clip.track.clips[index + 1];
            nextClipStartTime = nextClip.start?.seconds ?? Infinity;
	        }
	        let clampedStart = newStartSec;
	        let shouldRebuildTiming = true;
	        try {
	            clampedStart = Math.max(clampedStart, previousClipEndTime);
	            clampedStart = Math.min(clampedStart, nextClipStartTime - (clip.timelineDuration?.seconds ?? 0) - 0.001);
	            // Enforce ScriptClip-defined composition bounds:
	            // non-script clips cannot extend beyond the script end.
	            const scriptEnd = this.getScriptEndSec();
	            if (scriptEnd != null) {
	                const dur = Number(clip.timelineDuration?.seconds ?? 0);
	                if (Number.isFinite(dur)) {
	                    const maxStart = Math.max(0, scriptEnd - dur - 1e-3);
	                    clampedStart = Math.min(clampedStart, maxStart);
	                }
	            }
	            //Media clip path: start = range[0] + offset → offset = newStart - range[0]
	            if ('range' in clip && 'offset' in clip) {
	                const inSec = clip.range?.[0]?.seconds ?? 0;
	                const newOffsetSec = clampedStart - inSec;
	                clip.offset = Timestamp.fromSeconds(newOffsetSec);
	                // MediaClip.offset setter already calls composition.onClipTimingChanged().
	                shouldRebuildTiming = false;
	            } else if ('start' in clip && 'offsetBy' in clip) {
	                // Base clip path: move by delta
	                const currentStartSec = clip.start?.seconds ?? 0;
	                const deltaSec = clampedStart - currentStartSec;
	                clip.offsetBy(Timestamp.fromSeconds(deltaSec));
	            }
	            (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, {clip});
	            (this.composition as any)?.trigger?.(EngineInternalEventsEnum.CLIP_UPDATED, {clip});
	        } catch (e) {
	            console.log('setClipStart failed:', e);
	        }
	        if (shouldRebuildTiming) {
	            (this.composition as any)?.onClipTimingChanged?.();
	        }
	        return clampedStart;
	    }

	    static trimClipLeft(clipId: string, deltaSec: number) {
	        const clip: any = this.getClipById(clipId);
	        if (!clip) return;
	        if (Math.abs(deltaSec) < 1e-6) return;

	        // ScriptClip trim is disabled for now (script clip defines the composition bounds and is always anchored at t=0).
	        if (String(clip.type) === 'script' || this.isScriptLayerClip(clipId)) {
	            return;
	        }

	        // Script clip is the composition bounds: left trim acts like trimming composition start.
	        // - Advance script clip in-point by the requested visible amount.
	        // - Shift all other clips earlier by the same amount (ripple/rebase).
        if (String(clip.type) === 'script') {
            try {
                // Extending before t=0 isn't supported (script clip defines composition start).
                if (deltaSec >= 0) return;

                const scriptClip: any = clip;
                const currentVisibleDur = scriptClip.timelineDuration?.seconds ?? scriptClip.effectiveDuration ?? 0;
                const minDur = 0.01;
                const trimIn = Math.min(Math.max(0, -deltaSec), Math.max(0, currentVisibleDur - minDur));
                if (trimIn <= 0) return;

                const baseStart = scriptClip.start?.seconds ?? 0;
                const targetCompTime = baseStart + trimIn;

                let newInSec: number | null = null;
                if (typeof scriptClip.mapCompTimeToClipLocal === 'function') {
                    newInSec = scriptClip.mapCompTimeToClipLocal(targetCompTime);
                }
                if (newInSec == null && typeof scriptClip.CompositionTimeToClipTime === 'function') {
                    newInSec = scriptClip.CompositionTimeToClipTime(targetCompTime);
                }
                if (newInSec == null) {
                    const oldIn = scriptClip.range?.[0]?.seconds ?? 0;
                    newInSec = oldIn + trimIn;
                }
	                if (!Number.isFinite(newInSec)) return;

	                // Keep the script clip anchored at t=0 by coupling offset to range[0].
	                try {
	                    (scriptClip as any)._offset.seconds = -newInSec;
	                } catch {
	                    (scriptClip as any)._offset = Timestamp.fromSeconds(-newInSec);
	                }
	                if (Array.isArray(scriptClip.range) && scriptClip.range[0]) {
	                    scriptClip.range[0].seconds = newInSec;
	                }

		                // Normalize script-specific edits to new bounds (splits/gaps/cuts that fall outside become invalid).
		                try { scriptClip.normalizeScriptCuts?.(); } catch {}
		                try { scriptClip.normalizeGaps?.(); } catch {}
		                try { scriptClip.normalizeGapInsertions?.(); } catch {}
		                try { scriptClip.normalizeSplits?.(); } catch {}
		                try { scriptClip.invalidateScenesCache?.(); } catch {}
		                try { scriptClip.deriveSegments?.(); } catch {}
		                try { scriptClip.trigger?.('frame', undefined); } catch {}

                // New composition end is the script clip end (it defines composition bounds).
                const newEndSec =
                    (scriptClip.start?.seconds ?? 0) +
                    (scriptClip.timelineDuration?.seconds ?? scriptClip.effectiveDuration ?? Math.max(0, currentVisibleDur - trimIn));

                // Shift all other clips earlier by the same amount, then clamp into [0, newEndSec].
                const otherClipIds: string[] = [];
                for (const track of (this.composition as any)?.tracks ?? []) {
                    for (const c of track?.clips ?? []) {
                        if (!c || c.id === scriptClip.id) continue;
                        otherClipIds.push(String(c.id));
                    }
                }

	                for (const id of otherClipIds) {
	                    const c: any = this.getClipById(id);
	                    if (!c) continue;
	                    try {
	                        if (c instanceof MediaClip) {
	                            // Avoid MediaClip.offset setter to prevent repeated schedule rebuilds during ripple.
	                            const offsetSec = (c as any)._offset?.seconds ?? c.offset?.seconds ?? 0;
	                            if ((c as any)._offset) {
	                                (c as any)._offset.seconds = offsetSec - trimIn;
	                            } else {
	                                (c as any)._offset = Timestamp.fromSeconds(offsetSec - trimIn);
	                            }
	                            try { c.deriveSegments?.(); } catch {}
	                            try { c.trigger?.('frame', undefined); } catch {}
	                        } else if (typeof c.offsetBy === 'function') {
	                            c.offsetBy(Timestamp.fromSeconds(-trimIn));
	                        }
	                        (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: c });
	                        (this.composition as any)?.trigger?.(EngineInternalEventsEnum.CLIP_UPDATED, { clip: c });
	                    } catch {}
	                }

                const EPS = 1e-3;
                for (const id of otherClipIds) {
                    let c: any = this.getClipById(id);
                    if (!c) continue;

                    let start = c.start?.seconds ?? 0;
                    let dur = c.timelineDuration?.seconds;
                    if (typeof dur !== 'number' || !Number.isFinite(dur)) {
                        const stop = c.stop?.seconds ?? start;
                        dur = Math.max(0, stop - start);
                    }
                    let end = start + dur;

                    // Drop fully out-of-bounds clips.
                    if (end <= 0 + EPS || start >= newEndSec - EPS) {
                        this.deleteClipById(id);
                        continue;
                    }

                    // Clamp start to 0 by trimming away the portion that would be before 0.
                    if (start < 0) {
                        this.trimClipLeft(id, start);
                        c = this.getClipById(id);
                        if (!c) continue;
                        start = c.start?.seconds ?? 0;
                        dur = c.timelineDuration?.seconds ?? dur;
                        end = start + (dur ?? 0);
                        if (end <= 0 + EPS) {
                            this.deleteClipById(id);
                            continue;
                        }
                    }

                    // Clamp end to composition end.
                    if (end > newEndSec + EPS) {
                        const desiredDur = Math.max(0, newEndSec - start);
                        const delta = desiredDur - (dur ?? 0);
                        this.trimClipRight(id, delta);
                        c = this.getClipById(id);
                        if (!c) continue;
                        start = c.start?.seconds ?? start;
                        dur = c.timelineDuration?.seconds ?? dur;
                        end = start + (dur ?? 0);
                        if (dur != null && dur <= minDur) {
                            this.deleteClipById(id);
                        }
                    }
                }

                // Notify script-layer listeners (transcript, waveform, persistence).
	                try {
	                    this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
	                        clipId: scriptClip.id,
	                        cuts: scriptClip.scriptCuts,
	                        gaps: scriptClip.gaps,
	                        gapInsertions: scriptClip.gapInsertions,
	                        segments: scriptClip.scriptSegments,
	                        splits: scriptClip.splits,
	                        duration: scriptClip.duration?.seconds ?? 0,
	                        hasVideo: (scriptClip.getVideoSources?.()?.length ?? 0) > 0,
                    });
                } catch {}

                try {
                    this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                        script: {
                            layerId: (this.composition as any)?.scriptLayer?.layerId ?? scriptClip.track?.id ?? null,
                            clipId: scriptClip.id,
                            cutsChanged: true,
                            gapsChanged: true,
                            splitsChanged: true,
                        },
                    });
                } catch {}

                try {
                    (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: scriptClip });
                } catch {}

                this.composition?.onClipTimingChanged?.();
                return;
            } catch (e) {
                console.log('trimClipLeft (script) failed:', e);
                return;
            }
        }

        // Non-script media clips: trim against visible (cut-collapsed) duration, not clip.stop.
        if (clip instanceof MediaClip && String(clip.type) !== 'script') {
            try {
                const rate = Number(clip.playbackRate ?? 1) || 1;
                const currentStart = clip.start?.seconds ?? 0;
                const currentVisibleSrcDur = Array.isArray(clip.segments) && clip.segments.length
                    ? clip.segments.reduce((sum: number, seg: any) => sum + Math.max(0, (seg.srcEndSec ?? 0) - (seg.srcStartSec ?? 0)), 0)
                    : Math.max(0, (clip.range?.[1]?.seconds ?? 0) - (clip.range?.[0]?.seconds ?? 0));

                const currentTimelineDur = currentVisibleSrcDur / rate;
                const minTimelineDur = 0.01;
                const minSrcDur = minTimelineDur * rate;

                const currentInSec = clip.range?.[0]?.seconds ?? 0;

                // Clamp extension so we don't move the edge past available source.
                let effectiveDeltaSec = deltaSec;
                if (deltaSec > 0) {
                    const maxExtend = currentInSec / rate;
                    effectiveDeltaSec = Math.min(deltaSec, maxExtend);
                }

                const desiredStart = Math.max(0, currentStart - effectiveDeltaSec);
                const desiredVisibleSrcDur = Math.max(minSrcDur, currentVisibleSrcDur + effectiveDeltaSec * rate);

                const sourceTimeAtVisibleOffset = (visibleOffsetSrcSec: number): number => {
                    let remaining = Math.max(0, visibleOffsetSrcSec);
                    const segs = Array.isArray(clip.segments) ? clip.segments : [];
                    for (const seg of segs) {
                        const s = Number(seg?.srcStartSec ?? 0);
                        const e = Number(seg?.srcEndSec ?? s);
                        const dur = Math.max(0, e - s);
                        if (remaining <= dur + 1e-6) return s + remaining;
                        remaining -= dur;
                    }
                    // Fallback: end of outer range
                    return Number(clip.range?.[1]?.seconds ?? currentInSec);
                };

                let newInSec = currentInSec;
                if (desiredVisibleSrcDur >= currentVisibleSrcDur) {
                    const extendSrc = desiredVisibleSrcDur - currentVisibleSrcDur;
                    newInSec = Math.max(0, currentInSec - extendSrc);
                } else {
                    const removeSrc = Math.min(currentVisibleSrcDur - minSrcDur, currentVisibleSrcDur - desiredVisibleSrcDur);
                    newInSec = sourceTimeAtVisibleOffset(removeSrc);
	                }
	
	                const desiredOffset = desiredStart - newInSec;
	                // Apply without invoking setters (they each call composition.onClipTimingChanged).
	                try {
	                    (clip as any)._offset.seconds = desiredOffset;
	                } catch {
	                    (clip as any)._offset = Timestamp.fromSeconds(desiredOffset);
	                }
	                if (Array.isArray(clip.range) && clip.range[0]) {
	                    clip.range[0].seconds = newInSec;
	                }
	                try { clip.normalizeCuts?.(); } catch {}
	                try { clip.deriveSegments?.(); } catch {}
	                try { clip.trigger?.('frame', undefined); } catch {}

	                this.notifyClipUpdated(clip, { timingChanged: true });
	                return;
	            } catch (e) {
	                console.log('trimClipLeft (media) failed:', e);
	                return;
            }
        }

        if (!('start' in clip)) return;
	        try {
	            const currentStart = clip.start?.seconds ?? 0;
	            // UI sends negative delta for trim-in on LEFT; invert so trim-in moves start right by |delta|
	            const desiredStart = currentStart - deltaSec;
	            // Let MediaClip.start setter handle clamping internally
	            clip.start = Timestamp.fromSeconds(desiredStart);
	            this.notifyClipUpdated(clip, { timingChanged: true });
	        } catch (e) {
	            console.log('trimClipLeft failed:', e);
	        }
    }

    static trimClipRight(clipId: string, deltaSec: number) {
	        const clip: any = this.getClipById(clipId);
	        if (!clip) return;
	        if (Math.abs(deltaSec) < 1e-6) return;

	        // ScriptClip trim is disabled for now (script clip defines the composition bounds and is always anchored at t=0).
	        if (String(clip.type) === 'script' || this.isScriptLayerClip(clipId)) {
	            return;
	        }

	        // Enforce ScriptClip-defined composition bounds:
	        // non-script clips cannot extend beyond the script end.
	        const scriptEnd = this.getScriptEndSec();
	        if (scriptEnd != null && Number.isFinite(scriptEnd)) {
	            try {
	                const start = Number(clip.start?.seconds ?? 0);
	                const dur = Number(clip.timelineDuration?.seconds ?? 0);
	                if (Number.isFinite(start) && Number.isFinite(dur)) {
	                    const end = start + dur;
	                    const EPS = 1e-3;
	                    if (start >= scriptEnd - EPS) {
	                        this.deleteClipById(clipId);
	                        return;
	                    }
	                    const desiredEnd = end + deltaSec;
	                    if (desiredEnd > scriptEnd + EPS) {
	                        deltaSec = scriptEnd - end;
	                    }
	                }
	            } catch {}
	        }

	        // Script clip is the composition bounds: right trim changes composition end.
	        // Other clips are clamped to the new end (no ripple shift).
	        if (String(clip.type) === 'script') {
            try {
                const scriptClip: any = clip;
                const currentVisibleDur = scriptClip.timelineDuration?.seconds ?? scriptClip.effectiveDuration ?? 0;
                const minDur = 0.01;

                // Clamp extension to available source duration.
                let effectiveDelta = deltaSec;
                if (deltaSec > 0) {
                    const currentOut = scriptClip.range?.[1]?.seconds ?? 0;
                    const sourceDur = scriptClip.duration?.seconds ?? currentOut;
                    const maxExtend = Math.max(0, sourceDur - currentOut);
                    effectiveDelta = Math.min(deltaSec, maxExtend);
                }

                const desiredVisibleDur = Math.max(minDur, currentVisibleDur + effectiveDelta);
                const baseStart = scriptClip.start?.seconds ?? 0;

                let newOutSec: number | null = null;
                if (desiredVisibleDur <= currentVisibleDur + 1e-6) {
                    const targetCompTime = baseStart + desiredVisibleDur;
                    if (typeof scriptClip.mapCompTimeToClipLocal === 'function') {
                        newOutSec = scriptClip.mapCompTimeToClipLocal(targetCompTime);
                    }
                    if (newOutSec == null && typeof scriptClip.CompositionTimeToClipTime === 'function') {
                        newOutSec = scriptClip.CompositionTimeToClipTime(targetCompTime);
                    }
                } else {
                    const currentOut = scriptClip.range?.[1]?.seconds ?? 0;
                    newOutSec = currentOut + (desiredVisibleDur - currentVisibleDur);
                }
	                if (newOutSec == null || !Number.isFinite(newOutSec)) return;

	                // Apply out trim (keep start anchored at 0 via existing offset/range[0] coupling).
	                if (Array.isArray(scriptClip.range) && scriptClip.range[1]) {
	                    scriptClip.range[1].seconds = newOutSec;
	                }

		                // Normalize script-specific edits and recompute segments.
		                try { scriptClip.normalizeScriptCuts?.(); } catch {}
		                try { scriptClip.normalizeGaps?.(); } catch {}
		                try { scriptClip.normalizeGapInsertions?.(); } catch {}
		                try { scriptClip.normalizeSplits?.(); } catch {}
		                try { scriptClip.invalidateScenesCache?.(); } catch {}
		                try { scriptClip.deriveSegments?.(); } catch {}
		                try { scriptClip.trigger?.('frame', undefined); } catch {}

                const newEndSec =
                    (scriptClip.start?.seconds ?? 0) +
                    (scriptClip.timelineDuration?.seconds ?? scriptClip.effectiveDuration ?? desiredVisibleDur);

                const otherClipIds: string[] = [];
                for (const track of (this.composition as any)?.tracks ?? []) {
                    for (const c of track?.clips ?? []) {
                        if (!c || c.id === scriptClip.id) continue;
                        otherClipIds.push(String(c.id));
                    }
                }

                const EPS = 1e-3;
                for (const id of otherClipIds) {
                    let c: any = this.getClipById(id);
                    if (!c) continue;

                    let start = c.start?.seconds ?? 0;
                    let dur = c.timelineDuration?.seconds;
                    if (typeof dur !== 'number' || !Number.isFinite(dur)) {
                        const stop = c.stop?.seconds ?? start;
                        dur = Math.max(0, stop - start);
                    }
                    let end = start + dur;

                    if (end <= 0 + EPS || start >= newEndSec - EPS) {
                        this.deleteClipById(id);
                        continue;
                    }

                    if (start < 0) {
                        this.trimClipLeft(id, start);
                        c = this.getClipById(id);
                        if (!c) continue;
                        start = c.start?.seconds ?? 0;
                        dur = c.timelineDuration?.seconds ?? dur;
                        end = start + (dur ?? 0);
                        if (end <= 0 + EPS) {
                            this.deleteClipById(id);
                            continue;
                        }
                    }

                    if (end > newEndSec + EPS) {
                        const desiredDur = Math.max(0, newEndSec - start);
                        const delta = desiredDur - (dur ?? 0);
                        this.trimClipRight(id, delta);
                        c = this.getClipById(id);
                        if (!c) continue;
                        dur = c.timelineDuration?.seconds ?? dur;
                        if (dur != null && dur <= minDur) {
                            this.deleteClipById(id);
                        }
                    }
                }

                // Notify script-layer listeners (transcript, waveform, persistence).
	                try {
	                    this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
	                        clipId: scriptClip.id,
	                        cuts: scriptClip.scriptCuts,
	                        gaps: scriptClip.gaps,
	                        gapInsertions: scriptClip.gapInsertions,
	                        segments: scriptClip.scriptSegments,
	                        splits: scriptClip.splits,
	                        duration: scriptClip.duration?.seconds ?? 0,
	                        hasVideo: (scriptClip.getVideoSources?.()?.length ?? 0) > 0,
                    });
                } catch {}

                try {
                    this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                        script: {
                            layerId: (this.composition as any)?.scriptLayer?.layerId ?? scriptClip.track?.id ?? null,
                            clipId: scriptClip.id,
                            cutsChanged: true,
                            gapsChanged: true,
                            splitsChanged: true,
                        },
                    });
                } catch {}

                try {
                    (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: scriptClip });
                } catch {}

                this.composition?.onClipTimingChanged?.();
                return;
            } catch (e) {
                console.log('trimClipRight (script) failed:', e);
                return;
            }
        }

        // Non-script media clips: trim against visible (cut-collapsed) duration, not clip.stop.
        if (clip instanceof MediaClip && String(clip.type) !== 'script') {
            try {
                const rate = Number(clip.playbackRate ?? 1) || 1;
                const currentVisibleSrcDur = Array.isArray(clip.segments) && clip.segments.length
                    ? clip.segments.reduce((sum: number, seg: any) => sum + Math.max(0, (seg.srcEndSec ?? 0) - (seg.srcStartSec ?? 0)), 0)
                    : Math.max(0, (clip.range?.[1]?.seconds ?? 0) - (clip.range?.[0]?.seconds ?? 0));

                const minTimelineDur = 0.01;
                const minSrcDur = minTimelineDur * rate;

                const currentOutSec = clip.range?.[1]?.seconds ?? 0;
                const sourceDurSec = clip.duration?.seconds ?? currentOutSec;

                let effectiveDeltaSec = deltaSec;
                if (deltaSec > 0) {
                    const maxExtend = Math.max(0, sourceDurSec - currentOutSec) / rate;
                    effectiveDeltaSec = Math.min(deltaSec, maxExtend);
                }

                const desiredVisibleSrcDur = Math.max(minSrcDur, currentVisibleSrcDur + effectiveDeltaSec * rate);

                const sourceTimeAtVisibleOffset = (visibleOffsetSrcSec: number): number => {
                    let remaining = Math.max(0, visibleOffsetSrcSec);
                    const segs = Array.isArray(clip.segments) ? clip.segments : [];
                    for (const seg of segs) {
                        const s = Number(seg?.srcStartSec ?? 0);
                        const e = Number(seg?.srcEndSec ?? s);
                        const dur = Math.max(0, e - s);
                        if (remaining <= dur + 1e-6) return s + remaining;
                        remaining -= dur;
                    }
                    return Number(clip.range?.[1]?.seconds ?? 0);
                };

                let newOutSec = currentOutSec;
                if (desiredVisibleSrcDur > currentVisibleSrcDur) {
                    const extendSrc = desiredVisibleSrcDur - currentVisibleSrcDur;
                    newOutSec = Math.min(sourceDurSec, currentOutSec + extendSrc);
	                } else {
	                    newOutSec = sourceTimeAtVisibleOffset(desiredVisibleSrcDur);
	                }
	
	                if (Array.isArray(clip.range) && clip.range[1]) {
	                    clip.range[1].seconds = newOutSec;
	                }
	                try { clip.normalizeCuts?.(); } catch {}
	                try { clip.deriveSegments?.(); } catch {}
	                try { clip.trigger?.('frame', undefined); } catch {}

	                this.notifyClipUpdated(clip, { timingChanged: true });
	                return;
	            } catch (e) {
	                console.log('trimClipRight (media) failed:', e);
	                return;
            }
        }

        if (!('stop' in clip)) return;
	        try {
	            const currentStop = clip.stop?.seconds ?? 0;
	            const desiredStop = currentStop + deltaSec;
	            // Let MediaClip.stop setter handle clamping internally
	            clip.stop = Timestamp.fromSeconds(desiredStop);
	            this.notifyClipUpdated(clip, { timingChanged: true });
	        } catch (e) {
	            console.log('trimClipRight failed:', e);
	        }
    }

    /**
     * Trim the current composition down to a contiguous time range [startSec, endSec] (composition timeline seconds).
     *
     * Intended for "Duplicate selection to new composition" clip creation:
     * - The ScriptLayerClip is the spine and defines composition bounds.
     * - We trim the spine to the requested window and ripple-shift/clamp all other clips to fit.
     * - Boundaries may fall inside inserted-silence holes (gapInsertions). In that case we preserve the
     *   partial leading/trailing silence by shortening the relevant insertion(s) or appending an empty scene.
     */
    static trimCompositionToRange(startSec: number, endSec: number): boolean {
        const scriptClip: any = this.getScriptLayerClip();
        if (!scriptClip || String(scriptClip.type) !== "script") return false;

        const EPS = 1e-3;

        const scriptEnd = this.getScriptEndSec();
        if (scriptEnd == null || !Number.isFinite(scriptEnd) || scriptEnd <= 0) return false;

        const rawStart = Number(startSec ?? 0);
        const rawEnd = Number(endSec ?? 0);
        if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd)) return false;

        const start = Math.max(0, Math.min(scriptEnd, Math.min(rawStart, rawEnd)));
        const end = Math.max(start, Math.min(scriptEnd, Math.max(rawStart, rawEnd)));
        if (end - start <= EPS) return false;

        const otherClipIds: string[] = [];
        try {
            for (const track of (this.composition as any)?.tracks ?? []) {
                for (const c of track?.clips ?? []) {
                    if (!c) continue;
                    if (String(c.type) === "script" || String(c.id) === String(scriptClip.id)) continue;
                    otherClipIds.push(String(c.id));
                }
            }
        } catch {}

        // 1) Ripple-shift all non-script clips earlier by `start` (rebase so new t=0 == original start).
        try {
            for (const id of otherClipIds) {
                const c: any = this.getClipById(id);
                if (!c) continue;
                if (String(c.type) === "script" || this.isScriptLayerClip(id)) continue;

                if (c instanceof MediaClip) {
                    const offsetSec = Number((c as any)._offset?.seconds ?? (c as any).offset?.seconds ?? 0);
                    const next = offsetSec - start;
                    try {
                        (c as any)._offset.seconds = next;
                    } catch {
                        (c as any)._offset = Timestamp.fromSeconds(next);
                    }
                    try { c.deriveSegments?.(); } catch {}
                    try { c.trigger?.("frame", undefined); } catch {}
                } else if (typeof c.offsetBy === "function") {
                    try {
                        c.offsetBy(Timestamp.fromSeconds(-start));
                    } catch {}
                }

                try {
                    (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: c });
                    (this.composition as any)?.trigger?.(EngineInternalEventsEnum.CLIP_UPDATED, { clip: c });
                } catch {}
            }
        } catch {}

        // 2) Compute script-clip-local trim boundaries from the current segment mapping.
        const segs: any[] = Array.isArray(scriptClip.scriptSegments) ? scriptClip.scriptSegments : [];
        const clipInSec = Number(scriptClip.range?.[0]?.seconds ?? 0);
        const clipOutSec = Number(scriptClip.range?.[1]?.seconds ?? scriptClip.duration?.seconds ?? clipInSec);

        const getSegEnd = (seg: any): number => {
            const s0 = Number(seg?.srcStartSec ?? NaN);
            const s1 = Number(seg?.srcEndSec ?? NaN);
            const c0 = Number(seg?.compStartSec ?? NaN);
            if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) return NaN;
            return c0 + Math.max(0, s1 - s0);
        };

        const sortSegs = () => {
            try {
                segs.sort((a, b) => Number(a?.compStartSec ?? 0) - Number(b?.compStartSec ?? 0));
            } catch {}
        };
        sortSegs();

        const trailingInsertionDurationSec = (): number => {
            const out = Number(clipOutSec);
            if (!Number.isFinite(out)) return 0;
            let total = 0;
            for (const ins of (scriptClip.gapInsertions ?? []) as any[]) {
                const at = Number(ins?.atSec ?? NaN);
                const dur = Number(ins?.durationSec ?? 0);
                if (!Number.isFinite(at) || !Number.isFinite(dur) || dur <= EPS) continue;
                if (Math.abs(at - out) <= EPS) total += dur;
            }
            return total;
        };

        type Located =
            | { kind: "segment"; localSec: number }
            | { kind: "hole"; atSec: number; holeStartCompSec: number; holeEndCompSec: number };

        const locate = (tCompSec: number): Located => {
            const t = Number(tCompSec);
            if (!Number.isFinite(t) || segs.length === 0) {
                return { kind: "segment", localSec: Math.max(clipInSec, Math.min(clipOutSec, t)) };
            }

            // Leading hole before first segment.
            const first = segs[0];
            const firstStart = Number(first?.compStartSec ?? NaN);
            const firstLocal = Number(first?.srcStartSec ?? clipInSec);
            if (Number.isFinite(firstStart) && t < firstStart - EPS) {
                return {
                    kind: "hole",
                    atSec: firstLocal,
                    holeStartCompSec: Number(scriptClip.start?.seconds ?? 0),
                    holeEndCompSec: firstStart,
                };
            }

            for (let i = 0; i < segs.length; i++) {
                const seg = segs[i];
                const s0 = Number(seg?.srcStartSec ?? NaN);
                const s1 = Number(seg?.srcEndSec ?? NaN);
                const c0 = Number(seg?.compStartSec ?? NaN);
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
                const segEnd = c0 + Math.max(0, s1 - s0);
                if (t >= c0 - EPS && t < segEnd - EPS) {
                    return { kind: "segment", localSec: s0 + (t - c0) };
                }

                const next = segs[i + 1];
                if (next) {
                    const nextStart = Number(next?.compStartSec ?? NaN);
                    if (!Number.isFinite(nextStart)) continue;
                    if (t >= segEnd - EPS && t < nextStart - EPS) {
                        const atSec = Number(next?.srcStartSec ?? s1);
                        return { kind: "hole", atSec, holeStartCompSec: segEnd, holeEndCompSec: nextStart };
                    }
                }
            }

            // Trailing hole (e.g., trailing gap insertion at clip out).
            const last = segs[segs.length - 1];
            const lastEnd = getSegEnd(last);
            const trailing = trailingInsertionDurationSec();
            if (Number.isFinite(lastEnd) && trailing > EPS && t >= lastEnd - EPS && t < lastEnd + trailing + EPS) {
                return {
                    kind: "hole",
                    atSec: clipOutSec,
                    holeStartCompSec: lastEnd,
                    holeEndCompSec: lastEnd + trailing,
                };
            }

            // Past all segments: clamp to end.
            return { kind: "segment", localSec: clipOutSec };
        };

        const startLoc = locate(start);
        const endLoc = locate(end);

        const sliceStartLocal = Math.max(clipInSec, Math.min(clipOutSec, startLoc.kind === "segment" ? startLoc.localSec : startLoc.atSec));
        const sliceEndLocal = Math.max(sliceStartLocal, Math.min(clipOutSec, endLoc.kind === "segment" ? endLoc.localSec : endLoc.atSec));

        const leadingHoleRemainingSec =
            startLoc.kind === "hole" ? Math.max(0, Number(startLoc.holeEndCompSec ?? 0) - start) : 0;
        const trailingHolePrefixSec =
            endLoc.kind === "hole" ? Math.max(0, end - Number(endLoc.holeStartCompSec ?? 0)) : 0;

        // 3) Prune/adjust gapInsertions so insertions before the slice don't shift our new start.
        try {
            const nextInsertions: any[] = [];
            for (const ins of (scriptClip.gapInsertions ?? []) as any[]) {
                const at = Number(ins?.atSec ?? NaN);
                const dur = Number(ins?.durationSec ?? 0);
                if (!Number.isFinite(at) || !Number.isFinite(dur) || dur <= EPS) continue;
                if (at < sliceStartLocal - EPS) continue;
                if (at > sliceEndLocal + EPS) continue;
                nextInsertions.push({ ...ins, atSec: at, durationSec: dur });
            }
            scriptClip.gapInsertions = nextInsertions;
        } catch {}

        // If the slice starts inside an insertion hole, shorten the insertion at that boundary to preserve tail silence.
        if (leadingHoleRemainingSec > EPS) {
            try {
                const at = sliceStartLocal;
                const existing = (scriptClip.gapInsertions ?? []).find((g: any) => Math.abs(Number(g?.atSec ?? NaN) - at) <= EPS);
                if (existing) {
                    existing.durationSec = leadingHoleRemainingSec;
                } else {
                    (scriptClip.gapInsertions ?? []).push({
                        id: crypto?.randomUUID?.() ?? `gapins_${Date.now()}_${Math.random()}`,
                        atSec: at,
                        durationSec: leadingHoleRemainingSec,
                    });
                }
            } catch {}
        }

        // If the slice ends inside a trailing insertion hole (at clipOutSec), shorten it to the prefix we keep.
        const isTrailingInsertionHole = endLoc.kind === "hole" && Math.abs(Number((endLoc as any).atSec ?? NaN) - clipOutSec) <= EPS;
        if (isTrailingInsertionHole && trailingHolePrefixSec > EPS) {
            try {
                const at = clipOutSec;
                const existing = (scriptClip.gapInsertions ?? []).find((g: any) => Math.abs(Number(g?.atSec ?? NaN) - at) <= EPS);
                if (existing) {
                    existing.durationSec = trailingHolePrefixSec;
                } else {
                    (scriptClip.gapInsertions ?? []).push({
                        id: crypto?.randomUUID?.() ?? `gapins_${Date.now()}_${Math.random()}`,
                        atSec: at,
                        durationSec: trailingHolePrefixSec,
                    });
                }
            } catch {}
        }

        // Normalize gap insertions after edits (private method on class; call via any).
        try { (scriptClip as any).normalizeGapInsertions?.(); } catch {}

        // 4) Apply prefix/suffix cuts on the script spine.
        if (sliceStartLocal > clipInSec + EPS) {
            this.cutScriptLayerRange(clipInSec, sliceStartLocal, { transcriptMode: "delete" });
        }
        if (sliceEndLocal < clipOutSec - EPS) {
            this.cutScriptLayerRange(sliceEndLocal, clipOutSec, { transcriptMode: "delete" });
        }

        // 5) If the slice ends inside an internal insertion hole (not trailing-out), append an empty scene
        // to preserve that trailing silence portion.
        if (endLoc.kind === "hole" && !isTrailingInsertionHole && trailingHolePrefixSec > EPS) {
            try {
                // Ensure any residual downstream insertions don't shift the appended silence.
                scriptClip.gapInsertions = (scriptClip.gapInsertions ?? []).filter(
                    (g: any) => Number(g?.atSec ?? 0) <= sliceEndLocal + EPS
                );
                try { (scriptClip as any).normalizeGapInsertions?.(); } catch {}

                // Insert silent time at the (trimmed) end. This is editor-only; we don't ripple other clips.
                scriptClip.insertEmptyScene?.(sliceEndLocal, trailingHolePrefixSec);
            } catch {}
        }

        // Recompute segments/scenes after edits.
        try { scriptClip.normalizeScriptCuts?.(); } catch {}
        try { scriptClip.normalizeGaps?.(); } catch {}
        try { (scriptClip as any).normalizeGapInsertions?.(); } catch {}
        try { scriptClip.normalizeSplits?.(); } catch {}
        try { scriptClip.invalidateScenesCache?.(); } catch {}
        try { scriptClip.deriveSegments?.(); } catch {}
        try { scriptClip.trigger?.("frame", undefined); } catch {}

        // 6) Clamp/delete non-script clips to the new authoritative script end.
        const newScriptEnd = this.getScriptEndSec();
        const newEndSec = newScriptEnd != null && Number.isFinite(newScriptEnd) ? newScriptEnd : end - start;

        const minDur = 0.01;
        for (const id of otherClipIds) {
            let c: any = this.getClipById(id);
            if (!c) continue;

            let clipStart = Number(c.start?.seconds ?? 0);
            let dur = Number(c.timelineDuration?.seconds ?? NaN);
            if (!Number.isFinite(dur)) {
                const stop = Number(c.stop?.seconds ?? clipStart);
                dur = Math.max(0, stop - clipStart);
            }
            let clipEnd = clipStart + dur;

            if (clipEnd <= 0 + EPS || clipStart >= newEndSec - EPS) {
                this.deleteClipById(id);
                continue;
            }

            if (clipStart < 0) {
                this.trimClipLeft(id, clipStart);
                c = this.getClipById(id);
                if (!c) continue;
                clipStart = Number(c.start?.seconds ?? 0);
                dur = Number(c.timelineDuration?.seconds ?? dur);
                clipEnd = clipStart + dur;
                if (clipEnd <= 0 + EPS) {
                    this.deleteClipById(id);
                    continue;
                }
            }

            if (clipEnd > newEndSec + EPS) {
                const desiredDur = Math.max(0, newEndSec - clipStart);
                const delta = desiredDur - dur;
                this.trimClipRight(id, delta);
                c = this.getClipById(id);
                if (!c) continue;
                dur = Number(c.timelineDuration?.seconds ?? dur);
                if (Number.isFinite(dur) && dur <= minDur) {
                    this.deleteClipById(id);
                }
            }
        }

        // Notify/persist the updated script clip (and ensure the schedule matches the new bounds).
        try {
            this.notifyClipUpdated(scriptClip, { timingChanged: true });
        } catch {}

        try {
            this.composition.onClipTimingChanged();
        } catch {}

        return true;
    }

    static trimClip(clipId: string, trimStartSec: number, trimEndSec: number) {
        const clip: any = this.getClipById(clipId);
        if (!clip) return;

        // Translate visible selection → source-time cuts via current segments
        if (clip instanceof MediaClip) {
            const {srcStartSec, srcEndSec} = clip.VisibleCutToSourceCuts(trimStartSec, trimEndSec);
            clip.addCut({startSec: srcStartSec, endSec: srcEndSec})
        }

        // Rebuild schedule and notify UI
        this.composition?.onClipTimingChanged?.();
        (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, {clip});
    }

    /**
     * Replace a range on a non-script MediaClip with a "gap" on the timeline (no ripple).
     *
     * Semantics:
     * - Unlike trimClip() (which collapses time within the clip), this leaves an empty hole on the track.
     * - Implemented by splitting around the range and deleting the middle segment.
     *
     * @param clipId - Target non-script clip id
     * @param absStartSec - Absolute composition time (seconds)
     * @param absEndSec - Absolute composition time (seconds)
     */
    static async replaceMediaRangeWithGap(clipId: string, absStartSec: number, absEndSec: number): Promise<void> {
        const clip: any = this.getClipById(clipId);
        if (!clip || !(clip instanceof MediaClip) || !clip.track) return;
        if (String(clip.type) === 'script' || this.isScriptLayerClip(String(clipId))) return;

        const EPS = 1e-3;
        const startAbsRaw = Number(absStartSec);
        const endAbsRaw = Number(absEndSec);
        if (!Number.isFinite(startAbsRaw) || !Number.isFinite(endAbsRaw)) return;
        const startAbs = Math.min(startAbsRaw, endAbsRaw);
        const endAbs = Math.max(startAbsRaw, endAbsRaw);

        const startTime = Number(CompositionStateStore.clips?.[clipId]?.startTime ?? clip.start?.seconds ?? 0);
        const dur =
            Number(CompositionStateStore.clips?.[clipId]?.trimmedDuration ?? clip.timelineDuration?.seconds ?? 0) || 0;
        const endTime = startTime + Math.max(0, dur);

        const clampedStart = Math.max(startTime, Math.min(endTime, startAbs));
        const clampedEnd = Math.max(startTime, Math.min(endTime, endAbs));
        if (clampedEnd - clampedStart <= EPS) return;

        // Full-span selection → delete clip.
        if (clampedStart <= startTime + EPS && clampedEnd >= endTime - EPS) {
            this.deleteClipById(clipId);
            ApplicationStore.clearSelectedClipId();
            return;
        }

        // Split at start boundary (if needed) so the selected range starts at a clip boundary.
        let middleClipId = clipId;
        if (clampedStart > startTime + EPS && clampedStart < endTime - EPS) {
            ApplicationStore.setSelectedClipId(clipId);
            await this.splitClip(clampedStart);
            // splitClip selects the new right-side clip.
            if (ApplicationStore.selectedClipId) {
                middleClipId = ApplicationStore.selectedClipId;
            }
        } else {
            ApplicationStore.setSelectedClipId(clipId);
        }

        // Split at end boundary (if needed) so the selected range ends at a clip boundary.
        const middleClip: any = this.getClipById(middleClipId);
        const middleStart = Number(CompositionStateStore.clips?.[middleClipId]?.startTime ?? middleClip?.start?.seconds ?? clampedStart);
        const middleDur =
            Number(CompositionStateStore.clips?.[middleClipId]?.trimmedDuration ?? middleClip?.timelineDuration?.seconds ?? 0) || 0;
        const middleEnd = middleStart + Math.max(0, middleDur);

        if (clampedEnd < middleEnd - EPS) {
            ApplicationStore.setSelectedClipId(middleClipId);
            await this.splitClip(clampedEnd);
        }

        // Delete the middle segment, leaving a hole on the track.
        this.deleteClipById(middleClipId);
        ApplicationStore.setSelectedRange(null);
    }

    // For splitting ordinary (layer) AUDIO, VIDEO, and IMAGE clips.
    static async splitClip(splitTimeSec?: number) {
        const clipId = ApplicationStore.selectedClipId;
        if (!clipId) return;

        const splitTimeRaw =
            typeof splitTimeSec === 'number' && Number.isFinite(splitTimeSec)
                ? splitTimeSec
                : CompositionStateStore.currentTime;
        const splitTime = typeof splitTimeRaw === 'number' && Number.isFinite(splitTimeRaw) ? splitTimeRaw : undefined;
        if (splitTime === undefined) return;

        const clip = this.getClipById(clipId);
        if (!clip || !clip.track) return;

        // Use clone() to create the right clip - this copies offset, range, etc.
        let rightClip: VideoClip | AudioClip | ImageClip | null = null;
        if (clip.type === 'video' && clip instanceof VideoClip) {
            rightClip = clip.clone();
        } else if (clip.type === 'audio' && clip instanceof AudioClip) {
            rightClip = clip.clone();
        } else if (clip.type === 'image' && clip instanceof ImageClip) {
            rightClip = clip.clone();
        }
        if (!rightClip) return;

        // Copy state store info
        CompositionStateStore.setAssetIdForClip(rightClip.id, CompositionStateStore.clipIdToAssetId[clip.id]);
        CompositionStateStore.setTitleForClip(rightClip.id, CompositionStateStore.clipIdToTitle[clip.id]);
        if (clip.type === 'image') {
            CompositionStateStore.setThumbnailIdForClip(rightClip.id, CompositionStateStore.clipIdToThumbnailId[clip.id]);
        }

        // Convert split time to source time
        const splitTimeInSource = clip.CompositionTimeToClipTime(splitTime);

        // Divide cuts between left and right clips
        const leftCuts: Array<{ id: string; startSec: number; endSec: number; groupId?: string }> = [];
        const rightCuts: Array<{ id: string; startSec: number; endSec: number; groupId?: string }> = [];

        if (clip instanceof MediaClip && Array.isArray(clip.cuts)) {
            for (const c of clip.cuts) {
                if (c.endSec <= splitTimeInSource) {
                    leftCuts.push(c);
                } else if (c.startSec >= splitTimeInSource) {
                    rightCuts.push({ ...c, id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}` });
                } else {
                    // Cut spans the split time, need to split it
                    leftCuts.push({ id: c.id, startSec: c.startSec, endSec: splitTimeInSource, groupId: c.groupId });
                    rightCuts.push({ id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}`, startSec: splitTimeInSource, endSec: c.endSec, groupId: c.groupId });
                }
            }
            clip.cuts = leftCuts;
            rightClip.cuts = rightCuts;
        }

        // Adjust ranges:
        // Left clip: keep original in, set out to split point
        // Right clip: set in to split point, keep original out
        const originalOutSec = (clip as MediaClip).range[1].seconds;
        
        // Left clip ends at the split point
        (clip as MediaClip).range[1] = Timestamp.fromSeconds(splitTimeInSource);
        
        // Right clip starts at split point, ends at original out
        rightClip.range = [
            Timestamp.fromSeconds(splitTimeInSource),
            Timestamp.fromSeconds(originalOutSec)
        ];

        // Normalize and derive segments
        (clip as MediaClip).normalizeCuts();
        (clip as MediaClip).deriveSegments();
        rightClip.normalizeCuts();
        rightClip.deriveSegments();

        // Insert right clip after left
        const index = clip.track.clips.findIndex((c: any) => c.id === clip.id);
        await clip.track.add(rightClip, index + 1);
        this.composition?.onClipTimingChanged?.();

        (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip });
        // For the new clip, the adding to the track triggers a clip added event.

        ApplicationStore.setSelectedClipId(rightClip.id);
    }

    static undoTrim(clipId: string, trimIndex: number) {
        const clip: any = this.getClipById(clipId);
        if (!clip) return;
        clip.removeCutAt(trimIndex);
        this.composition?.onClipTimingChanged?.();
        (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, {clip});
    }

    static undoTrimByGroup(clipId: string, groupId: string) {
        const clip: any = this.getClipById(clipId);
        if (!clip || !groupId) return;
        try {
            clip.removeCutsByGroup?.(groupId);
            this.composition?.onClipTimingChanged?.();
            (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, {clip});
        } catch (e) {
            console.log('undoTrimByGroup failed:', e);
        }
    }

    static deleteTransitionById(transitionId: string, layerId: string) {
        // TODO: Implement when needed
    }

    static deleteLibrettoClipAtCurrentTime() {
        // TODO: Implement when needed
    }

    static splitLibrettoClipAtCurrentTime = async () => {
        // TODO: Implement when needed
    }

    static async duplicateLibrettoClipById(librettoClipId: string) {
        // TODO: Implement when needed
    }

    static async duplicateClipById(clipId: string) {
        // TODO: Implement when needed
    }

    static moveClipLeft = () => {
        // TODO: Implement when needed
    };

    static export() {
        // TODO: Implement when needed
    }

    static async importSubtitlesSrt(srtContent: string) {
        // TODO: Implement when needed
    }

    static async addManualSubtitles(textBlocks) {
        // TODO: Implement when needed
    }

    static async addSubtitles(subtitles) {
        // TODO: Implement when needed
    }

    static async createLottieClip(options: {
        dataUrl: string;
        propertiesUrl: string;
        startTime: number;
        layerId?: string;
    }) {
        // TODO: Implement when needed
    }

    static async createTextClip(options: { text: string; startTime: number; layerId?: string }) {
        // TODO: Implement when needed
    }

    static loadFont(fontName: string, url: string) {
        // TODO: Implement when needed
    }

    static pauseUndo() {
        // TODO: Implement when needed
    }

    static resumeUndo() {
        // TODO: Implement when needed
    }

    static getTransitionById(transitionId: string) {
        // TODO: Implement when needed
    }

    /**
     * Insert an existing engine clip into the composition's script layer.
     * Returns the same clip after being attached.
     */
    static async addToScriptLayer<L>(clip: any): Promise<L> {
        // Route to engine method
        // @ts-ignore
        const added = await (this.composition as any).insertClipIntoScriptLayer(clip);
        // Default: when inserting a new ScriptLayerClip with video sources, apply the "no layout" placement
        // (fit/center, Screen, or MultiCam depending on sources + canvas aspect).
        // Restore flows do not call addToScriptLayer, so persisted visuals are not overridden.
        try {
            // Mark this script clip as eligible for auto default placement improvements when focus data arrives.
            // If the user moves/resizes/applies layouts, we set __librettoAutoDefaultPlacementUserEdited = true.
            try {
                (added as any).__librettoAutoDefaultPlacement = true;
                (added as any).__librettoAutoDefaultPlacementUserEdited = false;
            } catch {}
            CompositionService.applyDefaultScriptClipPlacement(String(added.id));
        } catch {}
        const layerId = added.track.id;
        return added as L;
    }

    /**
     * Get the script layer clip if it exists.
     */
    static getScriptLayerClip(): any | null {
        const scriptLayerInfo = (this.composition as any)?.scriptLayer;
        if (!scriptLayerInfo?.clipId) return null;
        return this.getClipById(scriptLayerInfo.clipId);
    }

    /**
     * Get the current script-layer scene key for the playhead (composition time domain).
     * Keyed by ScriptLayerScene.startSplitId with "__start__" for the first scene.
     */
    static getCurrentSceneKey(): string | null {
        const clip: any = this.getScriptLayerClip();
        if (!clip || typeof clip.getScenes !== "function") return null;
        const scenes: any[] = Array.isArray(clip.getScenes()) ? clip.getScenes() : [];
        if (!scenes.length) return null;
        const t = Number(CompositionStateStore.currentTime ?? 0);
        const scene =
            scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
            scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
            scenes[scenes.length - 1];
        return scene?.startSplitId ? String(scene.startSplitId) : "__start__";
    }

    /**
     * Scene keys overlapped by a clip's timeline range.
     * Useful for deciding whether a clip spans multiple scenes.
     */
    static getOverlappingSceneKeysForClip(clip: any): string[] {
        const scriptClip: any = this.getScriptLayerClip();
        if (!scriptClip || typeof scriptClip.getScenes !== "function") return [];
        const scenes: any[] = Array.isArray(scriptClip.getScenes()) ? scriptClip.getScenes() : [];
        if (!scenes.length) return [];

        const start = Number(clip?.start?.seconds ?? 0);
        const dur = Number((clip as any)?.timelineDuration?.seconds ?? 0);
        const end = start + Math.max(0, Number.isFinite(dur) ? dur : 0);
        if (!Number.isFinite(start) || !Number.isFinite(end)) return [];

        const keys: string[] = [];
        for (const s of scenes) {
            const s0 = Number(s?.timelineStartSec ?? 0);
            const s1 = Number(s?.timelineEndSec ?? 0);
            if (!Number.isFinite(s0) || !Number.isFinite(s1)) continue;
            if (end > s0 && start < s1) {
                keys.push(s?.startSplitId ? String(s.startSplitId) : "__start__");
            }
        }
        return keys;
    }

    static clipSpansMultipleScenes(clip: any): boolean {
        return this.getOverlappingSceneKeysForClip(clip).length > 1;
    }

    static setSceneVisualOverridesForClip(
        clip: any,
        sceneKey: string,
        props: Partial<{
            x: number;
            y: number;
            width: number;
            height: number;
            alpha: number;
            hiddenByUser: boolean;
            rotation: number;
            maskShape: string;
            crop: { x: number; y: number; width: number; height: number } | null;
        }>
    ): void {
        if (!clip || !sceneKey) return;
        const key = String(sceneKey);
        const comp = this._composition ?? (this as any).composition;
        const cw = Number(comp?.settings?.width ?? comp?.width ?? 1920);
        const ch = Number(comp?.settings?.height ?? comp?.height ?? 1080);
        const aspectPreset = this.getCanvasAspectPresetFromSize(cw, ch);

        const prevByAspect = (clip as any).sceneVisualOverridesByAspect;
        const baseByAspect: Record<string, any> = prevByAspect && typeof prevByAspect === "object" ? { ...prevByAspect } : {};
        const prevForAspect = baseByAspect[aspectPreset];
        const base: Record<string, any> = prevForAspect && typeof prevForAspect === "object" ? { ...prevForAspect } : {};
        const cur = base[key] && typeof base[key] === "object" ? { ...base[key] } : {};
        const next: any = { ...cur, ...props };
        for (const k of Object.keys(next)) {
            if (next[k] === undefined) delete next[k];
        }
        base[key] = next;
        // Drop empty entries
        if (!base[key] || Object.keys(base[key]).length === 0) delete base[key];
        if (Object.keys(base).length > 0) baseByAspect[aspectPreset] = base;
        else delete baseByAspect[aspectPreset];
        (clip as any).sceneVisualOverridesByAspect = baseByAspect;
        try { clip.trigger?.("frame", undefined); } catch {}
    }

    static clearSceneVisualOverrideKeysForClip(
        clip: any,
        keys: Array<"x" | "y" | "width" | "height" | "alpha" | "hiddenByUser" | "rotation" | "maskShape" | "crop">,
        sceneKeys?: string[],
        aspectPreset?: "horizontal" | "vertical" | "square" | "all"
    ): void {
        if (!clip || !Array.isArray(keys) || keys.length === 0) return;
        const comp = this._composition ?? (this as any).composition;
        const cw = Number(comp?.settings?.width ?? comp?.width ?? 1920);
        const ch = Number(comp?.settings?.height ?? comp?.height ?? 1080);
        const currentPreset = this.getCanvasAspectPresetFromSize(cw, ch);
        const preset = aspectPreset ?? currentPreset;

        const prevByAspect = (clip as any).sceneVisualOverridesByAspect;
        if (prevByAspect && typeof prevByAspect === "object") {
            const nextByAspect: Record<string, any> = { ...prevByAspect };
            const aspectKeys = preset === "all" ? Object.keys(nextByAspect) : [preset];
            for (const ak of aspectKeys) {
                const prev = nextByAspect[ak];
                if (!prev || typeof prev !== "object") continue;

                const targetKeys = Array.isArray(sceneKeys) ? sceneKeys.map((k) => String(k)) : Object.keys(prev);
                const next: Record<string, any> = { ...prev };
                for (const sk of targetKeys) {
                    const entry = next[sk];
                    if (!entry || typeof entry !== "object") continue;
                    const updated: any = { ...entry };
                    for (const k of keys) delete updated[k];
                    if (Object.keys(updated).length === 0) delete next[sk];
                    else next[sk] = updated;
                }
                if (Object.keys(next).length === 0) delete nextByAspect[ak];
                else nextByAspect[ak] = next;
            }
            (clip as any).sceneVisualOverridesByAspect = nextByAspect;
            try { clip.trigger?.("frame", undefined); } catch {}
            return;
        }

        // Legacy fallback
        const prev = (clip as any).sceneVisualOverrides;
        if (!prev || typeof prev !== "object") return;
        const targetKeys = Array.isArray(sceneKeys) ? sceneKeys.map((k) => String(k)) : Object.keys(prev);
        const next: Record<string, any> = { ...prev };
        for (const sk of targetKeys) {
            const entry = next[sk];
            if (!entry || typeof entry !== "object") continue;
            const updated: any = { ...entry };
            for (const k of keys) delete updated[k];
            if (Object.keys(updated).length === 0) delete next[sk];
            else next[sk] = updated;
        }
        (clip as any).sceneVisualOverrides = next;
        try { clip.trigger?.("frame", undefined); } catch {}
    }

    private static remapSceneKeyedOverrides(args: {
        beforeScenes: any[];
        afterScenes: any[];
        forceMoveStartFromKey?: string;
    }): void {
        const beforeScenes = Array.isArray(args.beforeScenes) ? args.beforeScenes : [];
        const afterScenes = Array.isArray(args.afterScenes) ? args.afterScenes : [];
        if (!beforeScenes.length || !afterScenes.length) return;

        const sceneKey = (s: any): string => (s?.startSplitId ? String(s.startSplitId) : "__start__");
        const sceneEndKey = (scenes: any[], i: number): string => {
            if (i + 1 >= scenes.length) return "__end__";
            const next = scenes[i + 1];
            return next?.startSplitId ? String(next.startSplitId) : "__end__";
        };

        const beforeKeys = new Set<string>(beforeScenes.map(sceneKey));
        const afterKeys = new Set<string>(afterScenes.map(sceneKey));

        // Map from a boundary (scene end) to the scene key that ends there (used for inheritance on splits/insertions).
        const beforeByEndKey = new Map<string, string>();
        for (let i = 0; i < beforeScenes.length; i++) {
            beforeByEndKey.set(sceneEndKey(beforeScenes, i), sceneKey(beforeScenes[i]));
        }

        // Determine new scene keys and which existing scene they should inherit from.
        const copyPairs: Array<{ from: string; to: string }> = [];
        for (let i = 0; i < afterScenes.length; i++) {
            const to = sceneKey(afterScenes[i]);
            if (beforeKeys.has(to)) continue;
            const inherited = beforeByEndKey.get(sceneEndKey(afterScenes, i));
            if (inherited && beforeKeys.has(inherited) && inherited !== to) {
                copyPairs.push({ from: inherited, to });
            } else if (to === "__start__" && beforeKeys.has("__start__")) {
                // New first scene created before any known boundary: inherit the old first scene.
                copyPairs.push({ from: "__start__", to });
            }
        }

        const deepClone = <T>(v: T): T => {
            try {
                return JSON.parse(JSON.stringify(v));
            } catch {
                return v;
            }
        };

        const remapObject = (obj: any): { value: any; changed: boolean } => {
            if (!obj || typeof obj !== "object") return { value: obj, changed: false };
            let next: any = obj;
            let changed = false;

            const ensureMutable = () => {
                if (!changed) {
                    next = { ...obj };
                    changed = true;
                }
            };

            // Optional forced move: used when deleting the first scene so the next scene becomes "__start__".
            if (args.forceMoveStartFromKey) {
                const from = String(args.forceMoveStartFromKey);
                if (from && next[from] !== undefined) {
                    ensureMutable();
                    next["__start__"] = deepClone(next[from]);
                    delete next[from];
                }
            }

            for (const { from, to } of copyPairs) {
                if (next[to] !== undefined) continue;
                if (next[from] === undefined) continue;
                ensureMutable();
                next[to] = deepClone(next[from]);
            }

            for (const k of Object.keys(next)) {
                if (!afterKeys.has(k)) {
                    ensureMutable();
                    delete next[k];
                }
            }
            return { value: next, changed };
        };

        // Apply remap to all clips that have scene-keyed overrides.
        for (const tr of this.composition.tracks) {
            for (const c of tr.clips) {
                if (!c) continue;
                const type = String((c as any).type ?? "");

                let didUpdate = false;

                if (type === "captions") {
                    const prev = (c as any)._sceneStyleOverrides;
                    const { value: next, changed } = remapObject(prev);
                    if (changed) {
                        (c as any)._sceneStyleOverrides = next;
                        didUpdate = true;
                    }
                }

                if (type === "text") {
                    const prev = (c as any)._sceneStyleOverrides;
                    const { value: next, changed } = remapObject(prev);
                    if (changed) {
                        (c as any)._sceneStyleOverrides = next;
                        didUpdate = true;
                    }
                }

                const prevVisualByAspect = (c as any).sceneVisualOverridesByAspect;
                if (prevVisualByAspect && typeof prevVisualByAspect === "object") {
                    let changedAny = false;
                    const nextByAspect: Record<string, any> = { ...prevVisualByAspect };
                    for (const [presetKey, mapRaw] of Object.entries(nextByAspect)) {
                        if (!mapRaw || typeof mapRaw !== "object") continue;
                        const { value: next, changed } = remapObject(mapRaw);
                        if (changed) {
                            nextByAspect[presetKey] = next;
                            changedAny = true;
                        }
                    }
                    if (changedAny) {
                        (c as any).sceneVisualOverridesByAspect = nextByAspect;
                        didUpdate = true;
                    }
                } else {
                    const prevVisual = (c as any).sceneVisualOverrides;
                    if (prevVisual && typeof prevVisual === "object") {
                        const { value: next, changed } = remapObject(prevVisual);
                        if (changed) {
                            (c as any).sceneVisualOverrides = next;
                            didUpdate = true;
                        }
                    }
                }

                if (didUpdate) {
                    try { (c as any).trigger?.("frame", undefined); } catch {}
                    try { this.notifyClipUpdated(c); } catch {}
                }
            }
        }
    }

    /**
     * Apply a cut to the script layer clip.
     * Removes the audio/video content between startSec and endSec.
     * Also removes any scene boundaries (splits) at the cut boundaries or inside the cut range.
     * @param startSec - Start time of cut in composition time
     * @param endSec - End time of cut in composition time
     * @param options - Cut options including transcriptMode and optional analysis parameters
     * @returns The created cut, or null if no script layer exists
     */
    static cutScriptLayerRange(
        startSec: number, 
        endSec: number, 
        options?: { 
            transcriptMode?: "delete" | "strikethrough" | "retake-strikethrough";
            // Optional: for zero-crossing analysis
            sourceIndex?: number;
            prevWordEndSec?: number | null;
            nextWordStartSec?: number | null;
        }
    ): any | null {
        const clip = this.getScriptLayerClip();
        if (!clip || typeof clip.addScriptCut !== 'function') {
            return null;
        }

        // Remove splits at the cut boundaries or inside the cut range
        // This prevents orphaned scene boundaries after deleting a scene
        const TOLERANCE = 0.05; // 50ms tolerance for boundary detection
        const splitsToRemove = (clip.splits ?? []).filter((split: any) => {
            const t = split.timeSec;
            // Remove if at start boundary, end boundary, or inside the cut
            return (Math.abs(t - startSec) < TOLERANCE) ||
                   (Math.abs(t - endSec) < TOLERANCE) ||
                   (t > startSec && t < endSec);
        });
        
        for (const split of splitsToRemove) {
            if (typeof clip.removeScriptSplit === 'function') {
                clip.removeScriptSplit(split.id);
            }
        }

        // The cut times are in clip-local time (from word's startSec/endSec which are adjusted)
        const cut = clip.addScriptCut(startSec, endSec, undefined, options?.transcriptMode);

        this._finalizeCut(clip, cut);
        return cut;
    }
    
    /**
     * Apply a cut with zero-crossing analysis for optimal audio quality.
     * Async version that analyzes the audio to find ideal cut points.
     * Also removes any scene boundaries (splits) at the cut boundaries or inside the cut range.
     * @param startSec - Start time of cut in composition time
     * @param endSec - End time of cut in composition time
     * @param sourceIndex - Which audio source to analyze
     * @param options - Cut options including adjacency info for safe snapping
     * @returns The created cut, or null if no script layer exists
     */
    static async cutScriptLayerRangeWithAnalysis(
        startSec: number, 
        endSec: number, 
        sourceIndex: number,
        options?: { 
            transcriptMode?: "delete" | "strikethrough" | "retake-strikethrough";
            prevWordEndSec?: number | null;
            nextWordStartSec?: number | null;
        }
    ): Promise<any | null> {
        const clip = this.getScriptLayerClip();
        if (!clip || typeof clip.addScriptCut !== 'function') {
            return null;
        }
        
        // Remove splits at the cut boundaries or inside the cut range
        // This prevents orphaned scene boundaries after deleting a scene
        const TOLERANCE = 0.05; // 50ms tolerance for boundary detection
        const splitsToRemove = (clip.splits ?? []).filter((split: any) => {
            const t = split.timeSec;
            return (Math.abs(t - startSec) < TOLERANCE) ||
                   (Math.abs(t - endSec) < TOLERANCE) ||
                   (t > startSec && t < endSec);
        });
        
        for (const split of splitsToRemove) {
            if (typeof clip.removeScriptSplit === 'function') {
                clip.removeScriptSplit(split.id);
            }
        }
        
        // Run zero-crossing + RMS analysis
        let analysis = null;
        if (typeof clip.analyzeCut === 'function') {
            try {
                analysis = await clip.analyzeCut(
                    startSec,
                    endSec,
                    sourceIndex,
                    options?.prevWordEndSec ?? null,
                    options?.nextWordStartSec ?? null
                );
                
                if (analysis) {
                    if (DEBUG_ENGINE_LOGS) {
                        console.log("[CompositionService] Cut analysis result:", {
                            originalRange: `${startSec.toFixed(3)}s - ${endSec.toFixed(3)}s`,
                            snappedRange: `${analysis.startAnalysis.snappedSec.toFixed(3)}s - ${analysis.endAnalysis.snappedSec.toFixed(3)}s`,
                            fadeOut: `${analysis.fadeOutMs}ms`,
                            fadeIn: `${analysis.fadeInMs}ms`,
                            isHarsh: analysis.isHarsh,
                            rmsRatio: analysis.rmsRatio.toFixed(2),
                            hasZeroCrossings: `start=${analysis.startAnalysis.hasZeroCrossing}, end=${analysis.endAnalysis.hasZeroCrossing}`,
                        });
                    }
                }
            } catch (err) {
                console.warn('[CompositionService] Cut analysis failed, using original times:', err);
            }
        }

        // Apply cut with analysis (or without if analysis failed)
        const cut = clip.addScriptCut(
            startSec, 
            endSec, 
            undefined, 
            options?.transcriptMode,
            analysis
        );

        this._finalizeCut(clip, cut);
        return cut;
    }
    
    /**
     * Internal helper to finalize a cut (fire events, update schedule).
     */
    private static _finalizeCut(clip: any, cut: any): void {
        if (!this.isInitialized()) {
            console.warn("[CompositionService] Composition not initialized, cannot update schedule");
            return;
        }
        this.queueOrApplyScriptLayerTimingUpdate(clip, { cutsChanged: true });
    }

    /**
     * Apply a split (scene boundary) to the script layer clip.
     * Creates a boundary point for transitions without removing content.
     * Ignores the request if a split already exists within 0.1s of the specified time.
     * @param timeSec - Time of split in script-clip local time (same domain as transcript words/cuts)
     * @param isSceneBoundary - Whether this is a scene boundary
     * @param label - Optional label for the scene
     * @returns The created split, or null if no script layer exists or split was skipped
     */
    static splitScriptLayer(timeSec: number, isSceneBoundary: boolean = true, label?: string): any | null {
        const clip = this.getScriptLayerClip();
        if (!clip || typeof clip.addScriptSplit !== 'function') {
            console.warn('[CompositionService] No script layer clip or addScriptSplit not available');
            return null;
        }

        const scenesBefore: any[] = isSceneBoundary && typeof clip.getScenes === "function" ? clip.getScenes() : [];

        // Check if a split already exists within tolerance (prevent near-duplicate splits)
        const TOLERANCE = 0.1; // 100ms - user shouldn't create splits this close together
        const existingSplits = clip.splits ?? [];
        const hasSplitNearby = existingSplits.some((s: any) => Math.abs(s.timeSec - timeSec) < TOLERANCE);
        
        if (hasSplitNearby) {
            console.log('[CompositionService] Skipping split - already exists within 0.1s of', timeSec);
            return null;
        }
        
        // Don't create splits at clip boundaries (start or end).
        // For trimmed clips, boundaries are range.inSec and range.outSec.
        const inSec = clip.range?.[0]?.seconds ?? 0;
        const outSec = clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? inSec;
        if (timeSec < inSec + TOLERANCE || timeSec > outSec - TOLERANCE) {
            console.log('[CompositionService] Skipping split - too close to clip boundary:', timeSec);
            return null;
        }

        const split = clip.addScriptSplit(timeSec, { isSceneBoundary, label });

        // Keep ScriptLayerClip.sceneProperties stable across scene splits.
        // sceneProperties is keyed by scene index, so inserting a new scene boundary shifts indices.
        // Rule: the new right-side scene inherits the split scene's properties, while:
        // - left drops outTransition (new boundary)
        // - right drops inTransition (new boundary)
        if (isSceneBoundary) {
            try {
                const map: Map<number, any> | undefined = (clip as any)?.sceneProperties;
                if (map && typeof (map as any).entries === "function" && scenesBefore.length > 0) {
                    const EPS = 1e-3;
                    const containing =
                        scenesBefore.find((s: any) => {
                            const s0 = Number(s?.startSec ?? NaN);
                            const s1 = Number(s?.endSec ?? NaN);
                            return Number.isFinite(s0) && Number.isFinite(s1) && timeSec > s0 + EPS && timeSec < s1 - EPS;
                        }) ??
                        scenesBefore.find((s: any) => {
                            const s0 = Number(s?.startSec ?? NaN);
                            const s1 = Number(s?.endSec ?? NaN);
                            return Number.isFinite(s0) && Number.isFinite(s1) && timeSec >= s0 - EPS && timeSec < s1 - EPS;
                        });

                    const splitSceneIndex = Number(containing?.index ?? NaN);
                    if (Number.isFinite(splitSceneIndex) && splitSceneIndex >= 0) {
                        const existingProps = new Map<number, any>(map as any);

                        const cloneSceneProps = (props: any): any => {
                            const cloned: any = { ...(props ?? {}) };
                            const layoutByAspect = (props as any)?.layoutByAspect;
                            if (layoutByAspect && typeof layoutByAspect === "object") {
                                try { cloned.layoutByAspect = { ...(layoutByAspect as any) }; } catch {}
                            }
                            const vis = (props as any)?.visualOverrides;
                            if (vis && typeof (vis as any)?.entries === "function") {
                                const clonedVis = new Map<number, any>();
                                for (const [k, v] of (vis as any).entries()) {
                                    clonedVis.set(Number(k), { ...(v ?? {}) });
                                }
                                cloned.visualOverrides = clonedVis;
                            }
                            const visByAspect = (props as any)?.visualOverridesByAspect;
                            if (visByAspect && typeof visByAspect === "object") {
                                const clonedByAspect: any = {};
                                for (const [presetKey, mapRaw] of Object.entries(visByAspect as any)) {
                                    const m: any = mapRaw as any;
                                    if (!m || typeof m?.entries !== "function") continue;
                                    const clonedMap = new Map<number, any>();
                                    for (const [k, v] of m.entries()) {
                                        clonedMap.set(Number(k), { ...(v ?? {}) });
                                    }
                                    clonedByAspect[presetKey] = clonedMap;
                                }
                                if (Object.keys(clonedByAspect).length > 0) cloned.visualOverridesByAspect = clonedByAspect;
                            }
                            if ((props as any)?.inTransition) {
                                cloned.inTransition = JSON.parse(JSON.stringify((props as any).inTransition));
                            }
                            if ((props as any)?.outTransition) {
                                cloned.outTransition = JSON.parse(JSON.stringify((props as any).outTransition));
                            }
                            return cloned;
                        };

                        const base = existingProps.get(splitSceneIndex);
                        const leftProps = base ? cloneSceneProps(base) : undefined;
                        const rightProps = base ? cloneSceneProps(base) : undefined;

                        if (leftProps && "outTransition" in leftProps) delete (leftProps as any).outTransition;
                        if (rightProps && "inTransition" in rightProps) delete (rightProps as any).inTransition;

                        const next = new Map<number, any>();
                        for (const [idxRaw, props] of existingProps.entries()) {
                            const idx = Number(idxRaw);
                            if (!Number.isFinite(idx) || idx < 0) continue;
                            if (idx < splitSceneIndex) next.set(idx, props);
                            else if (idx === splitSceneIndex) continue; // replaced below
                            else next.set(idx + 1, props);
                        }

                        if (leftProps && Object.keys(leftProps).length > 0) next.set(splitSceneIndex, leftProps);
                        if (rightProps && Object.keys(rightProps).length > 0) next.set(splitSceneIndex + 1, rightProps);

                        (clip as any).sceneProperties = next;
                        (clip as any)._scenesCache = null;
                    }
                }
            } catch {}
        }

        // By default, new scene boundaries get a Smart transition (configurable editor-wide).
        if (isSceneBoundary) {
            try {
                const boundaryTime = Number((split as any)?.timeSec ?? timeSec);
                const scenesAfter: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];
                const EPS = 0.02;
                let boundarySceneIndex =
                    scenesAfter.find((s: any) => Math.abs(Number(s?.startSec ?? NaN) - boundaryTime) <= EPS)?.index ?? null;
                if (boundarySceneIndex == null && typeof clip.findSceneIndexForTime === "function") {
                    boundarySceneIndex = clip.findSceneIndexForTime(boundaryTime + 1e-6);
                }
                if (boundarySceneIndex != null) {
                    this.ensureAutoSmartTransitionAtBoundary(clip, Number(boundarySceneIndex));
                }
            } catch {}
        }

        // Remap any scene-keyed overrides on other clips (captions styles, per-scene visual overrides).
        if (isSceneBoundary) {
            try {
                const scenesAfter: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];
                this.remapSceneKeyedOverrides({ beforeScenes: scenesBefore, afterScenes: scenesAfter });
            } catch {}
        }
        
        // Fire internal event with full state including splits for scene derivation
		        this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
		            clipId: clip.id,
	            cuts: clip.scriptCuts,
	            gaps: clip.gaps,
	            gapInsertions: clip.gapInsertions,
	            segments: clip.scriptSegments,
	            splits: clip.splits,
	            duration: clip.duration?.seconds ?? 0,
	            hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
	        });
        // Trigger updates
        this.composition?.onClipTimingChanged?.();

        // Fire UI event for persistence (splits/scene boundaries).
        this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
            script: {
                layerId: this.composition.scriptLayer?.layerId ?? null,
                clipId: clip.id,
                splitsChanged: true,
            }
        });
        
        return split;
    }

    /**
     * Insert a new empty scene on the ScriptClip spine.
     * - Adds fillable silent time (default 5s) at `atSec` (script-clip local time domain).
     * - Ripples all downstream non-script clips later on the composition timeline.
     */
    static insertEmptyScriptScene(atSec: number, durationSec: number = 5): boolean {
        const clip: any = this.getScriptLayerClip();
        if (!clip || typeof clip.insertEmptyScene !== "function") {
            console.warn("[CompositionService] No script layer clip or insertEmptyScene not available");
            return false;
        }

        const scenesBefore: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];

        const EPS = 1e-3;
        const dur = Number(durationSec ?? 0);
        if (!Number.isFinite(dur) || dur <= EPS) return false;

        // Capture the insertion position in clip-local time (source time domain) so we can identify new boundaries.
        const inSec = Number(clip.range?.[0]?.seconds ?? 0);
        const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? inSec);
        const at = Number(atSec ?? 0);
        if (!Number.isFinite(at)) return false;
        const clampedAt = Math.max(inSec, Math.min(outSec, at));
        const isAtStart = clampedAt <= inSec + EPS;
        const isAtEnd = clampedAt >= outSec - EPS;
        const emptyEnd = clampedAt + dur;

        // Map script-clip local time -> absolute composition time (pre-insert) to ripple non-script clips.
        const clipStartSec = Number(clip.start?.seconds ?? 0);
        const segments: any[] = Array.isArray(clip.scriptSegments) ? clip.scriptSegments : [];
        let insertionAbsCompSec: number | null = null;

        for (const seg of segments) {
            const s0 = Number(seg?.srcStartSec ?? NaN);
            const s1 = Number(seg?.srcEndSec ?? NaN);
            const c0 = Number(seg?.compStartSec ?? NaN);
            const rateRaw = Number(seg?.playbackRate ?? 1);
            const rate = Number.isFinite(rateRaw) && rateRaw > 0 ? rateRaw : 1;
            if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
            if (at >= s0 - EPS && at < s1 - EPS) {
                insertionAbsCompSec = c0 + (at - s0) / rate;
                break;
            }
        }

        // Exact end-of-clip insertion: anchor to the end of the last segment.
        if (insertionAbsCompSec == null && segments.length > 0) {
            const last = segments[segments.length - 1];
            const s0 = Number(last?.srcStartSec ?? NaN);
            const s1 = Number(last?.srcEndSec ?? NaN);
            const c0 = Number(last?.compStartSec ?? NaN);
            const rateRaw = Number(last?.playbackRate ?? 1);
            const rate = Number.isFinite(rateRaw) && rateRaw > 0 ? rateRaw : 1;
            if (Number.isFinite(s0) && Number.isFinite(s1) && Number.isFinite(c0) && Math.abs(at - s1) <= 0.01) {
                insertionAbsCompSec = c0 + (s1 - s0) / rate;
            }
        }

        if (insertionAbsCompSec == null) {
            // Conservative fallback.
            insertionAbsCompSec = clipStartSec;
        }

        const ok = Boolean(clip.insertEmptyScene(at, dur));
        if (!ok) return false;

        // `durationSec` is a timeline duration from every editor caller (for
        // example, "add a 5 second scene" or the natural duration of a newly
        // recorded asset). A ScriptClip that was previously a single scene can
        // carry a clip-level playback rate, which the new scene would otherwise
        // inherit. Give the inserted scene an explicit normal rate so its
        // timeline duration remains `dur` and the downstream ripple below stays
        // exactly aligned with the space that was added.
        try {
            const insertedScene = (typeof clip.getScenes === "function"
                ? clip.getScenes()
                : []
            ).find(
                (scene: any) =>
                    Math.abs(Number(scene?.startSec ?? NaN) - clampedAt) <= 0.01 &&
                    Math.abs(Number(scene?.endSec ?? NaN) - emptyEnd) <= 0.01,
            );
            const insertedSceneIndex = Number(insertedScene?.index ?? NaN);
            const insertedSceneRate = Number(
                typeof clip.getEffectivePlaybackRateForScene === "function" &&
                    Number.isFinite(insertedSceneIndex)
                    ? clip.getEffectivePlaybackRateForScene(insertedSceneIndex)
                    : insertedScene?.properties?.playbackRate ?? clip.playbackRate ?? 1,
            );
            if (
                Number.isFinite(insertedSceneIndex) &&
                Math.abs(insertedSceneRate - 1) > EPS &&
                typeof clip.setSceneProperties === "function"
            ) {
                clip.setSceneProperties(insertedSceneIndex, { playbackRate: 1 });
            }
        } catch {}

        // Scene zero has no persistent engine boundary id. Inserting an empty
        // scene can therefore make the positional `root` identity refer to a
        // different scene. Clear semantic scene/transcript selection before
        // publishing the new scene model instead of briefly selecting the new
        // content by accident. Range-tool selection is intentionally separate.
        ApplicationStore.clearSelectedScriptTimelineSection();

        // By default, newly inserted scene boundaries get Smart transitions (configurable editor-wide).
        if (this.shouldAutoApplySmartTransitionsToNewScenes()) {
            try {
                const times: number[] = [];
                if (!isAtStart) times.push(clampedAt);
                if (!isAtEnd) times.push(emptyEnd);

                const scenesAfter: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];
                const findBoundarySceneIndex = (tSec: number): number | null => {
                    const EPS2 = 0.02;
                    const hit = scenesAfter.find((s: any) => Math.abs(Number(s?.startSec ?? NaN) - tSec) <= EPS2);
                    const idx = Number(hit?.index ?? NaN);
                    if (Number.isFinite(idx) && idx >= 0) return idx;
                    if (typeof clip.findSceneIndexForTime === "function") {
                        const fallback = Number(clip.findSceneIndexForTime(tSec + 1e-6));
                        if (Number.isFinite(fallback) && fallback >= 0) return fallback;
                    }
                    return null;
                };

                for (const tSec of times) {
                    const boundarySceneIndex = findBoundarySceneIndex(tSec);
                    if (boundarySceneIndex == null) continue;
                    this.ensureAutoSmartTransitionAtBoundary(clip, boundarySceneIndex);
                }
            } catch {}
        }

        // Remap any scene-keyed overrides on other clips (captions styles, per-scene visual overrides).
        try {
            const scenesAfter: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];
            this.remapSceneKeyedOverrides({ beforeScenes: scenesBefore, afterScenes: scenesAfter });
        } catch {}

        // Ripple-shift all non-script clips that start at/after the insertion point later by dur.
        const movedClips: any[] = [];
        try {
            for (const tr of this.composition.tracks) {
                for (const c of tr.clips) {
                    if (!c || c.id === clip.id) continue;
                    if (String(c.type) === "script" || this.isScriptLayerClip(String(c.id))) continue;
                    const start = Number(c.start?.seconds ?? 0);
                    if (!Number.isFinite(start) || start < insertionAbsCompSec - EPS) continue;

                    if ("offset" in c) {
                        const currentOffset = Number((c as any)._offset?.seconds ?? (c as any).offset?.seconds ?? 0);
                        const nextOffset = Math.max(0, currentOffset + dur);
                        try {
                            (c as any)._offset.seconds = nextOffset;
                        } catch {
                            (c as any)._offset = Timestamp.fromSeconds(nextOffset);
                        }
                        movedClips.push(c);
                    } else if (typeof (c as any).offsetBy === "function") {
                        try {
                            (c as any).offsetBy(Timestamp.fromSeconds(dur));
                        } catch {}
                        movedClips.push(c);
                    }
                }
            }
        } catch (e) {
            console.warn("[CompositionService] Ripple shift (insert empty scene) failed:", e);
        }

        // Rebuild schedule + update rendering.
        if (this.isInitialized()) {
            try {
                this.composition.onClipTimingChanged();
            } catch (e) {
                console.error("[CompositionService] Error in onClipTimingChanged:", e);
            }

            try {
                this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                    clipId: clip.id,
                    cuts: clip.scriptCuts,
                    gaps: clip.gaps,
                    gapInsertions: clip.gapInsertions,
                    segments: clip.scriptSegments,
                    splits: clip.splits,
                    duration: clip.duration?.seconds ?? 0,
                    hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
                });
            } catch {}

            try {
                this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                    script: {
                        layerId: this.composition.scriptLayer?.layerId ?? null,
                        clipId: clip.id,
                        splitsChanged: true,
                    },
                });
            } catch {}

            try {
                this.notifyClipUpdated(clip, { timingChanged: true });
            } catch {}
            for (const c of movedClips) {
                try {
                    this.notifyClipUpdated(c, { timingChanged: true });
                } catch {}
            }
        }

        return true;
    }

    /**
     * Delete a whole script-layer scene (by scene index).
     *
     * Semantics:
     * - Removes the time span of the target scene via a script cut.
     * - Preserves the boundary between the remaining adjacent scenes:
     *   - Deleting a middle scene keeps the split at the scene START (it becomes the boundary between prev/next),
     *     and removes the split at the scene END (start of next scene).
     *   - Deleting the first scene removes the split at its END (next scene becomes scene 0).
     *   - Deleting the last scene removes the split at its START.
     *
     * Note: Callers should handle the "delete last remaining scene" case by deleting the script clip itself.
     */
    static deleteScriptLayerScene(
        sceneIndex: number,
        options?: { transcriptMode?: "delete" | "strikethrough" | "retake-strikethrough" },
    ): boolean {
        const clip: any = this.getScriptLayerClip();
        if (!clip || typeof clip.getScenes !== "function" || typeof clip.addScriptCut !== "function") {
            console.warn("[CompositionService] No script layer clip or getScenes/addScriptCut not available");
            return false;
        }

        const idx = Math.floor(Number(sceneIndex));
        if (!Number.isFinite(idx)) return false;

        const scenes: any[] = Array.isArray(clip.getScenes?.()) ? clip.getScenes() : [];
        if (!scenes.length || idx < 0 || idx >= scenes.length) return false;

        const scene = scenes.find((s: any) => Number(s?.index) === idx) ?? scenes[idx];
        const startSec = Number(scene?.startSec ?? 0);
        const endSec = Number(scene?.endSec ?? startSec);
        const EPS = 1e-3;
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec + EPS) return false;

        // ─────────────────────────────────────────────────────────────────────────
        // Ripple-delete semantics for non-script clips (Descript-style):
        // - Deleting a script scene removes timeline time on the "spine".
        // - Non-script clips are never split / hole-punched.
        // - Clips strictly after the deleted interval shift left by the removed duration.
        // - Clips that overlap the deleted interval are trimmed from the RIGHT only (or deleted if zero-length).
        //
        // We compute:
        // - `cutStartCompSec`: composition-time where the deleted scene begins (pre-cut)
        // - `deltaCompSec`: how much composition time is actually removed by the cut (accounts for existing cuts/insertions)
        // - Deleted interval in pre-cut composition time: [cutStartCompSec, cutStartCompSec + deltaCompSec)
        // ─────────────────────────────────────────────────────────────────────────
        const preCutScriptEnd = this.getScriptEndSec();
        const preCutSegments: any[] = Array.isArray(clip.scriptSegments) ? clip.scriptSegments : [];
        const mapClipLocalToComp = (tSec: number): number | null => {
            const t = Number(tSec ?? NaN);
            if (!Number.isFinite(t)) return null;
            const SEG_EPS = 1e-3;

            // Direct hit: inside a segment.
            for (const seg of preCutSegments) {
                const s0 = Number(seg?.srcStartSec ?? NaN);
                const s1 = Number(seg?.srcEndSec ?? NaN);
                const c0 = Number(seg?.compStartSec ?? NaN);
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
                if (t >= s0 - SEG_EPS && t < s1 - SEG_EPS) {
                    return c0 + (t - s0);
                }
            }

            // Exact end: map to end of the last segment.
            if (preCutSegments.length > 0) {
                const last = preCutSegments[preCutSegments.length - 1];
                const s0 = Number(last?.srcStartSec ?? NaN);
                const s1 = Number(last?.srcEndSec ?? NaN);
                const c0 = Number(last?.compStartSec ?? NaN);
                if (Number.isFinite(s0) && Number.isFinite(s1) && Number.isFinite(c0) && Math.abs(t - s1) <= 0.01) {
                    return c0 + Math.max(0, s1 - s0);
                }
            }

            // If t is inside a cut (no segment), map to the next segment's comp start.
            let nextComp: number | null = null;
            for (const seg of preCutSegments) {
                const s0 = Number(seg?.srcStartSec ?? NaN);
                const c0 = Number(seg?.compStartSec ?? NaN);
                if (!Number.isFinite(s0) || !Number.isFinite(c0)) continue;
                if (s0 >= t - SEG_EPS) {
                    nextComp = c0;
                    break;
                }
            }
            if (nextComp != null && Number.isFinite(nextComp)) return nextComp;

            // Fallback: if we can, map to the pre-cut script end.
            if (preCutScriptEnd != null && Number.isFinite(preCutScriptEnd)) return preCutScriptEnd;
            return null;
        };

        const cutStartCompSec = mapClipLocalToComp(startSec);

        const isFirst = idx === 0;
        const isLast = idx === scenes.length - 1;
        const startSplitId: string | undefined = typeof scene?.startSplitId === "string" ? scene.startSplitId : undefined;
        const nextScene = !isLast ? (scenes.find((s: any) => Number(s?.index) === idx + 1) ?? scenes[idx + 1]) : null;
        const endSplitId: string | undefined =
            !isLast && typeof nextScene?.startSplitId === "string" ? nextScene.startSplitId : undefined;

        // Which boundary should remain after deletion?
        // - First scene: remove its end boundary so the next scene becomes scene 0.
        // - Last scene: remove its start boundary.
        // - Middle scene: remove its start boundary; keep the next-scene boundary as the join marker.
        const keepSplitId: string | undefined = (!isFirst && !isLast) ? endSplitId : undefined;

        // Remove splits that are invalidated by deleting this scene.
        // Important: for middle-scene deletes, we KEEP the next-scene boundary split so the
        // surrounding scenes do not merge.
        const splits: any[] = Array.isArray(clip.splits) ? clip.splits : [];
        const toRemove = new Set<string>();
        const TOL = 0.05; // 50ms

        for (const s of splits) {
            const id = String(s?.id ?? "");
            const t = Number(s?.timeSec ?? NaN);
            if (!id || !Number.isFinite(t)) continue;

            // Inside the deleted scene (excluding the kept boundary when deleting middle scenes).
            const isStrictlyInside = t > startSec + TOL && t < endSec - TOL;
            if (isStrictlyInside) {
                toRemove.add(id);
                continue;
            }

            // Always remove the split that STARTS this scene (except scene 0 which has no start split).
            // This prevents the deleted scene from leaving behind a boundary at its start.
            if (!isFirst && startSplitId && id === startSplitId) {
                toRemove.add(id);
                continue;
            }

            // Deleting the first scene: remove its end boundary so the next scene becomes scene 0.
            if (isFirst && !isLast) {
                const isEndById = endSplitId && id === endSplitId;
                const isEndByTime = Math.abs(t - endSec) < TOL;
                if (isEndById || isEndByTime) {
                    toRemove.add(id);
                    continue;
                }
            }

            // Deleting the last scene: remove its start boundary (handled above); no end boundary exists.
            // Middle-scene deletes: keep the next-scene boundary (join marker).
            if (keepSplitId && id === keepSplitId) {
                // Ensure we didn't accidentally mark it for removal via time matching.
                toRemove.delete(id);
            }
        }

        for (const splitId of toRemove) {
            try { clip.removeScriptSplit?.(splitId); } catch {}
        }

        // Apply the cut for the scene span.
        const cut = clip.addScriptCut(startSec, endSec, undefined, options?.transcriptMode ?? 'delete');

        // Remap any scene-keyed overrides on other clips (captions styles, per-scene visual overrides).
        let scenesAfter: any[] = [];
        try {
            scenesAfter = typeof clip.getScenes === "function" ? clip.getScenes() : [];
            this.remapSceneKeyedOverrides({
                beforeScenes: scenes,
                afterScenes: scenesAfter,
                forceMoveStartFromKey: isFirst && endSplitId ? String(endSplitId) : undefined,
            });
        } catch {}

        // Keep ScriptLayerClip.sceneProperties stable after deleting a scene.
        try {
            const map: Map<number, any> | undefined = (clip as any)?.sceneProperties;
            if (map && typeof (map as any).entries === "function") {
                const oldProps = new Map<number, any>(map as any);
                const nextProps = new Map<number, any>();
                for (const [idxRaw, props] of oldProps.entries()) {
                    const sidx = Number(idxRaw);
                    if (!Number.isFinite(sidx) || sidx < 0) continue;
                    if (sidx === idx) continue;
                    if (sidx > idx) nextProps.set(sidx - 1, props);
                    else nextProps.set(sidx, props);
                }

                const cleanupProp = (sceneIdx: number, key: "inTransition" | "outTransition") => {
                    const cur = nextProps.get(sceneIdx);
                    if (!cur || typeof cur !== "object" || !(key in cur)) return;
                    const cloned: any = { ...(cur ?? {}) };
                    delete cloned[key];
                    if (Object.keys(cloned).length > 0) nextProps.set(sceneIdx, cloned);
                    else nextProps.delete(sceneIdx);
                };

                // If the next scene becomes the new first scene, it should not have an inTransition.
                if (isFirst) cleanupProp(0, "inTransition");
                // If the previous scene becomes the new last scene, it should not have an outTransition.
                if (isLast) {
                    const lastIdx = scenesAfter.length > 0 ? scenesAfter.length - 1 : Math.max(0, scenes.length - 2);
                    cleanupProp(lastIdx, "outTransition");
                }

                (clip as any).sceneProperties = nextProps;
                (clip as any)._scenesCache = null;
            }
        } catch {}

        // Ripple/update non-script clips BEFORE rebuilding the schedule.
        // We intentionally do this pre-`onClipTimingChanged()` so the schedule is rebuilt once with final timings.
        const changedClips: any[] = [];
        if (cutStartCompSec != null && Number.isFinite(cutStartCompSec) && preCutScriptEnd != null && Number.isFinite(preCutScriptEnd)) {
            const postCutScriptEnd = this.getScriptEndSec();
            const deltaCompSec = postCutScriptEnd != null && Number.isFinite(postCutScriptEnd)
                ? Math.max(0, Number(preCutScriptEnd) - Number(postCutScriptEnd))
                : 0;

            if (deltaCompSec > EPS) {
                const cutEndCompSec = cutStartCompSec + deltaCompSec;
                const MIN_DUR = 0.01;

                const shiftClipBy = (c: any, deltaSec: number): void => {
                    if (!c) return;
                    const d = Number(deltaSec ?? 0);
                    if (!Number.isFinite(d) || Math.abs(d) < 1e-6) return;

                    // Prevent negative start times; allow negative offsets for trimmed clips.
                    const currentStart = Number(c.start?.seconds ?? 0);
                    const safeDelta = currentStart + d < 0 ? -currentStart : d;

                    if ("offset" in c) {
                        const currentOffset = Number((c as any)._offset?.seconds ?? (c as any).offset?.seconds ?? 0);
                        const nextOffset = Number.isFinite(currentOffset) ? currentOffset + safeDelta : safeDelta;
                        try {
                            (c as any)._offset.seconds = nextOffset;
                        } catch {
                            (c as any)._offset = Timestamp.fromSeconds(nextOffset);
                        }
                        return;
                    }
                    if (typeof (c as any).offsetBy === "function") {
                        try { (c as any).offsetBy(Timestamp.fromSeconds(safeDelta)); } catch {}
                        return;
                    }
                    if ("start" in c) {
                        try { (c as any).start = Timestamp.fromSeconds(Math.max(0, currentStart + safeDelta)); } catch {}
                    }
                };

                const trimRightInPlace = (c: any, deltaSec: number): void => {
                    if (!c) return;
                    const d = Number(deltaSec ?? 0);
                    if (!Number.isFinite(d) || Math.abs(d) < 1e-6) return;

                    if (c instanceof MediaClip && String(c.type) !== "script") {
                        try {
                            const rate = Number((c as any).playbackRate ?? 1) || 1;
                            const currentVisibleSrcDur = Array.isArray((c as any).segments) && (c as any).segments.length
                                ? (c as any).segments.reduce(
                                      (sum: number, seg: any) => sum + Math.max(0, (seg?.srcEndSec ?? 0) - (seg?.srcStartSec ?? 0)),
                                      0
                                  )
                                : Math.max(0, ((c as any).range?.[1]?.seconds ?? 0) - ((c as any).range?.[0]?.seconds ?? 0));

                            const minTimelineDur = MIN_DUR;
                            const minSrcDur = minTimelineDur * rate;

                            // We only shrink in this path, but keep the same minimum constraints as trimClipRight().
                            const desiredVisibleSrcDur = Math.max(minSrcDur, currentVisibleSrcDur + d * rate);

                            const sourceTimeAtVisibleOffset = (visibleOffsetSrcSec: number): number => {
                                let remaining = Math.max(0, visibleOffsetSrcSec);
                                const segs = Array.isArray((c as any).segments) ? (c as any).segments : [];
                                for (const seg of segs) {
                                    const s = Number(seg?.srcStartSec ?? 0);
                                    const e = Number(seg?.srcEndSec ?? s);
                                    const dur = Math.max(0, e - s);
                                    if (remaining <= dur + 1e-6) return s + remaining;
                                    remaining -= dur;
                                }
                                return Number((c as any).range?.[1]?.seconds ?? 0);
                            };

                            const currentOutSec = Number((c as any).range?.[1]?.seconds ?? 0);
                            const newOutSec = Math.max(
                                Number((c as any).range?.[0]?.seconds ?? 0),
                                Math.min(currentOutSec, sourceTimeAtVisibleOffset(desiredVisibleSrcDur))
                            );

                            if (Array.isArray((c as any).range) && (c as any).range[1]) {
                                (c as any).range[1].seconds = newOutSec;
                            }
                            try { (c as any).normalizeCuts?.(); } catch {}
                            try { (c as any).deriveSegments?.(); } catch {}
                            try { (c as any).trigger?.("frame", undefined); } catch {}
                        } catch (e) {
                            console.warn("[CompositionService] trimRightInPlace failed:", e);
                        }
                        return;
                    }

                    if ("stop" in c) {
                        try {
                            const currentStop = Number((c as any).stop?.seconds ?? 0);
                            (c as any).stop = Timestamp.fromSeconds(currentStop + d);
                        } catch {}
                    }
                };

                // Work on a snapshot so we can delete safely.
                const toDelete: string[] = [];
                const candidates: any[] = [];
                try {
                    for (const tr of (this.composition as any)?.tracks ?? []) {
                        for (const c of tr?.clips ?? []) {
                            if (!c || c.id === clip.id) continue;
                            if (String((c as any).type) === "script" || this.isScriptLayerClip(String((c as any).id))) continue;
                            candidates.push(c);
                        }
                    }
                } catch {}

                for (const c of candidates) {
                    const id = String((c as any).id ?? "");
                    if (!id) continue;
                    const start = Number((c as any).start?.seconds ?? 0);
                    let dur = Number((c as any).timelineDuration?.seconds ?? NaN);
                    if (!Number.isFinite(dur)) {
                        const stop = Number((c as any).stop?.seconds ?? NaN);
                        dur = Number.isFinite(stop) ? Math.max(0, stop - start) : 0;
                    }
                    const end = start + Math.max(0, dur);

                    // Entirely before the removed interval: no-op.
                    if (end <= cutStartCompSec + EPS) continue;

                    // Entirely after the removed interval: shift left by deltaCompSec.
                    if (start >= cutEndCompSec - EPS) {
                        shiftClipBy(c, -deltaCompSec);
                        changedClips.push(c);
                        continue;
                    }

                    // Overlaps removed interval: trim from RIGHT only (no splitting / no mid-clip cuts).
                    // Start stays put; end shifts left if it was beyond cutEnd, or snaps to cutStart if it ended inside.
                    const newEnd = end >= cutEndCompSec - EPS ? end - deltaCompSec : cutStartCompSec;
                    const desiredDur = newEnd - start;
                    if (desiredDur <= MIN_DUR) {
                        toDelete.push(id);
                        continue;
                    }

                    const deltaDur = desiredDur - dur;
                    if (deltaDur < -EPS) {
                        trimRightInPlace(c, deltaDur);
                        changedClips.push(c);
                    }
                }

                for (const id of toDelete) {
                    try { this.deleteClipById(id); } catch {}
                }
            }
        }

        // Trigger updates once (cuts + splits changed).
        if (this.isInitialized()) {
            try {
                this.composition.onClipTimingChanged();
            } catch (e) {
                console.error('[CompositionService] Error in onClipTimingChanged:', e);
            }

            try {
                this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                    clipId: clip.id,
                    cuts: clip.scriptCuts,
                    gaps: clip.gaps,
                    gapInsertions: clip.gapInsertions,
                    segments: clip.scriptSegments,
                    splits: clip.splits,
                    duration: clip.duration?.seconds ?? 0,
                    hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
                });
            } catch {}

            try {
                this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                    script: {
                        layerId: this.composition.scriptLayer?.layerId ?? null,
                        clipId: clip.id,
                        cutsChanged: true,
                        splitsChanged: toRemove.size > 0,
                    },
                });
            } catch {}

            // Notify UI/persistence about changed non-script clips (offset/trim/delete).
            for (const c of changedClips) {
                try { this.notifyClipUpdated(c); } catch {}
            }
        }

        return Boolean(cut);
    }

    /**
     * Merge a script-layer scene with its previous scene (removes the scene boundary split).
     * @param sceneIndex - Scene index in the ScriptLayerClip scene model (0-based).
     * @returns true if merge succeeded, false otherwise.
     */
    static mergeScriptLayerSceneWithPrevious(sceneIndex: number): boolean {
        const clip: any = this.getScriptLayerClip();
        if (!clip || typeof clip.mergeSceneWithPrevious !== "function") {
            console.warn("[CompositionService] No script layer clip or mergeSceneWithPrevious not available");
            return false;
        }

        const idx = Math.floor(Number(sceneIndex));
        if (!Number.isFinite(idx)) return false;

        const scenesBefore: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];
        const merged = Boolean(clip.mergeSceneWithPrevious(idx));
        if (!merged) return false;

        // Remap any scene-keyed overrides on other clips (captions styles, per-scene visual overrides).
        try {
            const scenesAfter: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];
            this.remapSceneKeyedOverrides({ beforeScenes: scenesBefore, afterScenes: scenesAfter });
        } catch {}

        // Trigger updates + persist splits.
        try {
            this.composition?.onClipTimingChanged?.();
        } catch (e) {
            console.error("[CompositionService] Error in onClipTimingChanged:", e);
        }

        try {
            this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                clipId: clip.id,
                cuts: clip.scriptCuts,
                gaps: clip.gaps,
                gapInsertions: clip.gapInsertions,
                segments: clip.scriptSegments,
                splits: clip.splits,
                duration: clip.duration?.seconds ?? 0,
                hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
            });
        } catch {}

        try {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    splitsChanged: true,
                },
            });
        } catch {}

        return true;
    }

    /**
     * Merge a script-layer scene with its next scene (removes the boundary between them).
     * @param sceneIndex - Scene index (0-based) to merge with the scene below it.
     * @returns true if merge succeeded, false otherwise.
     */
    static mergeScriptLayerSceneWithNext(sceneIndex: number): boolean {
        const idx = Math.floor(Number(sceneIndex));
        if (!Number.isFinite(idx)) return false;
        // Merging "with next" is equivalent to merging the next scene with its previous.
        return this.mergeScriptLayerSceneWithPrevious(idx + 1);
    }

    /**
     * Restore (remove) a script cut by ID - brings the audio/video back.
     * @param cutId - The ID of the cut to remove
     * @returns true if cut was removed, false otherwise
     */
    static restoreScriptCut(cutId: string): boolean {
        const clip = this.getScriptLayerClip();
        if (!clip || typeof clip.removeScriptCut !== 'function') {
            console.warn('[CompositionService] No script layer clip or removeScriptCut not available');
            return false;
        }

        const removed = clip.removeScriptCut(cutId);
        if (!removed) {
            console.warn('[CompositionService] Cut not found:', cutId);
            return false;
        }

        this.queueOrApplyScriptLayerTimingUpdate(clip, { cutsChanged: true });
        
        return true;
    }

    /**
     * Add a gap to the script layer (replace audio with silence, maintain timeline position).
     * Unlike cuts, gaps don't collapse the timeline.
     * @param startSec - Start time of gap in clip-local time
     * @param endSec - End time of gap in clip-local time
     * @returns The created gap, or null if no script layer exists
     */
    static addScriptLayerGap(startSec: number, endSec: number): any | null {
        const clip = this.getScriptLayerClip();
        if (!clip || typeof clip.addScriptGap !== 'function') {
            console.warn('[CompositionService] No script layer clip or addScriptGap not available');
            return null;
        }

        const gap = clip.addScriptGap(startSec, endSec);

        this.queueOrApplyScriptLayerTimingUpdate(clip, { gapsChanged: true });
        
        return gap;
    }

    /**
     * Hide a range of words from the transcript UI only (does NOT affect playback).
     * @param startSec - Start time in script clip-local time
     * @param endSec - End time in script clip-local time
     */
    static hideTranscriptRange(startSec: number, endSec: number): any | null {
        const clip: any = this.getScriptLayerClip();
        if (!clip || String(clip.type) !== "script") return null;

        const s = Number(startSec ?? 0);
        const e = Number(endSec ?? s);
        if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;

        if (!Array.isArray(clip.transcriptHiddenRanges)) {
            clip.transcriptHiddenRanges = [];
        }

        const range = {
            id: crypto?.randomUUID?.() ?? `hidden_${Date.now()}_${Math.random()}`,
            startSec: s,
            endSec: e,
        };
        clip.transcriptHiddenRanges.push(range);
        try {
            clip.transcriptHiddenRanges.sort((a: any, b: any) => (a.startSec ?? 0) - (b.startSec ?? 0));
        } catch {}

        if (this.isInitialized()) {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    transcriptHiddenRangesChanged: true,
                },
            });
        }

        return range;
    }

    /**
     * Insert a manual paragraph break in the transcript UI (editor-only; does NOT affect playback).
     * The break is anchored to either:
     * - the word immediately BEFORE the break (`anchorSide: "after"`, legacy/default), or
     * - the word immediately AFTER the break (`anchorSide: "before"`).
     */
    static addTranscriptParagraphBreak(anchorWordPayload: any, opts?: { anchorSide?: "after" | "before" }): any | null {
        const clip: any = this.getScriptLayerClip();
        if (!clip || String(clip.type) !== "script") return null;

        const anchorSide: "after" | "before" =
            String(opts?.anchorSide ?? "").toLowerCase() === "before" ? "before" : "after";
        const sourceIndex = Number.isFinite(anchorWordPayload?.sourceIndex) ? Number(anchorWordPayload.sourceIndex) : undefined;
        const srcStartSec = Number.isFinite(anchorWordPayload?.srcStartSec) ? Number(anchorWordPayload.srcStartSec) : undefined;
        const srcEndSec = Number.isFinite(anchorWordPayload?.srcEndSec) ? Number(anchorWordPayload.srcEndSec) : undefined;
        const timeSec =
            anchorSide === "before"
                ? Number.isFinite(anchorWordPayload?.startSec)
                    ? Number(anchorWordPayload.startSec)
                    : Number.isFinite(anchorWordPayload?.endSec)
                      ? Number(anchorWordPayload.endSec)
                      : undefined
                : Number.isFinite(anchorWordPayload?.endSec)
                  ? Number(anchorWordPayload.endSec)
                  : Number.isFinite(anchorWordPayload?.startSec)
                    ? Number(anchorWordPayload.startSec)
                    : undefined;

        // Require at least one stable identifier.
        if (sourceIndex == null && srcStartSec == null && timeSec == null) return null;

        if (!Array.isArray(clip.transcriptParagraphBreaks)) {
            clip.transcriptParagraphBreaks = [];
        }

        const EPS = 1e-3;
        const isSame = (b: any) => {
            const bAnchorSide: "after" | "before" =
                String((b as any)?.anchorSide ?? "").toLowerCase() === "before" ? "before" : "after";
            if (bAnchorSide !== anchorSide) return false;
            if (sourceIndex != null && Number.isFinite(b?.sourceIndex) && Number(b.sourceIndex) !== sourceIndex) return false;
            if (srcStartSec != null && Number.isFinite(b?.srcStartSec) && Math.abs(Number(b.srcStartSec) - srcStartSec) > EPS) return false;
            if (srcEndSec != null && Number.isFinite(b?.srcEndSec) && Math.abs(Number(b.srcEndSec) - srcEndSec) > EPS) return false;
            if (timeSec != null && Number.isFinite(b?.timeSec) && Math.abs(Number(b.timeSec) - timeSec) > EPS) return false;
            return true;
        };

        // Avoid duplicates.
        if (clip.transcriptParagraphBreaks.some((b: any) => isSame(b))) {
            return null;
        }

        const rec = {
            id: crypto?.randomUUID?.() ?? `pbreak_${Date.now()}_${Math.random()}`,
            sourceIndex,
            srcStartSec,
            srcEndSec,
            timeSec,
            anchorSide,
        };

        clip.transcriptParagraphBreaks.push(rec);
        try {
            clip.transcriptParagraphBreaks.sort((a: any, b: any) => (Number(a?.timeSec ?? 0) - Number(b?.timeSec ?? 0)));
        } catch {}

        if (this.isInitialized()) {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    transcriptParagraphBreaksChanged: true,
                },
            });
        }

        return rec;
    }

    /**
     * Remove a manual transcript paragraph break, identified by the anchor word payload
     * and anchor side.
     */
    static removeTranscriptParagraphBreak(anchorWordPayload: any, opts?: { anchorSide?: "after" | "before" }): any | null {
        const clip: any = this.getScriptLayerClip();
        if (!clip || String(clip.type) !== "script") return null;

        const anchorSide: "after" | "before" =
            String(opts?.anchorSide ?? "").toLowerCase() === "before" ? "before" : "after";
        const sourceIndex = Number.isFinite(anchorWordPayload?.sourceIndex) ? Number(anchorWordPayload.sourceIndex) : undefined;
        const srcStartSec = Number.isFinite(anchorWordPayload?.srcStartSec) ? Number(anchorWordPayload.srcStartSec) : undefined;
        const srcEndSec = Number.isFinite(anchorWordPayload?.srcEndSec) ? Number(anchorWordPayload.srcEndSec) : undefined;
        const timeSec =
            anchorSide === "before"
                ? Number.isFinite(anchorWordPayload?.startSec)
                    ? Number(anchorWordPayload.startSec)
                    : Number.isFinite(anchorWordPayload?.endSec)
                      ? Number(anchorWordPayload.endSec)
                      : undefined
                : Number.isFinite(anchorWordPayload?.endSec)
                  ? Number(anchorWordPayload.endSec)
                  : Number.isFinite(anchorWordPayload?.startSec)
                    ? Number(anchorWordPayload.startSec)
                    : undefined;

        if (!Array.isArray(clip.transcriptParagraphBreaks) || clip.transcriptParagraphBreaks.length === 0) return null;

        const EPS = 1e-3;
        const isSame = (b: any) => {
            const bAnchorSide: "after" | "before" =
                String((b as any)?.anchorSide ?? "").toLowerCase() === "before" ? "before" : "after";
            if (bAnchorSide !== anchorSide) return false;
            if (sourceIndex != null && Number.isFinite(b?.sourceIndex) && Number(b.sourceIndex) !== sourceIndex) return false;
            if (srcStartSec != null && Number.isFinite(b?.srcStartSec) && Math.abs(Number(b.srcStartSec) - srcStartSec) > EPS) return false;
            if (srcEndSec != null && Number.isFinite(b?.srcEndSec) && Math.abs(Number(b.srcEndSec) - srcEndSec) > EPS) return false;
            if (timeSec != null && Number.isFinite(b?.timeSec) && Math.abs(Number(b.timeSec) - timeSec) > EPS) return false;
            return true;
        };

        const idx = clip.transcriptParagraphBreaks.findIndex((b: any) => isSame(b));
        if (idx < 0) return null;

        const [removed] = clip.transcriptParagraphBreaks.splice(idx, 1);

        if (this.isInitialized()) {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    transcriptParagraphBreaksChanged: true,
                },
            });
        }

        return removed ?? null;
    }

    /**
     * Add a transcript-only marker (chapter/header) anchored between words.
     * Markers are editor-only navigation and do NOT affect captions/export/playback.
     */
    static addTranscriptMarker(args: {
        title?: string;
        afterWordPayload?: any;
        beforeWordPayload?: any;
    }): any | null {
        const clip: any = this.getScriptLayerClip();
        if (!clip || String(clip.type) !== "script") return null;

        const title = String(args?.title ?? "Marker").trim() || "Marker";

        const cleanAnchor = (payload: any, mode: "after" | "before"): any | undefined => {
            if (!payload || typeof payload !== "object") return undefined;
            const sourceIndex = Number.isFinite(payload?.sourceIndex) ? Number(payload.sourceIndex) : undefined;
            const srcStartSec = Number.isFinite(payload?.srcStartSec) ? Number(payload.srcStartSec) : undefined;
            const srcEndSec = Number.isFinite(payload?.srcEndSec) ? Number(payload.srcEndSec) : undefined;
            const timeSec =
                mode === "after"
                    ? Number.isFinite(payload?.startSec)
                        ? Number(payload.startSec)
                        : undefined
                    : Number.isFinite(payload?.endSec)
                      ? Number(payload.endSec)
                      : Number.isFinite(payload?.startSec)
                        ? Number(payload.startSec)
                        : undefined;

            if (sourceIndex == null && srcStartSec == null && timeSec == null) return undefined;
            return { sourceIndex, srcStartSec, srcEndSec, timeSec };
        };

        const after = cleanAnchor(args?.afterWordPayload, "after");
        const before = cleanAnchor(args?.beforeWordPayload, "before");

        const timeSec =
            Number.isFinite(args?.beforeWordPayload?.endSec)
                ? Number(args.beforeWordPayload.endSec)
                : Number.isFinite(args?.beforeWordPayload?.startSec)
                  ? Number(args.beforeWordPayload.startSec)
                  : 0;

        if (!Array.isArray(clip.transcriptMarkers)) clip.transcriptMarkers = [];

        const EPS = 1e-3;
        const sameAnchor = (a: any, b: any) => {
            if (!a || !b) return false;
            if (Number.isFinite(a?.sourceIndex) && Number.isFinite(b?.sourceIndex) && Number(a.sourceIndex) !== Number(b.sourceIndex)) return false;
            if (Number.isFinite(a?.srcStartSec) && Number.isFinite(b?.srcStartSec) && Math.abs(Number(a.srcStartSec) - Number(b.srcStartSec)) > EPS) return false;
            if (Number.isFinite(a?.srcEndSec) && Number.isFinite(b?.srcEndSec) && Math.abs(Number(a.srcEndSec) - Number(b.srcEndSec)) > EPS) return false;
            if (Number.isFinite(a?.timeSec) && Number.isFinite(b?.timeSec) && Math.abs(Number(a.timeSec) - Number(b.timeSec)) > EPS) return false;
            return true;
        };

        // Avoid duplicates at the same boundary (primarily keyed by `after` when present).
        if (after) {
            const existing = clip.transcriptMarkers.find((m: any) => sameAnchor(m?.after, after));
            if (existing) return existing;
        }

        const now = Date.now();
        const rec: any = {
            id: crypto?.randomUUID?.() ?? `marker_${now}_${Math.random()}`,
            title,
            timeSec,
            createdAt: now,
            updatedAt: now,
        };
        if (after) rec.after = after;
        if (before) rec.before = before;

        clip.transcriptMarkers.push(rec);
        try {
            clip.transcriptMarkers.sort((a: any, b: any) => Number(a?.timeSec ?? 0) - Number(b?.timeSec ?? 0));
        } catch {}

        if (this.isInitialized()) {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    transcriptMarkersChanged: true,
                },
            });
        }

        return rec;
    }

    static renameTranscriptMarker(markerId: string, title: string): void {
        const clip: any = this.getScriptLayerClip();
        if (!clip || String(clip.type) !== "script") return;
        if (!Array.isArray(clip.transcriptMarkers)) return;

        const id = String(markerId ?? "");
        if (!id) return;
        const nextTitle = String(title ?? "").trim();
        if (!nextTitle) return;

        const idx = clip.transcriptMarkers.findIndex((m: any) => String(m?.id ?? "") === id);
        if (idx < 0) return;

        clip.transcriptMarkers[idx] = {
            ...(clip.transcriptMarkers[idx] ?? {}),
            title: nextTitle,
            updatedAt: Date.now(),
        };

        if (this.isInitialized()) {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    transcriptMarkersChanged: true,
                },
            });
        }
    }

    static removeTranscriptMarker(markerId: string): void {
        const clip: any = this.getScriptLayerClip();
        if (!clip || String(clip.type) !== "script") return;
        if (!Array.isArray(clip.transcriptMarkers) || clip.transcriptMarkers.length === 0) return;

        const id = String(markerId ?? "");
        if (!id) return;

        const prevLen = clip.transcriptMarkers.length;
        clip.transcriptMarkers = clip.transcriptMarkers.filter((m: any) => String(m?.id ?? "") !== id);
        if (clip.transcriptMarkers.length === prevLen) return;

        if (this.isInitialized()) {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    transcriptMarkersChanged: true,
                },
            });
        }
    }

    /**
     * Replace all transcript markers of a given origin with a new set.
     * Used for AI-generated chapters so re-running does not disturb manual markers.
     *
     * This is a single SCRIPT_LAYER_UPDATED event so undo/redo treats it as one action.
     */
    static replaceTranscriptMarkersByOrigin(args: {
        origin: string;
        markers: Array<{
            title: string;
            timeSec?: number;
            afterWordPayload?: any;
            beforeWordPayload?: any;
        }>;
    }): void {
        const clip: any = this.getScriptLayerClip();
        if (!clip || String(clip.type) !== "script") return;

        const origin = String(args?.origin ?? "").trim();
        if (!origin) return;

        const cleanAnchor = (payload: any, mode: "after" | "before"): any | undefined => {
            if (!payload || typeof payload !== "object") return undefined;
            const sourceIndex = Number.isFinite(payload?.sourceIndex) ? Number(payload.sourceIndex) : undefined;
            const srcStartSec = Number.isFinite(payload?.srcStartSec) ? Number(payload.srcStartSec) : undefined;
            const srcEndSec = Number.isFinite(payload?.srcEndSec) ? Number(payload.srcEndSec) : undefined;
            const timeSec =
                mode === "after"
                    ? Number.isFinite(payload?.startSec)
                        ? Number(payload.startSec)
                        : undefined
                    : Number.isFinite(payload?.endSec)
                      ? Number(payload.endSec)
                      : Number.isFinite(payload?.startSec)
                        ? Number(payload.startSec)
                        : undefined;

            if (sourceIndex == null && srcStartSec == null && timeSec == null) return undefined;
            return { sourceIndex, srcStartSec, srcEndSec, timeSec };
        };

        const EPS = 1e-3;
        const sameAnchor = (a: any, b: any) => {
            if (!a || !b) return false;
            if (
                Number.isFinite(a?.sourceIndex) &&
                Number.isFinite(b?.sourceIndex) &&
                Number(a.sourceIndex) !== Number(b.sourceIndex)
            )
                return false;
            if (
                Number.isFinite(a?.srcStartSec) &&
                Number.isFinite(b?.srcStartSec) &&
                Math.abs(Number(a.srcStartSec) - Number(b.srcStartSec)) > EPS
            )
                return false;
            if (
                Number.isFinite(a?.srcEndSec) &&
                Number.isFinite(b?.srcEndSec) &&
                Math.abs(Number(a.srcEndSec) - Number(b.srcEndSec)) > EPS
            )
                return false;
            if (
                Number.isFinite(a?.timeSec) &&
                Number.isFinite(b?.timeSec) &&
                Math.abs(Number(a.timeSec) - Number(b.timeSec)) > EPS
            )
                return false;
            return true;
        };

        if (!Array.isArray(clip.transcriptMarkers)) clip.transcriptMarkers = [];

        const kept = clip.transcriptMarkers.filter((m: any) => String(m?.origin ?? "") !== origin);
        const next: any[] = [];

        const now = Date.now();
        for (const m of args?.markers ?? []) {
            const title = String((m as any)?.title ?? "").trim();
            if (!title) continue;

            const after = cleanAnchor((m as any)?.afterWordPayload, "after");
            const before = cleanAnchor((m as any)?.beforeWordPayload, "before");

            const explicitTimeSec = Number.isFinite((m as any)?.timeSec) ? Number((m as any).timeSec) : undefined;
            const timeSec =
                explicitTimeSec != null
                    ? explicitTimeSec
                    : Number.isFinite((m as any)?.beforeWordPayload?.endSec)
                      ? Number((m as any).beforeWordPayload.endSec)
                      : Number.isFinite((m as any)?.beforeWordPayload?.startSec)
                        ? Number((m as any).beforeWordPayload.startSec)
                        : Number.isFinite((m as any)?.afterWordPayload?.startSec)
                          ? Number((m as any).afterWordPayload.startSec)
                          : 0;

            // De-dupe against kept markers and already-added markers at the same boundary.
            if (after) {
                const dupInKept = kept.some((k: any) => sameAnchor(k?.after, after));
                const dupInNext = next.some((k: any) => sameAnchor(k?.after, after));
                if (dupInKept || dupInNext) continue;
            }

            const rec: any = {
                id: crypto?.randomUUID?.() ?? `marker_${now}_${Math.random()}`,
                title,
                origin,
                timeSec,
                createdAt: now,
                updatedAt: now,
            };
            if (after) rec.after = after;
            if (before) rec.before = before;
            next.push(rec);
        }

        const merged = [...kept, ...next];
        try {
            merged.sort((a: any, b: any) => Number(a?.timeSec ?? 0) - Number(b?.timeSec ?? 0));
        } catch {}

        clip.transcriptMarkers = merged;

        if (this.isInitialized()) {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    transcriptMarkersChanged: true,
                },
            });
        }
    }

    // =========================
    // Script chunk copy/paste
    // =========================

    private static getScriptInputSegments(cfg: any): ScriptLayerSourceSegment[] {
        const segs: ScriptLayerSourceSegment[] = Array.isArray(cfg?.segments) ? (cfg.segments as any) : [];
        if (segs.length > 0) return segs;

        const range = cfg?.range;
        if (range && Number.isFinite(range?.inSec) && Number.isFinite(range?.outSec) && Number(range.outSec) > Number(range.inSec)) {
            const off = Number(cfg?.offsetSec ?? 0);
            const id = crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;
            return [
                {
                    id,
                    offsetSec: Number.isFinite(off) ? off : 0,
                    range: { inSec: Number(range.inSec), outSec: Number(range.outSec) },
                    playbackRate: Number.isFinite(cfg?.playbackRate) ? Number(cfg.playbackRate) : undefined,
                } as ScriptLayerSourceSegment,
            ];
        }

        return [];
    }

    private static sliceSegmentsForChunk(args: {
        segments: ScriptLayerSourceSegment[];
        cfgPlaybackRate: number;
        startSec: number;
        endSec: number;
    }): Array<{ offsetSec: number; playbackRate?: number | null; range: { inSec: number; outSec: number } }> {
        const EPS = 1e-3;
        const out: Array<{ offsetSec: number; playbackRate?: number | null; range: { inSec: number; outSec: number } }> = [];
        const start = Number(args.startSec ?? 0);
        const end = Number(args.endSec ?? 0);
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start + EPS) return out;

        const cfgRate = Number(args.cfgPlaybackRate ?? 1) || 1;

        for (const seg of args.segments ?? []) {
            if (!seg) continue;
            const segOffset = Number((seg as any).offsetSec ?? NaN);
            const rin = Number((seg as any).range?.inSec ?? NaN);
            const rout = Number((seg as any).range?.outSec ?? NaN);
            if (!Number.isFinite(segOffset) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) continue;

            const segRateRaw = (seg as any).playbackRate;
            const segRate = Number.isFinite(segRateRaw) ? Number(segRateRaw) : cfgRate;
            if (!Number.isFinite(segRate) || segRate <= 0) continue;

            const segDur = (rout - rin) / segRate;
            const segEnd = segOffset + segDur;

            const interStart = Math.max(start, segOffset);
            const interEnd = Math.min(end, segEnd);
            if (interEnd <= interStart + EPS) continue;

            const srcStart = (interStart - segOffset) * segRate + rin;
            const srcEnd = (interEnd - segOffset) * segRate + rin;
            if (!Number.isFinite(srcStart) || !Number.isFinite(srcEnd) || srcEnd <= srcStart) continue;

            out.push({
                offsetSec: interStart - start,
                playbackRate: Number.isFinite(segRateRaw) ? Number(segRateRaw) : null,
                range: { inSec: srcStart, outSec: srcEnd },
            });
        }

        out.sort((a, b) => Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0));
        return out;
    }

    private static serializeScenePropsForChunk(propsRaw: any): ScriptChunkScenePropsV1 {
        const props: any = propsRaw ?? {};
        const rec: ScriptChunkScenePropsV1 = {};

        if (Number.isFinite(props.gain)) rec.gain = Number(props.gain);
        if (Number.isFinite(props.playbackRate)) rec.playbackRate = Number(props.playbackRate);
        if (typeof props.backgroundColor === "string" && props.backgroundColor.trim()) rec.backgroundColor = props.backgroundColor.trim();

        try {
            const byAspect = props.layoutByAspect;
            if (byAspect && typeof byAspect === "object") {
                const cleaned: Record<string, string | null> = {};
                for (const key of ["horizontal", "vertical", "square"]) {
                    if (!Object.prototype.hasOwnProperty.call(byAspect, key)) continue;
                    const v = (byAspect as any)[key];
                    if (v === null) cleaned[key] = null;
                    else if (typeof v === "string" && v) cleaned[key] = v;
                }
                if (Object.keys(cleaned).length > 0) rec.layoutByAspect = cleaned;
            }
        } catch {}

        try {
            if (props.inTransition) rec.inTransition = JSON.parse(JSON.stringify(props.inTransition));
            if (props.outTransition) rec.outTransition = JSON.parse(JSON.stringify(props.outTransition));
        } catch {}

        try {
            const audioSourceGains = props.audioSourceGains;
            if (audioSourceGains && typeof audioSourceGains?.entries === "function") {
                const arr: any[] = [];
                for (const [sourceIndexRaw, gainRaw] of audioSourceGains.entries()) {
                    const sourceIndex = Number(sourceIndexRaw);
                    const gain = Number(gainRaw);
                    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                    if (!Number.isFinite(gain)) continue;
                    arr.push({ sourceIndex, gain });
                }
                arr.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                if (arr.length > 0) rec.audioSourceGains = arr as any;
            }
        } catch {}

        const serializeVisualMap = (m: any): any[] | undefined => {
            if (!m || typeof m?.entries !== "function") return undefined;
            const arr: any[] = [];
            for (const [sourceIndexRaw, vRaw] of m.entries()) {
                const sourceIndex = Number(sourceIndexRaw);
                if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                const v = vRaw ?? {};
                const vrec: any = { sourceIndex };
                if (Number.isFinite(v.alpha)) vrec.alpha = Number(v.alpha);
                if (typeof v.hiddenByUser === "boolean") vrec.hiddenByUser = Boolean(v.hiddenByUser);
                if (Number.isFinite(v.x)) vrec.x = Number(v.x);
                if (Number.isFinite(v.y)) vrec.y = Number(v.y);
                if (Number.isFinite(v.width)) vrec.width = Number(v.width);
                if (Number.isFinite(v.height)) vrec.height = Number(v.height);
                try {
                    const crop = (v as any)?.crop;
                    if (crop && typeof crop === "object") {
                        const x = Number((crop as any).x);
                        const y = Number((crop as any).y);
                        const width = Number((crop as any).width);
                        const height = Number((crop as any).height);
                        if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                            vrec.crop = { x, y, width, height };
                        }
                    }
                } catch {}
                if (typeof (v as any)?.maskShape === "string") vrec.maskShape = String((v as any).maskShape);
                if (Object.keys(vrec).length > 1) arr.push(vrec);
            }
            arr.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
            return arr.length > 0 ? arr : undefined;
        };

        try {
            const visualOverrides = serializeVisualMap(props.visualOverrides);
            if (visualOverrides) rec.visualOverrides = visualOverrides as any;
        } catch {}

        try {
            const byAspect = props.visualOverridesByAspect;
            if (byAspect && typeof byAspect === "object") {
                const outByAspect: any = {};
                for (const [presetKey, mapRaw] of Object.entries(byAspect as any)) {
                    const arr = serializeVisualMap(mapRaw as any);
                    if (arr) outByAspect[presetKey] = arr;
                }
                if (Object.keys(outByAspect).length > 0) rec.visualOverridesByAspect = outByAspect;
            }
        } catch {}

        return rec;
    }

    private static deserializeScenePropsFromChunk(propsRaw: ScriptChunkScenePropsV1): SceneProperties {
        const props: any = propsRaw ?? {};
        const out: any = {};

        if (Number.isFinite(props.gain)) out.gain = Number(props.gain);
        if (Number.isFinite(props.playbackRate)) out.playbackRate = Number(props.playbackRate);
        if (typeof props.backgroundColor === "string" && props.backgroundColor.trim()) out.backgroundColor = props.backgroundColor.trim();

        if (props.layoutByAspect && typeof props.layoutByAspect === "object") {
            out.layoutByAspect = { ...(props.layoutByAspect as any) };
            // Keep legacy horizontal layout in sync.
            const h = (props.layoutByAspect as any).horizontal;
            if (typeof h === "string" && h) out.layout = h;
        }

        try {
            if (props.inTransition) out.inTransition = JSON.parse(JSON.stringify(props.inTransition));
            if (props.outTransition) out.outTransition = JSON.parse(JSON.stringify(props.outTransition));
        } catch {}

        try {
            const arr = Array.isArray(props.audioSourceGains) ? props.audioSourceGains : [];
            if (arr.length > 0) {
                const map = new Map<number, number>();
                for (const rec of arr) {
                    const idx = Number((rec as any)?.sourceIndex ?? NaN);
                    const gain = Number((rec as any)?.gain ?? NaN);
                    if (!Number.isFinite(idx) || idx < 0) continue;
                    if (!Number.isFinite(gain)) continue;
                    map.set(idx, gain);
                }
                if (map.size > 0) out.audioSourceGains = map;
            }
        } catch {}

        const buildVisualMap = (arrRaw: any): Map<number, any> | undefined => {
            const arr = Array.isArray(arrRaw) ? arrRaw : [];
            if (arr.length === 0) return undefined;
            const map = new Map<number, any>();
            for (const rec of arr) {
                const idx = Number((rec as any)?.sourceIndex ?? NaN);
                if (!Number.isFinite(idx) || idx < 0) continue;
                const v: any = {};
                if (Number.isFinite((rec as any).alpha)) v.alpha = Number((rec as any).alpha);
                if (typeof (rec as any).hiddenByUser === "boolean") v.hiddenByUser = Boolean((rec as any).hiddenByUser);
                if (Number.isFinite((rec as any).x)) v.x = Number((rec as any).x);
                if (Number.isFinite((rec as any).y)) v.y = Number((rec as any).y);
                if (Number.isFinite((rec as any).width)) v.width = Number((rec as any).width);
                if (Number.isFinite((rec as any).height)) v.height = Number((rec as any).height);
                try {
                    const crop = (rec as any)?.crop;
                    if (crop && typeof crop === "object") {
                        const x = Number((crop as any).x);
                        const y = Number((crop as any).y);
                        const width = Number((crop as any).width);
                        const height = Number((crop as any).height);
                        if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                            v.crop = { x, y, width, height };
                        }
                    }
                } catch {}
                if (typeof (rec as any)?.maskShape === "string") v.maskShape = String((rec as any).maskShape);
                map.set(idx, v);
            }
            return map.size > 0 ? map : undefined;
        };

        try {
            const vis = buildVisualMap(props.visualOverrides);
            if (vis) out.visualOverrides = vis;
        } catch {}

        try {
            const byAspect = props.visualOverridesByAspect;
            if (byAspect && typeof byAspect === "object") {
                const outByAspect: any = {};
                for (const [presetKey, arrRaw] of Object.entries(byAspect as any)) {
                    const m = buildVisualMap(arrRaw as any);
                    if (m) outByAspect[presetKey] = m;
                }
                if (Object.keys(outByAspect).length > 0) out.visualOverridesByAspect = outByAspect;
            }
        } catch {}

        return out as SceneProperties;
    }

    static copyScriptChunk(startSec: number, endSec: number): ScriptChunkV1 | null {
        const clip: any = this.getScriptLayerClip?.();
        if (!clip || String(clip.type) !== "script") return null;

        const start = Number(startSec ?? 0);
        const end = Number(endSec ?? 0);
        const EPS = 1e-3;
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start + EPS) return null;

        const scriptClipId = String(clip.id ?? "");
        if (!scriptClipId) return null;

        const cfgs: any[] = typeof clip.getInputConfigs === "function" ? (clip.getInputConfigs() as any[]) : [];
        const sources: ScriptChunkV1["sources"] = [];

        for (const cfg of cfgs) {
            const assetId = String(cfg?.assetId ?? "").trim();
            if (!assetId) continue;

            const cfgRate = Number(cfg?.playbackRate ?? 1.0) || 1.0;
            const segs = this.getScriptInputSegments(cfg);
            const sliced = this.sliceSegmentsForChunk({
                segments: segs,
                cfgPlaybackRate: cfgRate,
                startSec: start,
                endSec: end,
            });
            if (sliced.length === 0) continue;
            sources.push({ assetId, segments: sliced });
        }

        const scenes: ScriptChunkV1["scenes"] = [];
        try {
            const allScenes: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];
            for (const s of allScenes) {
                const s0 = Number((s as any)?.startSec ?? NaN);
                const s1 = Number((s as any)?.endSec ?? NaN);
                const idx = Number((s as any)?.index ?? NaN);
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || s1 <= s0) continue;
                if (!Number.isFinite(idx) || idx < 0) continue;
                if (s1 <= start + EPS || s0 >= end - EPS) continue;

                const pieceStart = Math.max(start, s0);
                const pieceEnd = Math.min(end, s1);
                if (pieceEnd <= pieceStart + EPS) continue;

                const propsRaw: any = typeof clip.getSceneProperties === "function" ? clip.getSceneProperties(idx) : (clip.sceneProperties?.get?.(idx) ?? {});
                const props = this.serializeScenePropsForChunk(propsRaw);
                scenes.push({
                    startOffsetSec: pieceStart - start,
                    endOffsetSec: pieceEnd - start,
                    properties: props,
                });
            }
        } catch {}

        // Ensure deterministic ordering and clean transitions at boundaries with surrounding content.
        scenes.sort((a, b) => Number(a.startOffsetSec ?? 0) - Number(b.startOffsetSec ?? 0));
        if (scenes.length > 0) {
            // Don't carry in/out transitions that were specific to the original neighboring scenes.
            delete (scenes[0].properties as any).inTransition;
            delete (scenes[scenes.length - 1].properties as any).outTransition;
        }

        const cuts: NonNullable<ScriptChunkV1["cuts"]> = [];
        try {
            const cs: any[] = Array.isArray((clip as any).scriptCuts) ? (clip as any).scriptCuts : [];
            for (const c of cs) {
                const s0 = Number((c as any)?.startSec ?? NaN);
                const s1 = Number((c as any)?.endSec ?? NaN);
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || s1 <= s0) continue;
                if (s1 <= start + EPS || s0 >= end - EPS) continue;
                const pieceStart = Math.max(start, s0);
                const pieceEnd = Math.min(end, s1);
                if (pieceEnd <= pieceStart + EPS) continue;
                const modeRaw = String((c as any)?.transcriptMode ?? "delete");
                const mode: "delete" | "strikethrough" | "retake-strikethrough" =
                    modeRaw === "retake-strikethrough"
                        ? "retake-strikethrough"
                        : modeRaw === "strikethrough"
                          ? "strikethrough"
                          : "delete";
                cuts.push({
                    startOffsetSec: pieceStart - start,
                    endOffsetSec: pieceEnd - start,
                    transcriptMode: mode,
                });
            }
        } catch {}

        const gaps: NonNullable<ScriptChunkV1["gaps"]> = [];
        try {
            const gs: any[] = Array.isArray((clip as any).gaps) ? (clip as any).gaps : [];
            for (const g of gs) {
                const s0 = Number((g as any)?.startSec ?? NaN);
                const s1 = Number((g as any)?.endSec ?? NaN);
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || s1 <= s0) continue;
                if (s1 <= start + EPS || s0 >= end - EPS) continue;
                const pieceStart = Math.max(start, s0);
                const pieceEnd = Math.min(end, s1);
                if (pieceEnd <= pieceStart + EPS) continue;
                gaps.push({
                    startOffsetSec: pieceStart - start,
                    endOffsetSec: pieceEnd - start,
                });
            }
        } catch {}

        const speakerMuteRanges: NonNullable<ScriptChunkV1["speakerMuteRanges"]> = [];
        try {
            const ms: any[] = Array.isArray((clip as any).speakerMuteRanges) ? (clip as any).speakerMuteRanges : [];
            for (const m of ms) {
                const s0 = Number((m as any)?.startSec ?? NaN);
                const s1 = Number((m as any)?.endSec ?? NaN);
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || s1 <= s0) continue;
                if (s1 <= start + EPS || s0 >= end - EPS) continue;
                const pieceStart = Math.max(start, s0);
                const pieceEnd = Math.min(end, s1);
                if (pieceEnd <= pieceStart + EPS) continue;

                const rec: any = {
                    startOffsetSec: pieceStart - start,
                    endOffsetSec: pieceEnd - start,
                };
                if (Number.isFinite((m as any)?.sourceIndex)) rec.sourceIndex = Number((m as any).sourceIndex);
                if (typeof (m as any)?.diarizationSpeakerId === "string" && String((m as any).diarizationSpeakerId).trim()) {
                    rec.diarizationSpeakerId = String((m as any).diarizationSpeakerId).trim();
                }
                if (typeof (m as any)?.speakerId === "string" && String((m as any).speakerId).trim()) {
                    rec.speakerId = String((m as any).speakerId).trim();
                }
                if (typeof (m as any)?.speakerName === "string" && String((m as any).speakerName).trim()) {
                    rec.speakerName = String((m as any).speakerName).trim();
                }
                speakerMuteRanges.push(rec);
            }
        } catch {}

        const durationSec = end - start;
        return {
            version: 1,
            scriptClipId,
            startSec: start,
            endSec: end,
            durationSec,
            sources,
            scenes,
            cuts: cuts.length > 0 ? cuts : undefined,
            gaps: gaps.length > 0 ? gaps : undefined,
            speakerMuteRanges: speakerMuteRanges.length > 0 ? speakerMuteRanges : undefined,
        };
    }

    static async pasteScriptChunk(atSec: number, chunk: ScriptChunkV1): Promise<boolean> {
        if (!this.isInitialized()) return false;
        const clip: any = this.getScriptLayerClip?.();
        if (!clip || String(clip.type) !== "script") return false;

        const ch: any = chunk as any;
        if (!ch || ch.version !== 1) return false;

        const scriptClipId = String(ch.scriptClipId ?? "");
        if (!scriptClipId || String(clip.id ?? "") !== scriptClipId) {
            console.warn("[CompositionService] pasteScriptChunk: clipboard script clip mismatch");
            return false;
        }

        const dur = Number(ch.durationSec ?? 0);
        const EPS = 1e-3;
        if (!Number.isFinite(dur) || dur <= EPS) return false;

        const inSec = Number(clip.range?.[0]?.seconds ?? 0);
        const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? inSec);
        const atRaw = Number(atSec ?? 0);
        if (!Number.isFinite(atRaw)) return false;
        const at = Math.max(inSec, Math.min(outSec, atRaw));

        // 1) Make room: create fillable spine time (empty scene) for the pasted duration.
        const ok = this.insertEmptyScriptScene(at, dur);
        if (!ok) return false;

        // 2) Internal scene boundaries (relative to paste start).
        const boundaries: number[] = [];
        try {
            const scenes = Array.isArray(ch.scenes) ? ch.scenes : [];
            for (const s of scenes) {
                const off = Number((s as any)?.startOffsetSec ?? NaN);
                if (!Number.isFinite(off)) continue;
                if (off <= EPS) continue;
                if (off >= dur - EPS) continue;
                boundaries.push(off);
            }
        } catch {}
        boundaries.sort((a, b) => a - b);
        const uniqBoundaries = boundaries.filter((t, i) => i === 0 || Math.abs(t - boundaries[i - 1]) > 0.02);
        for (const off of uniqBoundaries) {
            try {
                this.splitScriptLayer(at + off, true);
            } catch {}
        }

        // 3) Append per-source segments into the now-empty region.
        const genSegId = () => crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;
        for (const src of Array.isArray(ch.sources) ? ch.sources : []) {
            const assetId = String((src as any)?.assetId ?? "").trim();
            if (!assetId) continue;
            const segsRaw: any[] = Array.isArray((src as any)?.segments) ? ((src as any).segments as any[]) : [];
            if (segsRaw.length === 0) continue;

            const segs: ScriptLayerSourceSegment[] = segsRaw
                .map((s: any) => {
                    const off = Number(s?.offsetSec ?? NaN);
                    const rin = Number(s?.range?.inSec ?? NaN);
                    const rout = Number(s?.range?.outSec ?? NaN);
                    if (!Number.isFinite(off) || off < 0) return null;
                    if (!Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
                    const rec: any = {
                        id: genSegId(),
                        offsetSec: at + off,
                        range: { inSec: rin, outSec: rout },
                    };
                    const pr = s?.playbackRate;
                    if (Number.isFinite(pr) && Number(pr) > 0) rec.playbackRate = Number(pr);
                    return rec as ScriptLayerSourceSegment;
                })
                .filter(Boolean)
                .sort((a: any, b: any) => Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0));

            if (segs.length === 0) continue;
            try {
                (clip as any).appendSourceSegments?.(assetId, segs);
            } catch (e) {
                console.warn("[CompositionService] pasteScriptChunk: appendSourceSegments failed", { assetId }, e);
            }
        }

        // 4) Apply per-scene properties (layout + overrides) to the newly inserted scenes.
        try {
            const scenesAfter: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];
            const inserted = scenesAfter
                .filter((s: any) => {
                    const s0 = Number(s?.startSec ?? NaN);
                    if (!Number.isFinite(s0)) return false;
                    return s0 >= at - EPS && s0 < at + dur - EPS;
                })
                .sort((a: any, b: any) => Number(a?.startSec ?? 0) - Number(b?.startSec ?? 0));

            const chunkScenes = (Array.isArray(ch.scenes) ? ch.scenes : []).slice().sort((a: any, b: any) => Number(a?.startOffsetSec ?? 0) - Number(b?.startOffsetSec ?? 0));

            const applyPropsAtIndex = (sceneIndex: number, propsV1: ScriptChunkScenePropsV1) => {
                if (!Number.isFinite(sceneIndex) || sceneIndex < 0) return;
                const nextProps = this.deserializeScenePropsFromChunk(propsV1);
                try {
                    (clip as any).sceneProperties?.set?.(sceneIndex, nextProps);
                    (clip as any)._scenesCache = null;
                } catch {}
            };

            if (inserted.length === chunkScenes.length && inserted.length > 0) {
                for (let i = 0; i < inserted.length; i++) {
                    const idx = Number(inserted[i]?.index ?? NaN);
                    const props = (chunkScenes[i] as any)?.properties as ScriptChunkScenePropsV1;
                    applyPropsAtIndex(idx, props);
                }
            } else {
                // Fallback: map by time.
                for (const cs of chunkScenes) {
                    const off = Number((cs as any)?.startOffsetSec ?? 0);
                    const t = at + off + 1e-6;
                    let idx: number | null = null;
                    try {
                        if (typeof (clip as any).findSceneIndexForTime === "function") {
                            idx = Number((clip as any).findSceneIndexForTime(t));
                        }
                    } catch {}
                    if (idx == null || !Number.isFinite(idx) || idx < 0) continue;
                    applyPropsAtIndex(idx, (cs as any)?.properties as ScriptChunkScenePropsV1);
                }
            }

            try { (clip as any).markSegmentsDirty?.(); } catch {}
        } catch {}

        // 4b) Restore cuts/gaps within the inserted region (so pasted content matches edited playback).
        let didAddCuts = false;
        let didAddGaps = false;
        let didAddSpeakerMutes = false;
        try {
            const cutsRaw: any[] = Array.isArray((ch as any).cuts) ? ((ch as any).cuts as any[]) : [];
            for (const c of cutsRaw) {
                const off0 = Number((c as any)?.startOffsetSec ?? NaN);
                const off1 = Number((c as any)?.endOffsetSec ?? NaN);
                if (!Number.isFinite(off0) || !Number.isFinite(off1) || off1 <= off0) continue;
                const s0 = at + off0;
                const s1 = at + off1;
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || s1 <= s0 + EPS) continue;
                const modeRaw = String((c as any)?.transcriptMode ?? "delete");
                const mode: "delete" | "strikethrough" | "retake-strikethrough" =
                    modeRaw === "retake-strikethrough"
                        ? "retake-strikethrough"
                        : modeRaw === "strikethrough"
                          ? "strikethrough"
                          : "delete";
                try {
                    (clip as any).addScriptCut?.(s0, s1, undefined, mode);
                    didAddCuts = true;
                } catch {}
            }
        } catch {}

        try {
            const gapsRaw: any[] = Array.isArray((ch as any).gaps) ? ((ch as any).gaps as any[]) : [];
            for (const g of gapsRaw) {
                const off0 = Number((g as any)?.startOffsetSec ?? NaN);
                const off1 = Number((g as any)?.endOffsetSec ?? NaN);
                if (!Number.isFinite(off0) || !Number.isFinite(off1) || off1 <= off0) continue;
                const s0 = at + off0;
                const s1 = at + off1;
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || s1 <= s0 + EPS) continue;
                try {
                    (clip as any).addScriptGap?.(s0, s1);
                    didAddGaps = true;
                } catch {}
            }
        } catch {}

        try {
            const mutesRaw: any[] = Array.isArray((ch as any).speakerMuteRanges) ? ((ch as any).speakerMuteRanges as any[]) : [];
            const existing: any[] = Array.isArray((clip as any).speakerMuteRanges) ? [...((clip as any).speakerMuteRanges as any[])] : [];
            const additions: any[] = [];
            for (const m of mutesRaw) {
                const off0 = Number((m as any)?.startOffsetSec ?? NaN);
                const off1 = Number((m as any)?.endOffsetSec ?? NaN);
                if (!Number.isFinite(off0) || !Number.isFinite(off1) || off1 <= off0) continue;
                const s0 = at + off0;
                const s1 = at + off1;
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || s1 <= s0 + EPS) continue;

                const rec: any = {
                    id: crypto?.randomUUID?.() ?? `smute_${Date.now()}_${Math.random()}`,
                    startSec: s0,
                    endSec: s1,
                };
                if (Number.isFinite((m as any)?.sourceIndex)) rec.sourceIndex = Number((m as any).sourceIndex);
                if (typeof (m as any)?.diarizationSpeakerId === "string" && String((m as any).diarizationSpeakerId).trim()) {
                    rec.diarizationSpeakerId = String((m as any).diarizationSpeakerId).trim();
                }
                if (typeof (m as any)?.speakerId === "string" && String((m as any).speakerId).trim()) {
                    rec.speakerId = String((m as any).speakerId).trim();
                }
                if (typeof (m as any)?.speakerName === "string" && String((m as any).speakerName).trim()) {
                    rec.speakerName = String((m as any).speakerName).trim();
                }
                additions.push(rec);
            }

            if (additions.length > 0) {
                if (typeof (clip as any).applyPersistedEdits === "function") {
                    (clip as any).applyPersistedEdits({
                        speakerMuteRanges: [...existing, ...additions],
                    });
                } else {
                    (clip as any).speakerMuteRanges = [...existing, ...additions];
                }
                didAddSpeakerMutes = true;
            }
        } catch {}

        // 5) Rebuild schedule + refresh transcript/captions + persist clip properties.
        try {
            await this.composition.onClipTimingChanged();
        } catch {}
        try {
            const flags: any = {};
            if (didAddCuts) flags.cutsChanged = true;
            if (didAddGaps) flags.gapsChanged = true;
            if (didAddSpeakerMutes) flags.speakerMutesChanged = true;
            this.notifyScriptLayerUpdated(flags, { timingChanged: false });
        } catch {}
        try {
            this.notifyClipUpdated(clip, { timingChanged: false });
        } catch {}
        try {
            this.composition.computeFrame();
        } catch {}

        return true;
    }

    /**
     * Apply a controlled correction to a single transcript word.
     * The payload should include stable source-time coordinates when available.
     */
    static correctTranscriptWord(wordPayload: any, correctedText: string): void {
        const clip: any = this.getScriptLayerClip();
        if (!clip || String(clip.type) !== "script") return;

        const nextText = String(correctedText ?? "").trim();
        if (!nextText) return;

        if (!Array.isArray(clip.wordCorrections)) {
            clip.wordCorrections = [];
        }

        const sourceIndex = Number.isFinite(wordPayload?.sourceIndex) ? Number(wordPayload.sourceIndex) : undefined;
        const srcStartSec = Number.isFinite(wordPayload?.srcStartSec) ? Number(wordPayload.srcStartSec) : undefined;
        const srcEndSec = Number.isFinite(wordPayload?.srcEndSec) ? Number(wordPayload.srcEndSec) : undefined;

        const matches = (c: any) =>
            (Number.isFinite(sourceIndex) ? Number(c?.sourceIndex) === sourceIndex : true) &&
            (Number.isFinite(srcStartSec) ? Number(c?.srcStartSec) === srcStartSec : true) &&
            (Number.isFinite(srcEndSec) ? Number(c?.srcEndSec) === srcEndSec : true);

        const existingIdx = clip.wordCorrections.findIndex((c: any) => matches(c));
        const rec = {
            id: existingIdx >= 0 ? String(clip.wordCorrections[existingIdx]?.id ?? "") : (crypto?.randomUUID?.() ?? `corr_${Date.now()}_${Math.random()}`),
            sourceIndex,
            srcStartSec,
            srcEndSec,
            correctedText: nextText,
        };

        if (existingIdx >= 0) {
            clip.wordCorrections[existingIdx] = rec;
        } else {
            clip.wordCorrections.push(rec);
        }

        if (this.isInitialized()) {
            this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: this.composition.scriptLayer?.layerId ?? null,
                    clipId: clip.id,
                    wordCorrectionsChanged: true,
                },
            });
        }
    }

    /**
     * Insert silent time into the ScriptClip spine (adds time and ripples everything after later).
     * This is distinct from addScriptLayerGap which REPLACES existing time with silence.
     *
     * @param compTimeSec - Insertion point on the composition timeline (seconds)
     * @param durationSec - Duration of silence to insert (seconds)
     */
    static insertScriptLayerGap(compTimeSec: number, durationSec: number): any | null {
        const clip: any = this.getScriptLayerClip();
        if (!clip || typeof clip.addScriptGapInsertion !== "function") {
            console.warn("[CompositionService] No script layer clip or addScriptGapInsertion not available");
            return null;
        }

        const EPS = 1e-3;
        const dur = Math.max(0, Number(durationSec ?? 0));
        if (!Number.isFinite(dur) || dur <= EPS) return null;

        const insertionCompTime = Math.max(0, Number(compTimeSec ?? 0));

        // Determine script-clip local insertion anchor.
        // If the playhead is inside an existing inserted gap (hole), mapCompTimeToClipLocal returns null;
        // in that case, anchor the insertion to the next segment's srcStartSec (equivalent to extending the gap).
        let atSec: number | null = null;
        try {
            if (typeof clip.mapCompTimeToClipLocal === "function") {
                atSec = clip.mapCompTimeToClipLocal(insertionCompTime);
            }
        } catch {}

        if (atSec == null) {
            try {
                const segs: any[] = Array.isArray(clip.scriptSegments) ? clip.scriptSegments : [];
                const nextSeg = segs.find((s) => insertionCompTime < Number(s?.compStartSec ?? 0));
                if (nextSeg && Number.isFinite(nextSeg.srcStartSec)) {
                    atSec = Number(nextSeg.srcStartSec);
                } else {
                    atSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? 0);
                }
            } catch {}
        }

        if (atSec == null) {
            try {
                if (typeof clip.CompositionTimeToClipTime === "function") {
                    atSec = clip.CompositionTimeToClipTime(insertionCompTime);
                }
            } catch {}
        }

        if (atSec == null || !Number.isFinite(atSec)) return null;

        const inSec = Number(clip.range?.[0]?.seconds ?? 0);
        const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? inSec);
        const clampedAtSec = Math.max(inSec, Math.min(outSec, atSec));

        const insertion = clip.addScriptGapInsertion(clampedAtSec, dur);

        // Ripple-shift all non-script clips that start at/after the insertion point.
        const movedClips: any[] = [];
        try {
            for (const tr of this.composition.tracks) {
                for (const c of tr.clips) {
                    if (!c || c.id === clip.id) continue;
                    if (String(c.type) === "script") continue;
                    const start = Number(c.start?.seconds ?? 0);
                    if (!Number.isFinite(start) || start < insertionCompTime - EPS) continue;

                    if ("offset" in c) {
                        const currentOffset = Number((c as any)._offset?.seconds ?? (c as any).offset?.seconds ?? 0);
                        const nextOffset = currentOffset + dur;
                        try {
                            (c as any)._offset.seconds = nextOffset;
                        } catch {
                            (c as any)._offset = Timestamp.fromSeconds(nextOffset);
                        }
                        movedClips.push(c);
                    } else if (typeof (c as any).offsetBy === "function") {
                        (c as any).offsetBy(Timestamp.fromSeconds(dur));
                        movedClips.push(c);
                    }
                }
            }
        } catch (e) {
            console.warn("[CompositionService] Ripple shift failed:", e);
        }

        // Rebuild schedule + update rendering.
        if (this.isInitialized()) {
            try {
                this.composition.onClipTimingChanged();
            } catch (e) {
                console.error("[CompositionService] Error in onClipTimingChanged:", e);
            }

            // Broadcast internal state for waveform/transcript updates.
            try {
                this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                    clipId: clip.id,
                    cuts: clip.scriptCuts,
                    gaps: clip.gaps,
                    gapInsertions: clip.gapInsertions,
                    segments: clip.scriptSegments,
                    splits: clip.splits,
                    duration: clip.duration?.seconds ?? 0,
                    hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
                });
            } catch {}

            // Broadcast UI event for persistence and duration refresh.
            try {
                this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                    script: {
                        layerId: this.composition.scriptLayer?.layerId ?? null,
                        clipId: clip.id,
                        insertionsChanged: true,
                    },
                });
            } catch {}

            // Notify the store/persistence subscribers about moved clips + script clip timing changes.
            try { this.notifyClipUpdated(clip); } catch {}
            for (const c of movedClips) {
                try { this.notifyClipUpdated(c); } catch {}
            }
        }

        return insertion;
    }

    /**
     * Remove an inserted ScriptClip gap (gap insertion) by ID.
     * This REMOVES time from the script spine and ripples all downstream non-script clips earlier.
     */
    static removeScriptLayerGapInsertion(insertionId: string): boolean {
        const clip: any = this.getScriptLayerClip();
        if (!clip || typeof clip.removeScriptGapInsertion !== "function") {
            console.warn("[CompositionService] No script layer clip or removeScriptGapInsertion not available");
            return false;
        }

        const id = String(insertionId ?? "");
        if (!id) return false;

        const insertions: any[] = Array.isArray(clip.gapInsertions) ? clip.gapInsertions : [];
        const ins = insertions.find((x) => String(x?.id ?? "") === id);
        if (!ins) return false;

        const EPS = 1e-3;
        const dur = Math.max(0, Number(ins?.durationSec ?? 0));
        if (!Number.isFinite(dur) || dur <= EPS) return false;

        const atSec = Number(ins?.atSec ?? 0);
        const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? 0);
        const clipStartSec = Number(clip.start?.seconds ?? 0);

        // Determine where the inserted gap starts in composition-time (absolute seconds).
        // We compute this BEFORE removing the insertion so we can ripple-shift downstream clips.
        let gapStartCompSec: number | null = null;
        try {
            const segments: any[] = Array.isArray(clip.scriptSegments) ? clip.scriptSegments : [];

            // Trailing insertion at clip end: it starts after the last segment end.
            if (Number.isFinite(atSec) && Math.abs(atSec - outSec) <= EPS) {
                let maxEnd = clipStartSec;
                for (const seg of segments) {
                    const s0 = Number(seg?.srcStartSec ?? NaN);
                    const s1 = Number(seg?.srcEndSec ?? NaN);
                    const c0 = Number(seg?.compStartSec ?? NaN);
                    if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
                    const segDur = Math.max(0, s1 - s0);
                    const end = c0 + segDur;
                    if (end > maxEnd) maxEnd = end;
                }
                gapStartCompSec = maxEnd;
            } else if (Number.isFinite(atSec)) {
                // Non-trailing insertion: the first segment that starts at `atSec` is the "after" segment.
                // Its compStartSec is shifted by +dur, so gapStart = compStartSec - dur.
                const segAfter = segments.find((s: any) => Math.abs(Number(s?.srcStartSec ?? NaN) - atSec) <= EPS);
                if (segAfter && Number.isFinite(segAfter.compStartSec)) {
                    gapStartCompSec = Number(segAfter.compStartSec) - dur;
                }
            }
        } catch {}

        if (gapStartCompSec == null || !Number.isFinite(gapStartCompSec)) {
            // Conservative fallback: treat the insertion as starting at the script clip start.
            gapStartCompSec = clipStartSec;
        }

        const removed = clip.removeScriptGapInsertion(id);
        if (!removed) return false;

        // Ripple-shift all non-script clips that start at/after the gap start earlier by dur.
        const movedClips: any[] = [];
        try {
            for (const tr of this.composition.tracks) {
                for (const c of tr.clips) {
                    if (!c || c.id === clip.id) continue;
                    if (String(c.type) === "script") continue;
                    const start = Number(c.start?.seconds ?? 0);
                    if (!Number.isFinite(start) || start < gapStartCompSec - EPS) continue;

                    if ("offset" in c) {
                        const currentOffset = Number((c as any)._offset?.seconds ?? (c as any).offset?.seconds ?? 0);
                        const nextOffset = Math.max(0, currentOffset - dur);
                        try {
                            (c as any)._offset.seconds = nextOffset;
                        } catch {
                            (c as any)._offset = Timestamp.fromSeconds(nextOffset);
                        }
                        movedClips.push(c);
                    } else if (typeof (c as any).offsetBy === "function") {
                        try {
                            (c as any).offsetBy(Timestamp.fromSeconds(-dur));
                        } catch {}
                        movedClips.push(c);
                    }
                }
            }
        } catch (e) {
            console.warn("[CompositionService] Ripple shift (remove gap insertion) failed:", e);
        }

        // Rebuild schedule + update rendering.
        if (this.isInitialized()) {
            try {
                this.composition.onClipTimingChanged();
            } catch (e) {
                console.error("[CompositionService] Error in onClipTimingChanged:", e);
            }

            // Broadcast internal state for waveform/transcript updates.
            try {
                this.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                    clipId: clip.id,
                    cuts: clip.scriptCuts,
                    gaps: clip.gaps,
                    gapInsertions: clip.gapInsertions,
                    segments: clip.scriptSegments,
                    splits: clip.splits,
                    duration: clip.duration?.seconds ?? 0,
                    hasVideo: (clip.getVideoSources?.()?.length ?? 0) > 0,
                });
            } catch {}

            // Broadcast UI event for persistence and duration refresh.
            try {
                this.composition.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                    script: {
                        layerId: this.composition.scriptLayer?.layerId ?? null,
                        clipId: clip.id,
                        insertionsChanged: true,
                    },
                });
            } catch {}

            try { this.notifyClipUpdated(clip); } catch {}
            for (const c of movedClips) {
                try { this.notifyClipUpdated(c); } catch {}
            }
        }

        return true;
    }
}
